import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import config from '../config'; 
import { Link } from 'react-router-dom';
import { 
    BarChart2, Calendar, Building, User, Filter, ChevronDown, 
    Download, FileText, Printer, RefreshCw, Search, X, DollarSign,
    TrendingUp, TrendingDown, Users, Briefcase, Clipboard, Phone,
    Save, Copy, ArrowRight, Clock,  Package, PieChart, Zap,XCircle, CreditCard, FileSpreadsheet, AlertCircle, Wallet,
    Eye, CheckCircle // أضف هذه الأيقونات إذا لم تكن موجودة
} from 'lucide-react';

import * as XLSX from 'xlsx';


const LoadingSpinner = ({ message = "جاري التحميل..." }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 flex items-center gap-4 shadow-2xl">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            <span className="text-lg font-medium text-gray-700">{message}</span>
        </div>
    </div>
);


const getOldSameCompanyStatusMeta = (status, isOldSameCompany) => {
    const normalizedStatus = status || (isOldSameCompany === true ? 'قديم بنفس الشركة' : isOldSameCompany === false ? 'جديد على هذه الشركة' : 'غير محدد');

    if (normalizedStatus === 'قديم بنفس الشركة') {
        return {
            label: normalizedStatus,
            bg: '#fff7ed',
            border: '#fb923c',
            color: '#9a3412',
            className: 'bg-orange-100 text-orange-800 border-orange-200'
        };
    }

    if (normalizedStatus === 'جديد على هذه الشركة') {
        return {
            label: normalizedStatus,
            bg: '#ecfdf5',
            border: '#34d399',
            color: '#047857',
            className: 'bg-emerald-100 text-emerald-800 border-emerald-200'
        };
    }

    return {
        label: 'غير محدد',
        bg: '#f8fafc',
        border: '#cbd5e1',
        color: '#475569',
        className: 'bg-slate-100 text-slate-700 border-slate-200'
    };
};



const ClientsCommissionDisplay = ({ clientsData, clientsCount, commissionAmount, clientsWithCompanies }) => {
    const [showClientsModal, setShowClientsModal] = useState(false);
    const [parsedClients, setParsedClients] = useState([]);

    useEffect(() => {
        let clients = [];
        
        // أولوية للبيانات التفصيلية من قاعدة البيانات
        if (clientsWithCompanies && Array.isArray(clientsWithCompanies) && clientsWithCompanies.length > 0) {
            clients = clientsWithCompanies.map(client => {
                const oldSameCompanyStatus = client.OldSameCompanyStatus || client.CompanyRelationStatus || (
                    typeof client.IsOldSameCompany === 'boolean'
                        ? (client.IsOldSameCompany ? 'قديم بنفس الشركة' : 'جديد على هذه الشركة')
                        : 'غير محدد'
                );

                return {
                    name: client.ClientName || 'عميل غير محدد',
                    company: client.CompanyName || 'شركة غير محددة',
                    id: client.PassportID || null,
                    companyId: client.CompanyID || null,
                    previousCompaniesNames: client.PreviousCompaniesNames || '-',
                    isOldSameCompany: typeof client.IsOldSameCompany === 'boolean' ? client.IsOldSameCompany : null,
                    oldSameCompanyStatus
                };
            });
        }
        // إذا لم توجد البيانات التفصيلية، استخدم البيانات النصية
        else if (clientsData && clientsData !== '-' && clientsData.trim() !== '') {
            try {
                const clientsList = clientsData.split(',').map(item => {
                    const trimmed = item.trim();
                    
                    let clientName = 'عميل غير محدد';
                    let companyName = 'شركة غير محددة';
                    
                    // نمط: اسم العميل (اسم الشركة)
                    if (trimmed.includes('(') && trimmed.includes(')')) {
                        const openParen = trimmed.indexOf('(');
                        const closeParen = trimmed.lastIndexOf(')');
                        
                        clientName = trimmed.substring(0, openParen).trim() || 'عميل غير محدد';
                        companyName = trimmed.substring(openParen + 1, closeParen).trim() || 'شركة غير محددة';
                    }
                    // نمط: اسم العميل - اسم الشركة
                    else if (trimmed.includes(' - ')) {
                        const parts = trimmed.split(' - ');
                        clientName = parts[0]?.trim() || 'عميل غير محدد';
                        companyName = parts[1]?.trim() || 'شركة غير محددة';
                    }
                    // نمط: اسم العميل | اسم الشركة
                    else if (trimmed.includes(' | ')) {
                        const parts = trimmed.split(' | ');
                        clientName = parts[0]?.trim() || 'عميل غير محدد';
                        companyName = parts[1]?.trim() || 'شركة غير محددة';
                    }
                    // إذا لم يوجد نمط محدد، استخدم النص كاسم العميل
                    else if (trimmed.length > 0) {
                        clientName = trimmed;
                        companyName = 'شركة غير محددة';
                    }
                    
                    return {
                        name: clientName,
                        company: companyName,
                        id: null,
                        previousCompaniesNames: '-',
                        isOldSameCompany: null,
                        oldSameCompanyStatus: 'غير محدد'
                    };
                });
                
                clients = clientsList.filter(client => client.name && client.name !== 'عميل غير محدد');
            } catch (error) {
                console.error('خطأ في تحليل بيانات العملاء:', error);
            }
        }
        
        // إذا لم توجد بيانات، أنشئ عملاء افتراضيين
        if (clients.length === 0 && clientsCount > 0) {
            clients = Array.from({ length: clientsCount }, (_, index) => ({
                name: `عميل رقم ${index + 1}`,
                company: 'شركة غير محددة',
                id: null,
                previousCompaniesNames: '-',
                isOldSameCompany: null,
                oldSameCompanyStatus: 'غير محدد'
            }));
        }
        
        setParsedClients(clients);
    }, [clientsData, clientsWithCompanies, clientsCount]);

    if (parsedClients.length === 0) {
        return (
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 text-center text-gray-500">
                <Users size={24} className="mx-auto mb-2 text-gray-400" />
                <p>لا توجد بيانات عملاء مرتبطين</p>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-600">العملاء المرتبطين ({parsedClients.length}):</span>
                        {parsedClients.length > 3 && (
                            <button
                                onClick={() => setShowClientsModal(true)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                            >
                                <Eye size={14} />
                                عرض الكل
                            </button>
                        )}
                    </div>
                    
                    <div className="space-y-2">
                        {parsedClients.slice(0, 3).map((client, index) => {
                            const statusMeta = getOldSameCompanyStatusMeta(client.oldSameCompanyStatus, client.isOldSameCompany);
                            return (
                                <div key={index} className="flex items-center justify-between gap-3 py-2 px-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                            <User size={14} className="text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{client.name}</p>
                                            <p className="text-xs text-gray-500 truncate">الشركة: {client.company}</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                        <span className={`text-xs px-2 py-1 rounded-full border font-bold ${statusMeta.className}`}>
                                            {statusMeta.label}
                                        </span>
                                        {client.previousCompaniesNames && client.previousCompaniesNames !== '-' && (
                                            <span className="text-[11px] text-gray-500 max-w-[220px] truncate" title={client.previousCompaniesNames}>
                                                سابقًا: {client.previousCompaniesNames}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        
                        {parsedClients.length > 3 && (
                            <div className="text-center py-2">
                                <button
                                    onClick={() => setShowClientsModal(true)}
                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                    ... و {parsedClients.length - 3} عملاء أخرين
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* نافذة عرض جميع العملاء */}
            {showClientsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] financial-mobile-modal">
                    <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <Users size={20} />
                                العملاء المرتبطين بهذه العمولة ({parsedClients.length})
                            </h3>
                            <button
                                onClick={() => setShowClientsModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="grid gap-3">
                                {parsedClients.map((client, index) => {
                                    const statusMeta = getOldSameCompanyStatusMeta(client.oldSameCompanyStatus, client.isOldSameCompany);
                                    return (
                                        <div key={index} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                                                    {index + 1}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">{client.name}</p>
                                                    <p className="text-sm text-gray-600">الشركة: {client.company}</p>
                                                    {client.previousCompaniesNames && client.previousCompaniesNames !== '-' && (
                                                        <p className="text-xs text-gray-500 truncate" title={client.previousCompaniesNames}>
                                                            الشركات السابقة: {client.previousCompaniesNames}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <span className={`text-xs px-2 py-1 rounded-full border font-bold ${statusMeta.className}`}>
                                                    {statusMeta.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        
                        <div className="p-4 border-t bg-gray-50">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">إجمالي العملاء: {parsedClients.length}</span>
                                <span className="text-gray-600">إجمالي العمولة: {commissionAmount?.toLocaleString()} ج.م</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};



const FinancialReports = () => {
    // States
   const [activeTab, setActiveTab] = useState('clients'); // clients, delegates, company, comprehensive
    const [companies, setCompanies] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [companyCategories, setCompanyCategories] = useState([]);
    
        const [showDelegateDetailsModal, setShowDelegateDetailsModal] = useState(false);
    const [selectedDelegateDetails, setSelectedDelegateDetails] = useState(null);
    const [delegateDetailsLoading, setDelegateDetailsLoading] = useState(false);

    const [activeDetailsTab, setActiveDetailsTab] = useState('commissions');

    const [professions, setProfessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState({
        totalClients: 0,
        totalBalance: 0,
        totalDeposits: 0,
        totalWithdrawals: 0
    });
    const [toast, setToast] = useState(null);


// إضافة حالات الصفحات
const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 50,
    totalItems: 0,
    totalPages: 0
});
const [serverPagedClients, setServerPagedClients] = useState(false);


    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
 // تحديث exportOptions ليكون current_page هو الافتراضي
const [exportOptions, setExportOptions] = useState({
    format: 'excel',
    includeFilters: true,
    includeSummary: true,
    dateRange: 'current_page', // الافتراضي: البيانات المعروضة فقط
    customStartDate: '',
    customEndDate: '',
    selectedCategories: [],
    selectedTypes: ['income', 'expense'],
    includeInactive: false,
    groupByCompany: false,
    groupByDelegate: false,
    showDetailed: true
});



    // إضافة حالات جديدة
const [showAccountDialog, setShowAccountDialog] = useState(false);
const [selectedClient, setSelectedClient] = useState(null);
const [paymentForm, setPaymentForm] = useState({
    RequiredPayment: '',
    Notes: ''
});
const [paymentHistory, setPaymentHistory] = useState([]);
const [showPaymentHistory, setShowPaymentHistory] = useState(false);
const [showPaymentForm, setShowPaymentForm] = useState(false);

    // إضافة حالة جديدة لتخزين بيانات مستحقات العملاء
    const [clientDues, setClientDues] = useState([]);
    const [selectedNotes, setSelectedNotes] = useState(null);
    // إضافة متغير state جديد
    const [selectedTransactions, setSelectedTransactions] = useState(null);
    // فلاتر تقارير العملاء
    const [debtFilters, setDebtFilters] = useState({
        search: '',
        status: 'open',
        currency: 'all',
        companyId: '',
        delegateId: '',
        dateFrom: '',
        dateTo: ''
    });
    const [debtSummary, setDebtSummary] = useState({
        TotalDebts: 0,
        OpenDebtCount: 0,
        PaidDebtCount: 0,
        OverdueCount: 0,
        OutstandingEGP: 0,
        OutstandingSAR: 0,
        PendingSettlementEGP: 0,
        PendingSettlementSAR: 0
    });

    const [clientFilters, setClientFilters] = useState({
        companyId: '',
        delegateId: '',
        dateFrom: '',
        dateTo: '',
        professionId: '',
        hasTransactions: '',
        nationalId: '',
        balanceType: 'all', // all, positive, negative, zero
        netWorkDateFrom: '', // تاريخ النت (من)
        netWorkDateTo: '', // تاريخ النت (إلى)
        visaDateFrom: '', // تاريخ التأشير (من)
        visaDateTo: '', // تاريخ التأشير (إلى)
        travelDateFrom: '', // تاريخ السفر (من)
        travelDateTo: '', // تاريخ السفر (إلى)
        fingerprintDateFrom: '', // تاريخ البصمة (من)
        fingerprintDateTo: '', // تاريخ البصمة (إلى)
        consulateDateFrom: '', // تاريخ دخول القنصلية (من)
        consulateDateTo: '', // تاريخ دخول القنصلية (إلى)
        listIds: [],          // ✅ القوائم المحفوظة
        excludeList: false,   // ✅ استثناء عناصر القوائم
    });
    

    // ✅ القوائم المحفوظة
    const [savedLists, setSavedLists] = useState([]);
    const [savedListSearchTerm, setSavedListSearchTerm] = useState('');

    // فلاتر تقارير المندوبين
    const [delegateFilters, setDelegateFilters] = useState({
        delegateId: '',
        dateFrom: '',
        dateTo: ''
    });

    // بحث المندوب في الفلتر
    const [delegateSearchTerm, setDelegateSearchTerm] = useState('');
    const [showDelegateDropdown, setShowDelegateDropdown] = useState(false);
    const delegateDropdownRef = React.useRef(null);

// فلاتر تقارير الشركة
const [companyFilters, setCompanyFilters] = useState({
    dateFrom: '',
    dateTo: '',
    transactionType: '', // income, expense
    categoryId: ''
});

    // فلاتر تقارير الخزينة
    const [treasuryFilters, setTreasuryFilters] = useState({
        dateFrom: '',
        dateTo: '',
        transactionType: '' // deposit, withdrawal
    });

    

    const [comprehensiveFilters, setComprehensiveFilters] = useState({
        dateFrom: '',
        dateTo: '',
        transactionType: '',
        searchTerm: ''
    });
    
    // فلاتر تقارير الموظفين
    const [employeeFilters, setEmployeeFilters] = useState({
        employeeId: '',
        dateFrom: '',
        dateTo: '',
        transactionType: '',
        paymentStatus: ''
    });
    
    // حالة لتخزين قائمة الموظفين للفلترة
    const [employeesList, setEmployeesList] = useState([]);

const [comprehensiveData, setComprehensiveData] = useState({
    allTransactions: [],
    summary: {
        totalClients: 0,
        totalDelegates: 0,
        clientsBalance: 0,
        delegatesBalance: 0,
        companyIncome: 0,
        companyExpenses: 0,
        netBalance: 0
    }
});

    const printRef = useRef();


    const showNotesDetails = async (clientId) => {
        try {
            // عرض مؤشر التحميل
            setLoading(true);
            
            // استدعاء API الملاحظات
            const response = await fetch(`${config.API_BASE_URL}/api/reports/clients/${clientId}/notes`);
            
            if (!response.ok) {
                throw new Error('فشل في جلب ملاحظات العميل');
            }
            
            const data = await response.json();
            
            if (!data.notes || data.notes.length === 0) {
                showToast('لا توجد ملاحظات لهذا العميل', 'info');
                return;
            }
            
            // عرض الملاحظات
            setSelectedNotes({
                clientId,
                notes: data.notes
            });
        } catch (error) {
            console.error('خطأ في جلب ملاحظات العميل:', error);
            showToast('خطأ في جلب ملاحظات العميل', 'error');
        } finally {
            setLoading(false);
        }
    };


useEffect(() => {
    fetchCompanies();
    fetchDelegates();
    fetchProfessions();
    fetchCompanyCategories();
    // ✅ تحميل القوائم المحفوظة
    fetch(`${config.API_BASE_URL}/api/net-registrations/saved-lists?includeArchived=false`)
        .then(r => r.json())
        .then(data => setSavedLists(Array.isArray(data) ? data : []))
        .catch(() => setSavedLists([]));
}, []);

// useEffect لتحديث الصفحات عند تغيير البيانات
useEffect(() => {
    if (activeTab === 'clients' && serverPagedClients) return;

    const clientsData = clientDues.length > 0 ? clientDues : reportData;
    if (clientsData.length > 0) {
        updatePagination(clientsData.length);
    }
}, [reportData, clientDues, pagination.itemsPerPage, activeTab, serverPagedClients]);


useEffect(() => {
    if (activeTab) {
        resetFilters();
        setReportData([]);
        setSummaryData({
            totalClients: 0,
            totalBalance: 0,
            totalDeposits: 0,
            totalWithdrawals: 0
        });
        // إعادة تعيين الصفحات عند تغيير التبويب
        setPagination({
            currentPage: 1,
            itemsPerPage: 50,
            totalItems: 0,
            totalPages: 0
        });
        setServerPagedClients(false);
    }
}, [activeTab]);

    useEffect(() => {
    if (showDelegateDetailsModal) {
        setActiveDetailsTab('commissions');
    }
}, [showDelegateDetailsModal]);

// إغلاق dropdown المندوب عند الضغط خارجه
useEffect(() => {
    const handleClickOutside = (e) => {
        if (delegateDropdownRef.current && !delegateDropdownRef.current.contains(e.target)) {
            setShowDelegateDropdown(false);
        }
    };
    if (showDelegateDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
}, [showDelegateDropdown]);


// تحسين دالة getCurrentPageData للتعامل مع جميع أنواع البيانات
const getCurrentPageData = (data) => {
    if (!data || data.length === 0) return [];

    // في تقرير العملاء المحمل بالصفحات من الخادم، البيانات الموجودة هي الصفحة الحالية بالفعل
    if (activeTab === 'clients' && serverPagedClients) {
        return data;
    }
    
    // التأكد من وجود البيانات الصحيحة
    let sourceData = data;
    
    // إذا كانت بيانات العملاء ومتوفرة clientDues، استخدمها
    if (activeTab === 'clients' && clientDues.length > 0) {
        sourceData = clientDues;
    }
    
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return sourceData.slice(startIndex, endIndex);
};

// دالة لتحديث معلومات الصفحات
const updatePagination = (totalItems) => {
    const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
    setPagination(prev => ({
        ...prev,
        totalItems,
        totalPages
    }));
};

// دالة للانتقال لصفحة معينة
const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
        setPagination(prev => ({
            ...prev,
            currentPage: pageNumber
        }));

        if (activeTab === 'clients' && serverPagedClients) {
            fetchClientReport(pageNumber, pagination.itemsPerPage);
        }
    }
};

// مكون الصفحات
const PaginationComponent = () => {
    if (pagination.totalPages <= 1) return null;

    const getVisiblePages = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];

        for (let i = Math.max(2, pagination.currentPage - delta); 
             i <= Math.min(pagination.totalPages - 1, pagination.currentPage + delta); 
             i++) {
            range.push(i);
        }

        if (pagination.currentPage - delta > 2) {
            rangeWithDots.push(1, '...');
        } else {
            rangeWithDots.push(1);
        }

        rangeWithDots.push(...range);

        if (pagination.currentPage + delta < pagination.totalPages - 1) {
            rangeWithDots.push('...', pagination.totalPages);
        } else {
            rangeWithDots.push(pagination.totalPages);
        }

        return rangeWithDots;
    };

    const visiblePages = getVisiblePages();

    return (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-gray-50 border-t">
            <div className="text-sm text-gray-700">
                عرض {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} إلى {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} من أصل {pagination.totalItems} سجل
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => goToPage(1)}
                    disabled={pagination.currentPage === 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                    الأولى
                </button>
                
                <button
                    onClick={() => goToPage(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                    السابقة
                </button>

                {visiblePages.map((page, index) => (
                    page === '...' ? (
                        <span key={`dots-${index}`} className="px-2 py-1 text-gray-400">...</span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`px-3 py-1 rounded-lg border ${
                                pagination.currentPage === page
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {page}
                        </button>
                    )
                ))}

                <button
                    onClick={() => goToPage(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                    التالية
                </button>
                
                <button
                    onClick={() => goToPage(pagination.totalPages)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                    الأخيرة
                </button>
            </div>

            <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">عدد السجلات:</label>
                <select
                    value={pagination.itemsPerPage}
                    onChange={(e) => {
                        const newItemsPerPage = parseInt(e.target.value);
                        setPagination(prev => ({
                            ...prev,
                            itemsPerPage: newItemsPerPage,
                            currentPage: 1,
                            totalPages: Math.ceil(pagination.totalItems / newItemsPerPage)
                        }));

                        if (activeTab === 'clients' && serverPagedClients) {
                            fetchClientReport(1, newItemsPerPage);
                        }
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                </select>
            </div>
        </div>
    );
};

    // Helpers
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };


    const getExportDataFromSource = (sourceData) => {
        const allSourceData = Array.isArray(sourceData) ? sourceData : [];
        
        // إذا كان المطلوب البيانات المعروضة في الصفحة الحالية فقط (الافتراضي)
        if (exportOptions.dateRange === 'current_page') {
            // استخدام getCurrentPageData مباشرة لجلب البيانات المعروضة فقط
            return getCurrentPageData(allSourceData);
        }
        // جميع البيانات المفلترة الحالية
        else if (exportOptions.dateRange === 'filtered') {
            return [...allSourceData];
        } 
        // فترة مخصصة
        else if (exportOptions.dateRange === 'custom') {
            if (exportOptions.customStartDate || exportOptions.customEndDate) {
                return allSourceData.filter(item => {
                    const itemDate = new Date(item.TransactionDate || item.NetWorkDate || item.CreatedAt);
                    const startDate = exportOptions.customStartDate ? new Date(exportOptions.customStartDate) : null;
                    const endDate = exportOptions.customEndDate ? new Date(exportOptions.customEndDate) : null;
                    
                    if (startDate && itemDate < startDate) return false;
                    if (endDate && itemDate > endDate) return false;
                    return true;
                });
            }
            return [...allSourceData];
        }
        // جميع البيانات
        else {
            return [...allSourceData];
        }
    };

    const getExportData = () => {
        // تحديد البيانات المصدرية الصحيحة أولاً
        const allSourceData = activeTab === 'clients' && clientDues.length > 0 ? clientDues : reportData;
        return getExportDataFromSource(allSourceData);
    };

    const fetchAllClientReportDataForExport = async () => {
        const params = buildClientReportParams(); // بدون page/limit حتى يرجع السيرفر كل البيانات كما كان سابقًا
        const response = await fetch(`${config.API_BASE_URL}/api/reports/reports/clients?${params.toString()}`);
        if (!response.ok) throw new Error('فشل في جلب بيانات العملاء للتصدير');
        const data = await response.json();
        return data.clients || [];
    };

    const getExportDataAsync = async () => {
        if (activeTab === 'clients' && serverPagedClients && exportOptions.dateRange !== 'current_page') {
            const allClients = await fetchAllClientReportDataForExport();
            return getExportDataFromSource(allClients);
        }
        return getExportData();
    };

// حساب الملخص للبيانات المحددة
// حساب الملخص للبيانات المحددة
const calculateSummaryForData = (data) => {
    let totalIncome = 0;
    let totalExpenses = 0;
    let totalClients = 0;
    let totalBalance = 0;
    
    switch (activeTab) {
        case 'clients':
            const clientsData = Array.isArray(data) ? data : [];
            totalIncome = clientsData.reduce((sum, item) => sum + (item.TotalDeposits || 0), 0);
            totalExpenses = clientsData.reduce((sum, item) => sum + (item.TotalWithdrawals || 0), 0);
            totalBalance = clientsData.reduce((sum, item) => sum + (item.CurrentBalance || 0), 0);
            totalClients = clientsData.length;
            break;
            
        case 'delegates':
            const delegatesCount = data.length;
            const totalAllClients = data.reduce((sum, item) => sum + (item.TotalClients || 0), 0);
            const totalTraveledClients = data.reduce((sum, item) => sum + (item.TraveledClients || 0), 0);
            const totalCommissionsPaid = data.reduce((sum, item) => sum + (item.TotalCommissionsPaid || 0), 0);
            
            return {
                totalClients: delegatesCount,
                totalIncome: totalCommissionsPaid,  // إجمالي العمولات المدفوعة
                totalExpenses: totalAllClients,     // إجمالي العملاء
                balance: totalTraveledClients       // العملاء المسافرين
            };
            
        case 'company':
            totalIncome = data.filter(t => t.Type === 'income').reduce((sum, t) => sum + (t.Amount || 0), 0);
            totalExpenses = data.filter(t => t.Type === 'expense').reduce((sum, t) => sum + (t.Amount || 0), 0);
            totalBalance = totalIncome - totalExpenses;
            totalClients = data.length;
            break;
            
        case 'treasury':
            totalIncome = data.filter(t => t.TransactionType === 'إيداع').reduce((sum, t) => sum + (t.Amount || 0), 0);
            totalExpenses = data.filter(t => t.TransactionType === 'سحب').reduce((sum, t) => sum + (t.Amount || 0), 0);
            totalBalance = totalIncome - totalExpenses;
            totalClients = data.length;
            break;
            
        case 'comprehensive':
            data.forEach(transaction => {
                if ((transaction.amount || 0) >= 0) {
                    totalIncome += (transaction.amount || 0);
                } else {
                    totalExpenses += Math.abs(transaction.amount || 0);
                }
            });
            totalBalance = totalIncome - totalExpenses;
            totalClients = data.length;
            break;

        case 'debts':
            totalClients = data.length;
            totalIncome = data.reduce((sum, debt) => sum + Number(debt.PaidAmount || 0), 0);
            totalExpenses = data.reduce((sum, debt) => sum + Number(debt.AdministrativeSettledAmount || 0), 0);
            totalBalance = data.reduce((sum, debt) => sum + Number(debt.OutstandingAmount || 0), 0);
            break;
    }
    
    return {
        totalIncome,
        totalExpenses,
        balance: totalBalance,
        totalClients
    };
};

const getReportTitle = () => {
    switch (activeTab) {
        case 'clients': return 'تقرير حسابات العملاء';
        case 'delegates': return 'تقرير حسابات المندوبين';
        case 'company': return 'تقرير إيرادات ومصروفات الشركة';
        case 'treasury': return 'تقرير الخزينة';
        case 'comprehensive': return 'التقرير المالي الشامل';
        case 'debts': {
            const selectedCompany = debtFilters.companyId
                ? companies.find(company => String(company.CompanyID) === String(debtFilters.companyId))?.CompanyName
                : null;
            const selectedDelegate = debtFilters.delegateId
                ? delegates.find(delegate => String(delegate.DelegateID) === String(debtFilters.delegateId))?.DelegateName
                : null;
            const suffix = [selectedCompany ? `شركة ${selectedCompany}` : null, selectedDelegate ? `مندوب ${selectedDelegate}` : null]
                .filter(Boolean).join(' — ');
            return suffix ? `تقرير مديونيات العملاء — ${suffix}` : 'تقرير مديونيات العملاء';
        }
        default: return 'تقرير مالي';
    }
};

// دالة الحصول على رؤوس الجدول
const getTableHeaders = () => {
    switch (activeTab) {
        case 'clients':
            return ['م', 'اسم العميل', 'الرقم القومي', 'الشركة', 'المندوب', 'مهنة السفر', 'رقم الهاتف', 'تاريخ النت', 'تاريخ التأشير', 'تاريخ السفر', 'الرصيد النشط', 'المبلغ المطلوب', 'المتبقي/الزائد', 'الحالة', 'الملاحظات'];
        case 'delegates':
            return ['م', 'اسم المندوب', 'رقم الهاتف', 'إجمالي العملاء', 'العملاء المسافرين', 'عمولة مدفوعة', 'عمولة غير مدفوعة', 'إجمالي العمولات المدفوعة', 'عدد المعاملات', 'آخر معاملة'];
        case 'company':
            return ['م', 'التاريخ', 'نوع المعاملة', 'التصنيف', 'الوصف', 'المبلغ', 'طريقة الدفع', 'الملاحظات', 'تم بواسطة'];
        case 'treasury':
            return ['م', 'التاريخ', 'نوع المعاملة', 'المبلغ', 'الرصيد', 'اسم العميل', 'الملاحظات', 'تم بواسطة'];
            case 'comprehensive':
                return ['م', 'نوع المعاملة', 'الاسم/الوصف', 'الشركة/التصنيف', 'طريقة الدفع', 'المبلغ', 'التاريخ', 'الملاحظات'];
            case 'employees':
                return ['م', 'اسم الموظف', 'المنصب', 'الراتب الأساسي', 'إجمالي الرواتب', 'إجمالي السلف', 'إجمالي البونصات', 'عدد المعاملات', 'آخر معاملة', 'إجراءات'];
            case 'debts':
                return ['م', 'رقم المديونية', 'اسم العميل', 'الهاتف', 'الرقم القومي', 'رقم ملف السفرة', 'حالة السفرة', 'الشركة', 'المندوب', 'أصل الدين', 'المدفوع فعليًا', 'تسوية إدارية', 'المتبقي', 'الحالة', 'موعد السداد', 'تاريخ الاستحقاق', 'السبب'];
        default:
            return ['م', 'البيانات'];
    }
};

// دالة إنشاء صفوف الجدول
const createTableRows = (data) => {
    const clientsData = activeTab === 'clients' && clientDues.length > 0 ? clientDues : data;
    
    switch (activeTab) {
        case 'clients':
            return clientsData.map((client, index) => `
                <tr>
                    <td class="row-number">${index + 1}</td>
                    <td class="data-cell">${client.NameArabic || '-'}</td>
                    <td class="data-cell">${client.NationalID || '-'}</td>
                    <td class="data-cell">${client.CompanyDisplayName || client.CompanyName || ''}</td>
                    <td class="data-cell">${client.DelegateName || '-'}</td>
                    <td class="data-cell">${client.PassportProfession || '-'}</td>
                    <td class="data-cell">${formatPhoneNumber(client.PhoneNumbers)}</td>
                    <td class="data-cell">${client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-'}</td>
                    <td class="data-cell">${client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-'}</td>
                    <td class="data-cell">${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-'}</td>
                    <td class="data-cell ${client.CurrentBalance >= 0 ? 'positive-amount' : 'negative-amount'}">${(client.CurrentBalance || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${(client.RequiredPayment || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell ${client.IsOverpaid ? 'positive-amount' : (client.RemainingDue > 0 ? 'negative-amount' : 'positive-amount')}">${client.IsOverpaid ? '+' : ''}${(client.RemainingDue || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${!client.RequiredPayment || client.RequiredPayment <= 0 ? 'يلزم تحديد المبلغ المطلوب' : client.IsOverpaid ? 'يمكن سحب مبلغ زائد' : client.RemainingDue > 0 ? 'متبقي عليه مستحقات' : 'مسدد بالكامل'}</td>
                    <td class="data-cell">${client.LastActiveNote || '-'}</td>
                </tr>
            `).join('');
        
        case 'delegates':
            return data.map((delegate, index) => `
                <tr>
                    <td class="row-number">${index + 1}</td>
                    <td class="data-cell">${delegate.DelegateName || '-'}</td>
                    <td class="data-cell">${delegate.PhoneNumber || '-'}</td>
                    <td class="data-cell">${delegate.TotalClients || '0'}</td>
                    <td class="data-cell">${delegate.TraveledClients || '0'}</td>
                    <td class="data-cell positive-amount">${delegate.PaidCommissionClients || '0'}</td>
                    <td class="data-cell negative-amount">${delegate.UnpaidCommissionClients || '0'}</td>
                    <td class="data-cell positive-amount">${(delegate.TotalCommissionsPaid || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${delegate.TransactionsCount || '0'}</td>
                    <td class="data-cell">${delegate.LastTransactionDate ? new Date(delegate.LastTransactionDate).toLocaleDateString('ar-EG') : '-'}</td>
                </tr>
            `).join('');
        
        case 'company':
            return data.map((transaction, index) => `
                <tr>
                    <td class="row-number">${index + 1}</td>
                    <td class="data-cell">${transaction.TransactionDate ? new Date(transaction.TransactionDate).toLocaleDateString('ar-EG') : '-'}</td>
                    <td class="data-cell">${transaction.Type === 'income' ? 'إيراد' : 'مصروف'}</td>
                    <td class="data-cell">${transaction.CategoryName || '-'}</td>
                    <td class="data-cell">${transaction.Description || '-'}</td>
                    <td class="data-cell ${transaction.Type === 'income' ? 'positive-amount' : 'negative-amount'}">${(transaction.Amount || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${transaction.PaymentMethodName || '-'}</td>
                    <td class="data-cell">${transaction.Notes || '-'}</td>
                    <td class="data-cell">${transaction.CreatedBy || '-'}</td>
                </tr>
            `).join('');
            
        case 'treasury':
            return data.map((transaction, index) => `
                <tr>
                    <td class="row-number">${index + 1}</td>
                    <td class="data-cell">${new Date(transaction.TransactionDate).toLocaleDateString('ar-EG')}</td>
                    <td class="data-cell">${transaction.TransactionType || '-'}</td>
                    <td class="data-cell ${transaction.TransactionType === 'إيداع' ? 'positive-amount' : 'negative-amount'}">${(transaction.Amount || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${(transaction.Balance || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${transaction.ClientName || '-'}</td>
                    <td class="data-cell">${transaction.Notes || '-'}</td>
                    <td class="data-cell">${transaction.CreatedBy || '-'}</td>
                </tr>
            `).join('');
        
        case 'comprehensive':
            return data.map((transaction, index) => `
                <tr>
                    <td class="row-number">${index + 1}</td>
                    <td class="data-cell">
                        <span class="transaction-type ${(transaction.amount || 0) >= 0 ? 'positive-amount' : 'negative-amount'}">
                            ${transaction.typeName || '-'}
                        </span>
                    </td>
                    <td class="data-cell">${transaction.name || '-'}</td>
                    <td class="data-cell">${transaction.company || '-'}</td>
                    <td class="data-cell">${transaction.paymentMethod || 'غير محدد'}</td>
                    <td class="data-cell ${(transaction.amount || 0) >= 0 ? 'positive-amount' : 'negative-amount'}">
                        ${(transaction.amount || 0) >= 0 ? '+' : ''}${(transaction.amount || 0).toLocaleString()} ج.م
                    </td>
                    <td class="data-cell">${transaction.date ? new Date(transaction.date).toLocaleDateString('ar-EG') : '-'}</td>
                    <td class="data-cell">${transaction.notes || 'لا توجد ملاحظات'}</td>
                </tr>
            `).join('');

        case 'debts':
            return data.map((debt, index) => `
                <tr>
                    <td class="row-number">${index + 1}</td>
                    <td class="data-cell">${debt.DebtNumber || '-'}</td>
                    <td class="data-cell">${debt.NameArabic || '-'}</td>
                    <td class="data-cell">${debt.MainPhone || '-'}</td>
                    <td class="data-cell">${debt.NationalID || '-'}</td>
                    <td class="data-cell">${debt.OfficeSerial || debt.PassportID || '-'}</td>
                    <td class="data-cell">${debt.TripIsActive ? 'نشطة' : 'مؤرشفة'}</td>
                    <td class="data-cell">${debt.CompanyName || '-'}</td>
                    <td class="data-cell">${debt.DelegateName || '-'}</td>
                    <td class="data-cell">${Number(debt.OriginalAmount || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell positive-amount">${Number(debt.PaidAmount || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${Number(debt.AdministrativeSettledAmount || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell negative-amount">${Number(debt.OutstandingAmount || 0).toLocaleString()} ج.م</td>
                    <td class="data-cell">${debt.Status || '-'}</td>
                    <td class="data-cell">${debt.DueType || '-'}</td>
                    <td class="data-cell">${debt.DueDate ? new Date(debt.DueDate).toLocaleDateString('ar-EG') : '-'}</td>
                    <td class="data-cell">${debt.Reason || '-'}</td>
                </tr>
            `).join('');
        
        default:
            return '';
    }
};





    const resetFilters = () => {
        switch (activeTab) {
            case 'clients':
                setClientFilters({
                    companyId: '',
                    delegateId: '',
                    dateFrom: '',
                    dateTo: '',
                    professionId: '',
                    hasTransactions: '',
                    nationalId: '',
                    balanceType: 'all',
                    netWorkDateFrom: '',
                    netWorkDateTo: '',
                    visaDateFrom: '',
                    visaDateTo: '',
                    travelDateFrom: '',
                    travelDateTo: '',
                    listIds: [],
                    excludeList: false,
                });
                break;
            case 'delegates':
                setDelegateFilters({
                    delegateId: '',
                    dateFrom: '',
                    dateTo: ''
                });
                break;
         case 'company':
    setCompanyFilters({
        dateFrom: '',
        dateTo: '',
        transactionType: '',
        categoryId: ''
    });
    break;
               
            case 'treasury':
                setTreasuryFilters({
                    dateFrom: '',
                    dateTo: '',
                    transactionType: ''
                });
                break;
            default:
                break;
                


                case 'comprehensive':
                    setComprehensiveFilters({
                        dateFrom: '',
                        dateTo: '',
                        transactionType: '',
                        searchTerm: ''
                    });
                    break;
                
                case 'employees':
                    setEmployeeFilters({
                        employeeId: '',
                        dateFrom: '',
                        dateTo: '',
                        transactionType: '',
                        paymentStatus: ''
                    });
                    break;

                case 'debts':
                    setDebtFilters({
                        search: '',
                        status: 'open',
                        currency: 'all',
                        companyId: '',
                        delegateId: '',
                        dateFrom: '',
                        dateTo: ''
                    });
                    break;
                    


        }
    };

    // طرق تنسيق البيانات
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || amount === 0) return '0 ج.م';
        return amount.toLocaleString('ar-SA') + ' ج.م';
    };

    const formatPhoneNumber = (phoneNumbers) => {
        if (!phoneNumbers) return '-';
        
        try {
            let numbers = [];
            
            if (typeof phoneNumbers === 'string') {
                try {
                    const parsed = JSON.parse(phoneNumbers);
                    
                    if (Array.isArray(parsed)) {
                        numbers = parsed.filter(n => n && n.length > 0);
                    } else if (typeof parsed === 'object' && parsed !== null) {
                        numbers = Object.values(parsed).filter(n => n && n.length > 0);
                    } else if (parsed) {
                        numbers = [parsed];
                    }
                } catch (e) {
                    if (phoneNumbers !== '[]') {
                        numbers = [phoneNumbers];
                    }
                }
            } else if (Array.isArray(phoneNumbers)) {
                numbers = phoneNumbers.filter(n => n && n.length > 0);
            } else if (typeof phoneNumbers === 'object' && phoneNumbers !== null) {
                numbers = Object.values(phoneNumbers).filter(n => n && n.length > 0);
            }
            
            return numbers.length > 0 ? numbers.join(' ، ') : '-';
        } catch (error) {
            console.error('خطأ في معالجة رقم الهاتف:', error);
            return '-';
        }
    };

    // إنشاء صورة واضحة لمعاملة عمولة مندوب مع إمكانية النسخ للحافظة بدون تنزيل
    const normalizePreviousCompaniesForImage = (previousCompaniesValue) => {
        if (!previousCompaniesValue) return [];

        try {
            if (Array.isArray(previousCompaniesValue)) {
                return previousCompaniesValue.filter(id => id !== null && id !== undefined && String(id).trim() !== '');
            }

            if (typeof previousCompaniesValue === 'string') {
                const trimmed = previousCompaniesValue.trim();
                if (!trimmed || trimmed === '[]') return [];

                if (trimmed.startsWith('[')) {
                    const parsed = JSON.parse(trimmed);
                    return Array.isArray(parsed)
                        ? parsed.filter(id => id !== null && id !== undefined && String(id).trim() !== '')
                        : [];
                }

                return trimmed.split(',').map(id => id.trim()).filter(Boolean);
            }
        } catch (error) {
            console.error('خطأ في تحليل الشركات السابقة داخل صورة العمولة:', error);
        }

        return [];
    };

    const getCompanyNameForImage = (companyId) => {
        const company = companies.find(item => String(item.CompanyID) === String(companyId));
        return company?.CompanyName || null;
    };

    const buildOldSameCompanyInfoForImage = (client) => {
        const previousCompanyIds = normalizePreviousCompaniesForImage(client.PreviousCompanies || client.previousCompanies);
        const currentCompanyId = client.CompanyID || client.companyId || null;
        const isOldSameCompany = typeof client.IsOldSameCompany === 'boolean'
            ? client.IsOldSameCompany
            : Boolean(currentCompanyId && previousCompanyIds.some(companyId => String(companyId) === String(currentCompanyId)));

        const previousCompaniesNames = client.PreviousCompaniesNames || previousCompanyIds
            .map(companyId => getCompanyNameForImage(companyId))
            .filter(Boolean)
            .join(' ، ') || '-';

        const oldSameCompanyStatus = client.OldSameCompanyStatus || client.CompanyRelationStatus || (
            currentCompanyId && previousCompanyIds.length > 0
                ? (isOldSameCompany ? 'قديم بنفس الشركة' : 'جديد على هذه الشركة')
                : 'غير محدد'
        );

        return {
            currentCompanyId,
            previousCompanyIds,
            previousCompaniesNames,
            isOldSameCompany,
            oldSameCompanyStatus
        };
    };

    const getCommissionClientsForImage = (commission) => {
        if (!commission) return [];

        if (Array.isArray(commission.ClientsWithCompanies) && commission.ClientsWithCompanies.length > 0) {
            return commission.ClientsWithCompanies.map((client, index) => {
                const companyInfo = buildOldSameCompanyInfoForImage(client);

                return {
                    name: client.ClientName || client.NameArabic || client.name || `عميل رقم ${index + 1}`,
                    company: client.CompanyName || client.company || 'شركة غير محددة',
                    id: client.PassportID || client.id || null,
                    ...companyInfo
                };
            });
        }

        if (commission.ClientsNames && commission.ClientsNames !== '-') {
            return String(commission.ClientsNames).split(',').map((item, index) => {
                const trimmed = item.trim();
                const match = trimmed.match(/^(.*?)\s*\((.*?)\)\s*$/);
                return {
                    name: match ? match[1].trim() : (trimmed || `عميل رقم ${index + 1}`),
                    company: match ? match[2].trim() : 'شركة غير محددة',
                    id: null,
                    previousCompaniesNames: '-',
                    isOldSameCompany: null,
                    oldSameCompanyStatus: 'غير محدد'
                };
            }).filter(client => client.name);
        }

        const count = Number(commission.ClientsCount || 0);
        return Array.from({ length: count }, (_, index) => ({
            name: `عميل رقم ${index + 1}`,
            company: 'شركة غير محددة',
            id: null,
            previousCompaniesNames: '-',
            isOldSameCompany: null,
            oldSameCompanyStatus: 'غير محدد'
        }));
    };

    const formatImageDate = (dateValue, includeTime = false) => {
        if (!dateValue) return '-';
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return '-';
        return includeTime
            ? date.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })
            : date.toLocaleDateString('ar-EG');
    };

    const exportDelegateCommissionAsImage = async (commission, action = 'copy') => {
        try {
            if (!commission || !selectedDelegateDetails) {
                showToast('لا توجد بيانات كافية لإنشاء صورة العمولة', 'error');
                return;
            }

            const clients = getCommissionClientsForImage(commission);
            const delegateName = selectedDelegateDetails.delegateName || selectedDelegateDetails.delegateInfo?.DelegateName || 'مندوب غير محدد';
            const delegatePhone = selectedDelegateDetails.delegateInfo?.PhoneNumber || '-';
            const amount = Number(commission.CommissionAmount || 0);
            const paymentMethodName = commission.CompanyName || 'الخزينة الرئيسية';
            const paymentMethodType = commission.PaymentMethodType === 'treasury'
                ? 'الخزينة الرئيسية'
                : commission.PaymentMethodType === 'bank'
                    ? 'حساب بنكي'
                    : 'طريقة دفع أخرى';
            const notes = commission.CommissionNotes || 'لا توجد ملاحظات';
            const createdBy = commission.CreatedBy || 'النظام';
            const transactionNo = commission.TransactionID || '-';
            const commissionDate = formatImageDate(commission.CommissionDate);
            const commissionDateTime = formatImageDate(commission.CommissionDate, true);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const width = 1400;
            const margin = 70;
            canvas.width = width;

            ctx.direction = 'rtl';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';

            const wrapText = (text, maxWidth, font) => {
                ctx.font = font;
                const words = String(text || '-').replace(/\s+/g, ' ').trim().split(' ');
                const lines = [];
                let line = '';

                words.forEach(word => {
                    const testLine = line ? `${line} ${word}` : word;
                    if (ctx.measureText(testLine).width <= maxWidth || !line) {
                        line = testLine;
                    } else {
                        lines.push(line);
                        line = word;
                    }
                });

                if (line) lines.push(line);
                return lines.length ? lines : ['-'];
            };

            const notesLines = wrapText(notes, width - margin * 2 - 50, '32px Arial');
            const clientRowHeight = 82;
            const clientRowsHeight = Math.max(120, clients.length * clientRowHeight + 92);
            const notesHeight = Math.max(110, notesLines.length * 42 + 68);
            const height = 760 + clientRowsHeight + notesHeight + 170;
            canvas.height = height;

            const roundRect = (x, y, w, h, r, fill, stroke) => {
                ctx.beginPath();
                ctx.moveTo(x + r, y);
                ctx.lineTo(x + w - r, y);
                ctx.quadraticCurveTo(x + w, y, x + w, y + r);
                ctx.lineTo(x + w, y + h - r);
                ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
                ctx.lineTo(x + r, y + h);
                ctx.quadraticCurveTo(x, y + h, x, y + h - r);
                ctx.lineTo(x, y + r);
                ctx.quadraticCurveTo(x, y, x + r, y);
                ctx.closePath();
                if (fill) {
                    ctx.fillStyle = fill;
                    ctx.fill();
                }
                if (stroke) {
                    ctx.strokeStyle = stroke;
                    ctx.lineWidth = 3;
                    ctx.stroke();
                }
            };

            const drawText = (text, x, y, options = {}) => {
                ctx.save();
                ctx.direction = options.direction || 'rtl';
                ctx.textAlign = options.align || 'right';
                ctx.textBaseline = options.baseline || 'top';
                ctx.fillStyle = options.color || '#111827';
                ctx.font = options.font || '30px Arial';
                ctx.fillText(String(text ?? '-'), x, y);
                ctx.restore();
            };

            const drawWrappedText = (text, x, y, maxWidth, lineHeight, options = {}) => {
                const lines = wrapText(text, maxWidth, options.font || '30px Arial');
                lines.forEach((line, index) => {
                    drawText(line, x, y + index * lineHeight, options);
                });
                return y + lines.length * lineHeight;
            };

            const drawInfoBox = (x, y, w, h, label, value, accent = '#2563eb') => {
                roundRect(x, y, w, h, 18, '#ffffff', '#e5e7eb');
                ctx.fillStyle = accent;
                ctx.fillRect(x + w - 9, y + 18, 7, h - 36);
                drawText(label, x + w - 28, y + 18, { font: '24px Arial', color: '#6b7280' });
                drawWrappedText(value || '-', x + w - 28, y + 52, w - 56, 34, { font: 'bold 29px Arial', color: '#111827' });
            };

            const drawSectionHeader = (title, y, color = '#1d4ed8') => {
                roundRect(margin, y, width - margin * 2, 58, 14, '#eff6ff', '#bfdbfe');
                drawText(title, width - margin - 28, y + 14, { font: 'bold 30px Arial', color });
            };

            // الخلفية العامة
            ctx.fillStyle = '#f3f4f6';
            ctx.fillRect(0, 0, width, height);
            roundRect(38, 38, width - 76, height - 76, 34, '#ffffff', '#d1d5db');

            // رأس الصورة
            roundRect(margin, 70, width - margin * 2, 155, 28, '#0f766e', null);
            drawText('إيصال دفع عمولة مندوب', width - margin - 40, 98, { font: 'bold 48px Arial', color: '#ffffff' });
            drawText('صورة مخصصة للإرسال للمندوب - تفاصيل المعاملة كاملة', width - margin - 40, 158, { font: '27px Arial', color: '#d1fae5' });
            drawText(`تاريخ إنشاء الصورة: ${new Date().toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}`, margin + 470, 160, { font: '24px Arial', color: '#d1fae5', align: 'left', direction: 'ltr' });

            // مربع المبلغ الرئيسي
            roundRect(margin, 252, 520, 152, 24, '#ecfdf5', '#10b981');
            drawText('مبلغ العمولة المدفوعة', margin + 480, 278, { font: 'bold 27px Arial', color: '#065f46' });
            drawText(`${amount.toLocaleString('ar-EG')} ج.م`, margin + 480, 322, { font: 'bold 52px Arial', color: '#047857' });

            // بيانات أساسية
            const rightBoxX = width - margin - 610;
            const leftBoxX = margin;
            drawInfoBox(rightBoxX, 252, 610, 94, 'اسم المندوب', delegateName, '#0f766e');
            drawInfoBox(rightBoxX, 364, 610, 94, 'رقم هاتف المندوب', delegatePhone, '#0f766e');
            drawInfoBox(leftBoxX, 422, 610, 94, 'رقم المعاملة', `#${transactionNo}`, '#16a34a');
            drawInfoBox(rightBoxX, 476, 610, 94, 'تاريخ دفع العمولة', commissionDateTime, '#2563eb');
            drawInfoBox(leftBoxX, 534, 610, 94, 'طريقة الدفع', `${paymentMethodName} - ${paymentMethodType}`, '#7c3aed');
            drawInfoBox(rightBoxX, 588, 610, 94, 'إجمالي العملاء المرتبطين', `${clients.length || Number(commission.ClientsCount || 0)} عميل`, '#dc2626');

            let y = 720;
            drawSectionHeader('العملاء المرتبطين بهذه العمولة', y, '#1d4ed8');
            y += 78;

            if (clients.length > 0) {
                clients.forEach((client, index) => {
                    const rowY = y + index * clientRowHeight;
                    const statusMeta = getOldSameCompanyStatusMeta(client.oldSameCompanyStatus, client.isOldSameCompany);
                    const previousCompaniesText = client.previousCompaniesNames && client.previousCompaniesNames !== '-'
                        ? client.previousCompaniesNames
                        : 'لا توجد بيانات مسجلة';

                    roundRect(margin, rowY, width - margin * 2, 70, 14, index % 2 === 0 ? '#f9fafb' : '#ffffff', '#e5e7eb');
                    roundRect(width - margin - 76, rowY + 13, 44, 42, 18, '#dbeafe', null);
                    drawText(String(index + 1), width - margin - 46, rowY + 21, { font: 'bold 22px Arial', color: '#1d4ed8', align: 'center' });
                    drawWrappedText(client.name || '-', width - margin - 100, rowY + 10, 460, 27, { font: 'bold 25px Arial', color: '#111827' });
                    drawText(`الشركة الحالية: ${client.company || 'شركة غير محددة'}`, margin + 810, rowY + 13, { font: '23px Arial', color: client.company === 'شركة غير محددة' ? '#6b7280' : '#2563eb', align: 'right' });
                    drawText(`الشركات السابقة: ${previousCompaniesText}`, width - margin - 100, rowY + 44, { font: '21px Arial', color: '#64748b' });

                    roundRect(margin + 22, rowY + 17, 300, 38, 18, statusMeta.bg, statusMeta.border);
                    drawText(statusMeta.label, margin + 300, rowY + 24, { font: 'bold 21px Arial', color: statusMeta.color, align: 'right' });

                    if (client.id) {
                        drawText(`ID: ${client.id}`, margin + 60, rowY + 51, { font: '18px Arial', color: '#94a3b8', align: 'left', direction: 'ltr' });
                    }
                });
                y += clients.length * clientRowHeight + 25;
            } else {
                roundRect(margin, y, width - margin * 2, 74, 14, '#f9fafb', '#e5e7eb');
                drawText('لا توجد بيانات عملاء مرتبطة بهذه المعاملة', width - margin - 32, y + 20, { font: 'bold 27px Arial', color: '#6b7280' });
                y += 100;
            }

            drawSectionHeader('ملاحظات المعاملة', y, '#92400e');
            y += 78;
            roundRect(margin, y, width - margin * 2, notesHeight - 25, 16, '#fffbeb', '#fde68a');
            drawWrappedText(notes, width - margin - 30, y + 24, width - margin * 2 - 60, 42, { font: '31px Arial', color: '#78350f' });
            y += notesHeight;

            // شريط اعتماد مختصر
            roundRect(margin, y, width - margin * 2, 92, 18, '#f8fafc', '#cbd5e1');
            drawText(`تم بواسطة: ${createdBy}`, width - margin - 34, y + 24, { font: 'bold 27px Arial', color: '#334155' });
            drawText(`تاريخ المعاملة: ${commissionDate}`, margin + 34, y + 28, { font: '25px Arial', color: '#475569', align: 'left', direction: 'ltr' });
            y += 118;

            drawText('هذا الإيصال صادر من نظام التقارير المالية ويخص معاملة العمولة الموضحة فقط.', width / 2, y, { font: '23px Arial', color: '#6b7280', align: 'center' });

            const safeDelegateName = delegateName.replace(/[\/:*?"<>|]+/g, '').replace(/\s+/g, '_') || 'delegate';
            const filename = `commission_${safeDelegateName}_${transactionNo}_${new Date().toISOString().split('T')[0]}.png`;

            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob((createdBlob) => {
                    if (createdBlob) resolve(createdBlob);
                    else reject(new Error('تعذر إنشاء ملف الصورة'));
                }, 'image/png', 1);
            });

            const downloadImage = () => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            };

            if (action === 'download') {
                downloadImage();
                showToast('تم تحميل صورة العمولة بجودة عالية', 'success');
                return;
            }

            if (navigator.clipboard && window.ClipboardItem) {
                try {
                    await navigator.clipboard.write([
                        new window.ClipboardItem({ 'image/png': blob })
                    ]);
                    showToast('تم نسخ صورة العمولة للحافظة، يمكنك لصقها وإرسالها للمندوب مباشرة', 'success');
                    return;
                } catch (clipboardError) {
                    console.warn('فشل نسخ الصورة للحافظة، سيتم تنزيلها بدلاً من ذلك:', clipboardError);
                }
            }

            downloadImage();
            showToast('المتصفح لا يدعم نسخ الصور هنا، تم تحميل الصورة بدلاً من ذلك', 'info');
        } catch (error) {
            console.error('خطأ في إنشاء صورة العمولة:', error);
            showToast('حدث خطأ أثناء إنشاء صورة العمولة', 'error');
        }
    };

    // API Functions
    // في دالة fetchCompanies
    const fetchCompanies = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/reports/companies`);
            if (!response.ok) throw new Error('فشل في جلب بيانات الشركات');
            
            const data = await response.json();
            console.log('تم جلب الشركات:', data);
            setCompanies(data);
            return data;
        } catch (error) {
            console.error('خطأ في جلب بيانات الشركات:', error);
            showToast('فشل في جلب بيانات الشركات', 'error');
            return [];
        }
    };

    // نضيف هذه الدالة لجلب مبالغ الدفع للشركات
    const fetchCompanyPaymentAmounts = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/reports/companies`);
            if (!response.ok) throw new Error('فشل في جلب بيانات الشركات ومبالغ الدفع');
            
            const data = await response.json();
            console.log('بيانات الشركات ومبالغ الدفع:', data);
            
            // تحديث حالة الشركات مع معلومات مبالغ الدفع
            setCompanies(data);
            
            return data;
        } catch (error) {
            console.error('خطأ في جلب بيانات مبالغ الدفع:', error);
            showToast('فشل في جلب بيانات مبالغ الدفع', 'error');
            return [];
        }
    };

    // نعدل دالة fetchDelegates
    const fetchDelegates = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/reports/delegates`);
            if (!response.ok) throw new Error('فشل في جلب بيانات المندوبين');
            
            const data = await response.json();
            console.log('تم جلب المندوبين:', data);
            setDelegates(data);
        } catch (error) {
            console.error('خطأ في جلب بيانات المندوبين:', error);
            showToast('فشل في جلب بيانات المندوبين', 'error');
        }
    };

    const fetchProfessions = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/reports/professions`);
            if (!response.ok) throw new Error('فشل في جلب بيانات المهن');
            
            const data = await response.json();
            setProfessions(data);
        } catch (error) {
            console.error('خطأ في جلب بيانات المهن:', error);
            showToast('فشل في جلب بيانات المهن', 'error');
        }
    };

// جلب تصنيفات معاملات الشركة
const fetchCompanyCategories = async () => {
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/reports/reports/company-categories`);
        if (!response.ok) throw new Error('فشل في جلب تصنيفات الشركة');
        
        const data = await response.json();
        setCompanyCategories(data);
    } catch (error) {
        console.error('خطأ في جلب تصنيفات الشركة:', error);
        showToast('فشل في جلب تصنيفات الشركة', 'error');
    }
};

// تعديل دالة calculateClientDues
const calculateClientDues = async () => {
    try {
      // جلب العملاء
      if (reportData.length === 0) {
        await fetchClientReport();
      }
      
      // استخدام الدالة الجديدة لحساب المستحقات مباشرة من بيانات العميل
      await calculateClientDuesDirectly(reportData);
      
      showToast('تم حساب المستحقات المالية بنجاح', 'success');
      
    } catch (error) {
      console.error('خطأ في حساب المستحقات المالية:', error);
      showToast('فشل في حساب المستحقات المالية', 'error');
    }
};

const buildClientReportParams = (extraParams = {}) => {
    const params = new URLSearchParams();

    Object.keys(clientFilters).forEach(key => {
        if (key === 'listIds') {
            // ✅ إرسال كل ID على حدة
            if (Array.isArray(clientFilters.listIds) && clientFilters.listIds.length > 0) {
                clientFilters.listIds.forEach(id => params.append('listIds', id));
            }
        } else if (key === 'excludeList') {
            if (clientFilters.excludeList) params.append('excludeList', 'true');
        } else if (clientFilters[key]) {
            params.append(key, clientFilters[key]);
        }
    });

    Object.keys(extraParams).forEach(key => {
        if (extraParams[key] !== undefined && extraParams[key] !== null && extraParams[key] !== '') {
            params.set(key, extraParams[key]);
        }
    });

    return params;
};

const fetchClientReport = useCallback(async (pageNumber = pagination.currentPage, pageSize = pagination.itemsPerPage) => {
    setLoading(true);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    try {
        const params = buildClientReportParams({
            page: pageNumber,
            limit: pageSize
        });
        const url = `${config.API_BASE_URL}/api/reports/reports/clients?${params.toString()}`;
        
        console.log('جاري جلب بيانات العملاء للصفحة:', pageNumber);
        const response = await fetch(url, { signal: controller.signal });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) throw new Error('فشل في جلب تقرير العملاء');
        
        const data = await response.json();
        const clients = data.clients || [];
        const paginationInfo = data.pagination || {};
        const summary = data.summary || null;
        
        setServerPagedClients(Boolean(paginationInfo.serverPaginated));
        setReportData(clients);
        setClientDues(clients); // البيانات جاهزة من الخادم
        
        setPagination(prev => ({
            ...prev,
            currentPage: paginationInfo.page || pageNumber,
            itemsPerPage: paginationInfo.limit || pageSize,
            totalItems: paginationInfo.totalItems ?? clients.length,
            totalPages: paginationInfo.totalPages || Math.ceil((paginationInfo.totalItems ?? clients.length) / (paginationInfo.limit || pageSize)) || 0
        }));

        if (summary) {
            setSummaryData({
                totalClients: summary.totalClients || 0,
                totalBalance: summary.totalBalance || 0,
                totalDeposits: summary.totalDeposits || 0,
                totalWithdrawals: summary.totalWithdrawals || 0
            });
        } else {
            const totalDeposits = clients.reduce((sum, client) => sum + (client.TotalDeposits || 0), 0);
            const totalWithdrawals = clients.reduce((sum, client) => sum + (client.TotalWithdrawals || 0), 0);
            const totalBalance = clients.reduce((sum, client) => sum + (client.CurrentBalance || 0), 0);
            
            setSummaryData({
                totalClients: clients.length,
                totalBalance,
                totalDeposits,
                totalWithdrawals
            });
        }
        
        showToast('تم جلب التقرير بنجاح', 'success');
        
    } catch (error) {
        clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
            showToast('تم إلغاء العملية بسبب انتهاء الوقت', 'error');
        } else {
            console.error('خطأ في جلب تقرير العملاء:', error);
            showToast('فشل في جلب تقرير العملاء: ' + error.message, 'error');
        }
    } finally {
        setLoading(false);
    }
}, [clientFilters, pagination.currentPage, pagination.itemsPerPage]);
    


// دالة لحساب المستحقات المالية مباشرة من بيانات العميل
const calculateClientDuesDirectly = async (clients) => {
    try {
        // لنطبع بيانات أول عميل قبل المعالجة
        if (clients && clients.length > 0) {
            console.log("بيانات العميل قبل معالجة المستحقات:", clients[0]);
            console.log("المبلغ المطلوب قبل المعالجة:", clients[0].RequiredPayment);
        }
        
        const batchSize = 10;
        const batches = Math.ceil(clients.length / batchSize);
        let processedClients = [];
        
        for (let i = 0; i < batches; i++) {
            const startIdx = i * batchSize;
            const endIdx = Math.min(startIdx + batchSize, clients.length);
            const batch = clients.slice(startIdx, endIdx);
            
            const batchPromises = batch.map(async (client) => {
                try {
                    // استخدام المبلغ المطلوب من العميل مباشرة
                    const requiredPayment = client.RequiredPayment !== undefined ? Number(client.RequiredPayment) : 0;
                    const currentBalance = client.CurrentBalance !== undefined ? Number(client.CurrentBalance) : 0;
                    const totalDeposits = client.TotalDeposits !== undefined ? Number(client.TotalDeposits) : 0;
                    const totalWithdrawals = client.TotalWithdrawals !== undefined ? Number(client.TotalWithdrawals) : 0;
                    
                    // حساب صافي المبلغ المدفوع (الإيداعات - السحوبات)
                    const netPaid = totalDeposits - totalWithdrawals;
                    
                    // حساب المبلغ المتبقي أو الزائد
                    const difference = netPaid - requiredPayment;
                    const isOverpaid = difference > 0; // هل الرصيد أكبر من المطلوب؟
                    
                    return {
                        ...client,
                        RequiredPayment: requiredPayment,
                        PaidAmount: netPaid,
                        RemainingDue: Math.abs(difference), // القيمة المطلقة للفرق (سواء متبقي أو زائد)
                        IsOverpaid: isOverpaid // علامة لتحديد ما إذا كان الرصيد زائد
                    };
                } catch (error) {
                    console.error('خطأ في حساب مستحقات العميل:', client.NameArabic, error);
                    
                    // في حالة الخطأ، استخدم القيم الافتراضية
                    const requiredPayment = client.RequiredPayment || 0;
                    const currentBalance = client.CurrentBalance || 0;
                    const totalDeposits = client.TotalDeposits || 0;
                    const totalWithdrawals = client.TotalWithdrawals || 0;
                    const netPaid = totalDeposits - totalWithdrawals;
                    const difference = netPaid - requiredPayment;
                    const isOverpaid = difference > 0;
                    
                    return {
                        ...client,
                        RequiredPayment: requiredPayment,
                        PaidAmount: netPaid,
                        RemainingDue: Math.abs(difference),
                        IsOverpaid: isOverpaid
                    };
                }
            });
            
            const clientBatch = await Promise.all(batchPromises);
            processedClients = [...processedClients, ...clientBatch];
            
            if ((i + 1) % 2 === 0 || i === batches - 1) {
                setClientDues([...processedClients]);
            }
            
            if (i < batches - 1) {
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }
        
        // وبعد المعالجة، لنطبع بيانات العميل بعد الحساب
        if (processedClients && processedClients.length > 0) {
            console.log("بيانات العميل بعد معالجة المستحقات:", processedClients[0]);
            console.log("المبلغ المطلوب بعد المعالجة:", processedClients[0].RequiredPayment);
            console.log("صافي المبلغ المدفوع:", processedClients[0].PaidAmount);
            console.log("المبلغ المتبقي/الزائد:", processedClients[0].RemainingDue);
            console.log("هل الرصيد زائد؟", processedClients[0].IsOverpaid);
        }
    } catch (error) {
        console.error('خطأ في حساب المستحقات المالية:', error);
        showToast('فشل في حساب المستحقات المالية', 'error');
    }
};



// دالة مساعدة لحساب المستحقات المالية على دفعات مع دعم إصدارات مبالغ الدفع
const calculateClientDuesInBatches = async (clients) => {
    try {
        const companiesWithPayments = await fetchCompanyPaymentAmounts();
        
        const batchSize = 10;
        const batches = Math.ceil(clients.length / batchSize);
        let processedClients = [];
        
        for (let i = 0; i < batches; i++) {
            const startIdx = i * batchSize;
            const endIdx = Math.min(startIdx + batchSize, clients.length);
            const batch = clients.slice(startIdx, endIdx);
            
            const batchPromises = batch.map(async (client) => {
                try {
                    const clientCompany = companiesWithPayments.find(
                        company => company.CompanyName === client.CompanyName
                    );
                    
                    if (!clientCompany) {
                        return {
                            ...client,
                            RequiredPayment: 0,
                            PaidAmount: client.CurrentBalance || 0,
                            RemainingDue: 0,
                            IsOverpaid: false
                        };
                    }
                    
                    const searchDate = client.FirstTransactionDate || new Date().toISOString();
                    
                    const response = await fetch(`${config.API_BASE_URL}/api/reports/companies/${clientCompany.CompanyID}/payment-by-date?date=${encodeURIComponent(searchDate)}`);
                    
                    if (!response.ok) {
                        throw new Error('فشل في جلب مبلغ الدفع المناسب');
                    }
                    
                    const paymentVersion = await response.json();
                    
                    const requiredPayment = paymentVersion.PaymentAmount || 0;
                    const currentBalance = client.CurrentBalance || 0;
                    
                    // حساب المبلغ المتبقي أو الزائد
                    const difference = currentBalance - requiredPayment;
                    const isOverpaid = difference > 0; // هل الرصيد أكبر من المطلوب؟
                    
                    return {
                        ...client,
                        RequiredPayment: requiredPayment,
                        PaidAmount: currentBalance,
                        RemainingDue: Math.abs(difference), // القيمة المطلقة للفرق (سواء متبقي أو زائد)
                        IsOverpaid: isOverpaid, // علامة لتحديد ما إذا كان الرصيد زائد
                        EffectiveFrom: paymentVersion.EffectiveFrom,
                        EffectiveTo: paymentVersion.EffectiveTo
                    };
                } catch (error) {
                    console.error('خطأ في حساب مستحقات العميل:', client.NameArabic, error);
                    
                    const clientCompany = companiesWithPayments.find(
                        company => company.CompanyName === client.CompanyName
                    );
                    
                    const requiredPayment = clientCompany?.PaymentAmount || 0;
                    const currentBalance = client.CurrentBalance || 0;
                    const difference = currentBalance - requiredPayment;
                    const isOverpaid = difference > 0;
                    
                    return {
                        ...client,
                        RequiredPayment: requiredPayment,
                        PaidAmount: currentBalance,
                        RemainingDue: Math.abs(difference),
                        IsOverpaid: isOverpaid
                    };
                }
            });
            
            const clientBatch = await Promise.all(batchPromises);
            processedClients = [...processedClients, ...clientBatch];
            
            if ((i + 1) % 2 === 0 || i === batches - 1) {
                setClientDues([...processedClients]);
            }
            
            if (i < batches - 1) {
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }
    } catch (error) {
        console.error('خطأ في حساب المستحقات المالية:', error);
        showToast('فشل في حساب المستحقات المالية', 'error');
    }
};


// دالة لعرض معاملات العميل
const showClientTransactions = async (clientId, clientName) => {
    try {
        setLoading(true);
        
        const response = await fetch(`${config.API_BASE_URL}/api/reports/clients/${clientId}/all-transactions`);
        
        if (!response.ok) {
            throw new Error('فشل في جلب معاملات العميل');
        }
        
        const data = await response.json();
        
        if (!data.transactions || data.transactions.length === 0) {
            showToast('لا توجد معاملات لهذا العميل', 'info');
            return;
        }
        
        setSelectedTransactions({
            clientId,
            clientName,
            transactions: data.transactions
        });
    } catch (error) {
        console.error('خطأ في جلب معاملات العميل:', error);
        showToast('خطأ في جلب معاملات العميل', 'error');
    } finally {
        setLoading(false);
    }
};


// دالة لعرض تفاصيل حساب العميل
const showClientAccount = async (clientId) => {
    try {
        setLoading(true);
        const response = await fetch(`${config.API_BASE_URL}/api/reports/clients/${clientId}/account`);
        
        if (!response.ok) {
            throw new Error('فشل في جلب تفاصيل العميل');
        }
        
        const data = await response.json();
        
        if (data) {
            // تنسيق البيانات للعرض
            const formattedClient = {
                ...data,
                PhoneNumbers: data.PhoneNumbers || [],
                InactiveBalance: data.InactiveBalance || 0,
                TotalBalance: data.TotalBalance || 0,
                CurrentBalance: data.CurrentBalance || 0,
                RequiredPayment: data.RequiredPayment || 0,
                transactions: data.transactions || []
            };
            
            setSelectedClient(formattedClient);
            setShowAccountDialog(true);
        } else {
            throw new Error('بيانات العميل غير متوفرة');
        }
    } catch (error) {
        console.error('خطأ في جلب تفاصيل العميل:', error);
        showToast('فشل في جلب تفاصيل العميل', 'error');
    } finally {
        setLoading(false);
    }
};



// دالة لاسترجاع سجل تعديلات المبلغ المطلوب
const fetchPaymentHistory = async (clientId) => {
    try {
        setLoading(true);
        console.log("جاري استدعاء سجل تعديلات المبلغ المطلوب للعميل:", clientId);
        
        const response = await fetch(`${config.API_BASE_URL}/api/reports/clients/${clientId}/payment-history`);
        
        if (!response.ok) {
            console.error("استجابة API غير ناجحة:", response.status);
            throw new Error('فشل في جلب سجل تعديلات المبلغ المطلوب');
        }
  
        const data = await response.json();
        console.log("سجل تعديلات المبلغ المطلوب:", data);
        
        return data.history || [];
    } catch (error) {
        console.error('خطأ في جلب سجل تعديلات المبلغ المطلوب:', error);
        showToast('فشل في جلب سجل تعديلات المبلغ المطلوب', 'error');
        return [];
    } finally {
        setLoading(false);
    }
};


// دالة لحفظ المبلغ المطلوب للعميل
const saveClientPayment = async () => {
    // تأكيد إضافي قبل الحفظ
    if (!window.confirm('هل أنت متأكد من تحديث المبلغ المطلوب لهذا العميل؟ هذا الإجراء لا يمكن التراجع عنه بسهولة.')) {
        return;
    }
    
    if (!selectedClient) {
        showToast('خطأ: لم يتم تحديد عميل', 'error');
        return;
    }
    
    if (!paymentForm.RequiredPayment) {
        showToast('يرجى إدخال المبلغ المطلوب', 'error');
        return;
    }
    
    setLoading(true);
    try {
        const response = await fetch(`${config.API_BASE_URL}/api/reports/clients/${selectedClient.PassportID}/payment`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                RequiredPayment: parseFloat(paymentForm.RequiredPayment),
                Notes: paymentForm.Notes,
                UpdatedBy: 'Admin'
            }),
        });
        
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'فشل في تحديث المبلغ المطلوب');
        }
        
        // تحديث بيانات العميل المحدد
        setSelectedClient(prev => ({
            ...prev,
            RequiredPayment: parseFloat(paymentForm.RequiredPayment),
            Notes: paymentForm.Notes
        }));
        
        // إعادة تحميل البيانات في التقرير
        fetchClientReport();
        
        setShowPaymentForm(false);
        showToast('تم تحديث المبلغ المطلوب بنجاح', 'success');
    } catch (error) {
        console.error('خطأ في تحديث المبلغ المطلوب:', error);
        showToast(error.message || 'فشل في تحديث المبلغ المطلوب', 'error');
    } finally {
        setLoading(false);
    }
};


    // جلب تقارير المندوبين
    const fetchDelegateReport = async () => {
        setLoading(true);
        try {
            let url = `${config.API_BASE_URL}/api/reports/reports/delegates?`;            
            if (delegateFilters.delegateId) {
                url += `delegateId=${delegateFilters.delegateId}&`;
            }
            if (delegateFilters.dateFrom) {
                url += `dateFrom=${delegateFilters.dateFrom}&`;
            }
            if (delegateFilters.dateTo) {
                url += `dateTo=${delegateFilters.dateTo}&`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('فشل في جلب تقرير المندوبين');
            
            const data = await response.json();
            setReportData(data.delegates || []);
            
            // حساب الإحصائيات
            if (data.delegates?.length > 0) {
                const totalDeposits = data.delegates.reduce((sum, delegate) => sum + (delegate.TotalDeposits || 0), 0);
                const totalWithdrawals = data.delegates.reduce((sum, delegate) => sum + (delegate.TotalWithdrawals || 0), 0);
                const totalBalance = data.delegates.reduce((sum, delegate) => sum + (delegate.CurrentBalance || 0), 0);
                
                setSummaryData({
                    totalClients: data.delegates.length,
                    totalBalance,
                    totalDeposits,
                    totalWithdrawals
                });
            } else {
                setSummaryData({
                    totalClients: 0,
                    totalBalance: 0,
                    totalDeposits: 0,
                    totalWithdrawals: 0
                });
            }
            
            showToast('تم جلب التقرير بنجاح');
        } catch (error) {
            console.error('خطأ في جلب تقرير المندوبين:', error);
            showToast('فشل في جلب تقرير المندوبين', 'error');
        } finally {
            setLoading(false);
        }
    };

    // جلب تقارير الشركة
  // جلب تقارير الشركة
const fetchCompanyReport = async () => {
    setLoading(true);
    try {
        let url = `${config.API_BASE_URL}/api/reports/reports/companies?`;
        
        if (companyFilters.dateFrom) {
            url += `dateFrom=${companyFilters.dateFrom}&`;
        }
        if (companyFilters.dateTo) {
            url += `dateTo=${companyFilters.dateTo}&`;
        }
        if (companyFilters.transactionType) {
            url += `transactionType=${companyFilters.transactionType}&`;
        }
        if (companyFilters.categoryId) {
            url += `categoryId=${companyFilters.categoryId}&`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('فشل في جلب تقرير معاملات الشركة');
        
        const data = await response.json();
        setReportData(data.transactions || []);
        
        // حساب الإحصائيات من البيانات المسترجعة
        if (data.summary) {
            setSummaryData({
                totalClients: data.summary.TransactionsCount || 0,
                totalBalance: data.summary.Balance || 0,
                totalDeposits: data.summary.TotalIncome || 0,
                totalWithdrawals: data.summary.TotalExpenses || 0
            });
        } else {
            setSummaryData({
                totalClients: 0,
                totalBalance: 0,
                totalDeposits: 0,
                totalWithdrawals: 0
            });
        }
        
        showToast('تم جلب التقرير بنجاح');
    } catch (error) {
        console.error('خطأ في جلب تقرير معاملات الشركة:', error);
        showToast('فشل في جلب تقرير معاملات الشركة', 'error');
    } finally {
        setLoading(false);
    }
};

    // جلب تقارير الخزينة
    const fetchTreasuryReport = async () => {
        setLoading(true);
        try {
            let url = `${config.API_BASE_URL}/api/reports/reports/treasury?`;
            
            if (treasuryFilters.dateFrom) {
                url += `dateFrom=${treasuryFilters.dateFrom}&`;
            }
            if (treasuryFilters.dateTo) {
                url += `dateTo=${treasuryFilters.dateTo}&`;
            }
            if (treasuryFilters.transactionType) {
                url += `transactionType=${treasuryFilters.transactionType}&`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('فشل في جلب تقرير الخزينة');
            
            const data = await response.json();
            setReportData(data.transactions || []);
            
            // حساب الإحصائيات
            if (data.summary) {
                setSummaryData({
                    totalClients: data.summary.TransactionsCount || 0,
                    totalBalance: data.summary.CurrentBalance || 0,
                    totalDeposits: data.summary.TotalDeposits || 0,
                    totalWithdrawals: data.summary.TotalWithdrawals || 0
                });
            } else {
                setSummaryData({
                    totalClients: 0,
                    totalBalance: 0,
                    totalDeposits: 0,
                    totalWithdrawals: 0
                });
            }
            
            showToast('تم جلب التقرير بنجاح');
        } catch (error) {
            console.error('خطأ في جلب تقرير الخزينة:', error);
            showToast('فشل في جلب تقرير الخزينة', 'error');
        } finally {
            setLoading(false);
        }
    };




// جلب التقرير الشامل - محسن مع الفلاتر
const fetchComprehensiveReport = async () => {
    setLoading(true);
    try {
        console.log('فلاتر التقرير الشامل:', comprehensiveFilters);

        const params = new URLSearchParams();
        if (comprehensiveFilters.dateFrom) params.append('dateFrom', comprehensiveFilters.dateFrom);
        if (comprehensiveFilters.dateTo) params.append('dateTo', comprehensiveFilters.dateTo);
        if (comprehensiveFilters.transactionType) params.append('transactionType', comprehensiveFilters.transactionType);
        if (comprehensiveFilters.searchTerm) params.append('searchTerm', comprehensiveFilters.searchTerm);

        const response = await fetch(`${config.API_BASE_URL}/api/reports/reports/comprehensive?${params.toString()}`);

        if (!response.ok) {
            throw new Error('فشل في جلب التقرير الشامل من الخادم');
        }

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'فشل في جلب التقرير الشامل');
        }

        const transactions = data.transactions || [];
        const summary = data.summary || {
            totalClients: transactions.length,
            totalBalance: 0,
            totalDeposits: 0,
            totalWithdrawals: 0
        };

        setReportData(transactions);
        setSummaryData({
            totalClients: summary.totalClients || transactions.length,
            totalBalance: summary.totalBalance || 0,
            totalDeposits: summary.totalDeposits || 0,
            totalWithdrawals: summary.totalWithdrawals || 0
        });
        updatePagination(transactions.length);

        console.log(`تم جلب ${transactions.length} معاملة من التقرير الشامل`);
        showToast('تم جلب التقرير الشامل مع الفلاتر بنجاح');
    } catch (error) {
        console.error('خطأ في جلب التقرير الشامل:', error);
        showToast('فشل في جلب التقرير الشامل', 'error');
    } finally {
        setLoading(false);
    }
};

// دالة مساعدة لتحديد تفاصيل معاملات الموظفين
const getEmployeeTransactionDetails = (transactionType, amount) => {
    const numAmount = parseFloat(amount || 0);
    
    switch(transactionType) {
        case 'راتب':
            return {
                type: 'employee_salary',
                typeName: 'راتب موظف',
                amount: -Math.abs(numAmount) // مصروف للشركة
            };
        case 'بونص':
            return {
                type: 'employee_bonus',
                typeName: 'بونص موظف',
                amount: -Math.abs(numAmount) // مصروف للشركة
            };
        case 'سلفة':
            return {
                type: 'employee_advance',
                typeName: 'سلفة موظف',
                amount: -Math.abs(numAmount) // مصروف للشركة
            };
        case 'دفع قسط':
            return {
                type: 'employee_installment',
                typeName: 'دفع قسط موظف',
                amount: Math.abs(numAmount) // إيراد للشركة
            };
        case 'خصم':
            return {
                type: 'employee_deduction',
                typeName: 'خصم موظف',
                amount: Math.abs(numAmount) // توفير للشركة
            };
        default:
            return {
                type: 'employee_other',
                typeName: 'معاملة موظف أخرى',
                amount: numAmount
            };
    }
};


// جلب تقارير الموظفين
const fetchEmployeeReport = async () => {
    setLoading(true);
    try {
        let url = `${config.API_BASE_URL}/api/reports/reports/employees?`;
        
        const params = new URLSearchParams();
        Object.keys(employeeFilters).forEach(key => {
            if (employeeFilters[key]) {
                params.append(key, employeeFilters[key]);
            }
        });
        
        url += params.toString();
        
        const response = await fetch(url);
        if (!response.ok) throw new Error('فشل في جلب تقرير الموظفين');
        
        const data = await response.json();
        
        setReportData(data.employees || []);
        
        if (data.summary) {
            setSummaryData({
                totalClients: data.summary.TotalEmployees || 0,
                totalBalance: (data.summary.TotalSalariesPaid + data.summary.TotalBonuses) - (data.summary.TotalAdvances + data.summary.TotalDeductions) || 0,
                totalDeposits: data.summary.TotalSalariesPaid + data.summary.TotalBonuses || 0,
                totalWithdrawals: data.summary.TotalAdvances + data.summary.TotalDeductions || 0
            });
        }
        
        showToast('تم جلب التقرير بنجاح');
    } catch (error) {
        console.error('خطأ في جلب تقرير الموظفين:', error);
        showToast('فشل في جلب تقرير الموظفين', 'error');
    } finally {
        setLoading(false);
    }
};

// عرض معاملات الموظف
const showEmployeeTransactions = async (employeeId, employeeName) => {
    try {
        setLoading(true);
        
        const response = await fetch(`${config.API_BASE_URL}/api/reports/employees/${employeeId}/transactions`);
        
        if (!response.ok) {
            throw new Error('فشل في جلب معاملات الموظف');
        }
        
        const data = await response.json();
        
        if (!data.transactions || data.transactions.length === 0) {
            showToast('لا توجد معاملات لهذا الموظف', 'info');
            return;
        }
        
        setSelectedTransactions({
            clientId: employeeId,
            clientName: employeeName,
            transactions: data.transactions
        });
    } catch (error) {
        console.error('خطأ في جلب معاملات الموظف:', error);
        showToast('خطأ في جلب معاملات الموظف', 'error');
    } finally {
        setLoading(false);
    }
};


// تحديث دالة showDelegateDetails في FinancialReports.js
// تحديث دالة showDelegateDetails في FinancialReports.js
const showDelegateDetails = async (delegateId, delegateName) => {
    try {
        setDelegateDetailsLoading(true);
        setShowDelegateDetailsModal(true);
        
        console.log(`جاري جلب التفاصيل الشاملة للمندوب ${delegateName} (ID: ${delegateId})...`);
        
        // استدعاء API الجديد للتفاصيل الشاملة
        const response = await fetch(`${config.API_BASE_URL}/api/reports/delegates/${delegateId}/comprehensive-details`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        
        // معالجة الاستجابة
        if (!response.ok) {
            let errorMessage = 'فشل في جلب تفاصيل المندوب';
            
            try {
                const errorData = await response.json();
                errorMessage = errorData.error || errorData.details || errorMessage;
                console.error('تفاصيل الخطأ من الخادم:', errorData);
            } catch (parseError) {
                console.error('خطأ في تحليل استجابة الخطأ:', parseError);
                errorMessage = `خطأ ${response.status}: ${response.statusText}`;
            }
            
            throw new Error(errorMessage);
        }
        
        const data = await response.json();
        
        if (!data.success) {
            throw new Error(data.error || 'فشل في جلب البيانات');
        }
        
        console.log('تم جلب البيانات الحقيقية بنجاح:', data);
        
        // التحقق من وجود البيانات المطلوبة
        if (!data.delegateInfo) {
            throw new Error('بيانات المندوب غير مكتملة');
        }
        
        setSelectedDelegateDetails({
            delegateId,
            delegateName,
            delegateInfo: data.delegateInfo,
            commissionDetails: data.commissionDetails || [],
            clients: data.clients || [],
            transactions: data.transactions || [],
            summary: data.summary || {}
        });


        console.log('بيانات العمولة الأولى:', data.commissionDetails?.[0]);
        console.log('جميع بيانات العمولات:', data.commissionDetails);
        
        console.log('تم تحميل التفاصيل الشاملة بنجاح:', {
            delegateName,
            commissionsCount: data.commissionDetails?.length || 0,
            clientsCount: data.clients?.length || 0,
            transactionsCount: data.transactions?.length || 0,
            summary: data.summary
        });
        
        showToast(`تم تحميل تفاصيل المندوب ${delegateName} بنجاح`, 'success');
        
    } catch (error) {
        console.error('خطأ في جلب تفاصيل المندوب:', error);
        showToast(`فشل في جلب تفاصيل المندوب: ${error.message}`, 'error');
        
        // إغلاق النافذة في حالة الخطأ
        setShowDelegateDetailsModal(false);
        setSelectedDelegateDetails(null);
    } finally {
        setDelegateDetailsLoading(false);
    }
};





const fetchDebtReport = async () => {
    try {
        setLoading(true);
        setServerPagedClients(false);
        const params = new URLSearchParams();
        Object.entries(debtFilters).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) params.append(key, value);
        });
        const response = await fetch(`${config.API_BASE_URL}/api/reports/reports/debts?${params.toString()}`);
        const data = await response.json();
        if (!response.ok || data.success === false) throw new Error(data.error || 'تعذر جلب تقرير المديونيات');
        setReportData(data.data || []);
        setDebtSummary(data.summary || {});
        setSummaryData({
            totalClients: Number(data.summary?.TotalDebts || 0),
            totalBalance: Number(data.summary?.OutstandingEGP || 0),
            totalDeposits: Number(data.summary?.OutstandingSAR || 0),
            totalWithdrawals: Number(data.summary?.OverdueCount || 0)
        });
        setPagination(prev => ({ ...prev, currentPage: 1, totalItems: (data.data || []).length, totalPages: Math.max(1, Math.ceil((data.data || []).length / prev.itemsPerPage)) }));
    } catch (error) {
        console.error('خطأ في تقرير المديونيات:', error);
        showToast(error.message, 'error');
        setReportData([]);
    } finally {
        setLoading(false);
    }
};

const renderDebtFilters = () => (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">بحث</label>
            <input value={debtFilters.search} onChange={(e) => setDebtFilters({ ...debtFilters, search: e.target.value })} className="w-full border rounded-lg px-3 py-2" placeholder="اسم، هاتف، رقم قومي، رقم ملف أو رقم مديونية" />
        </div>
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select value={debtFilters.status} onChange={(e) => setDebtFilters({ ...debtFilters, status: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                <option value="open">غير مسددة</option>
                <option value="paid">مسددة</option>
                <option value="overdue">متأخرة</option>
                <option value="all">الكل</option>
            </select>
        </div>
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">العملة</label>
            <select value={debtFilters.currency} onChange={(e) => setDebtFilters({ ...debtFilters, currency: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                <option value="all">كل العملات</option>
                <option value="EGP">جنيه مصري</option>
                <option value="SAR">ريال سعودي</option>
            </select>
        </div>
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الشركة</label>
            <select value={debtFilters.companyId} onChange={(e) => setDebtFilters({ ...debtFilters, companyId: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                <option value="">كل الشركات</option>
                {companies.map(company => <option key={company.CompanyID} value={company.CompanyID}>{company.CompanyName}</option>)}
            </select>
        </div>
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">المندوب</label>
            <select value={debtFilters.delegateId} onChange={(e) => setDebtFilters({ ...debtFilters, delegateId: e.target.value })} className="w-full border rounded-lg px-3 py-2">
                <option value="">كل المندوبين</option>
                {delegates.map(delegate => <option key={delegate.DelegateID} value={delegate.DelegateID}>{delegate.DelegateName}</option>)}
            </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">من</label><input type="date" value={debtFilters.dateFrom} onChange={(e) => setDebtFilters({ ...debtFilters, dateFrom: e.target.value })} className="w-full border rounded-lg px-2 py-2" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">إلى</label><input type="date" value={debtFilters.dateTo} onChange={(e) => setDebtFilters({ ...debtFilters, dateTo: e.target.value })} className="w-full border rounded-lg px-2 py-2" /></div>
        </div>
    </div>
);

const renderDebtReport = () => {
    if (!reportData || reportData.length === 0) {
        return <div className="p-10 text-center text-gray-500"><Wallet size={48} className="mx-auto mb-3 text-gray-300" />لا توجد مديونيات مطابقة.</div>;
    }
    const currentPageData = getCurrentPageData(reportData);
    return (
        <>
            <div className="md:hidden space-y-3 p-2">
                {currentPageData.map(debt => (
                    <div key={debt.DebtID} className="bg-white rounded-2xl border p-4 shadow-sm">
                        <div className="flex justify-between gap-3">
                            <div><h3 className="font-black">{debt.NameArabic}</h3><p className="text-xs text-gray-500 mt-1">{debt.DebtNumber} • ملف {debt.OfficeSerial || debt.PassportID}</p></div>
                            <span className={`text-xs px-2 py-1 rounded-full h-fit font-bold ${debt.TripIsActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>{debt.TripIsActive ? 'نشطة' : 'مؤرشفة'}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                            <div className="bg-gray-50 rounded-xl p-2"><span className="text-gray-500 block text-xs">أصل الدين</span><b>{Number(debt.OriginalAmount || 0).toLocaleString()} {debt.OriginalCurrency === 'SAR' ? 'ر.س' : 'ج.م'}</b></div>
                            <div className="bg-rose-50 rounded-xl p-2"><span className="text-gray-500 block text-xs">المتبقي</span><b className="text-rose-700">{Number(debt.OutstandingAmount || 0).toLocaleString()} {debt.OriginalCurrency === 'SAR' ? 'ر.س' : 'ج.م'}</b></div>
                            <div className="bg-green-50 rounded-xl p-2"><span className="text-gray-500 block text-xs">المدفوع فعليًا</span><b>{Number(debt.PaidAmount || 0).toLocaleString()} ج.م</b></div>
                            <div className="bg-amber-50 rounded-xl p-2"><span className="text-gray-500 block text-xs">تسوية إدارية</span><b>{Number(debt.AdministrativeSettledAmount || 0).toLocaleString()} ج.م</b></div>
                            <div className="col-span-2 bg-sky-50 rounded-xl p-2"><span className="text-gray-500">الهاتف: </span><b>{debt.MainPhone || '-'}</b></div>
                            <div className="col-span-2 bg-indigo-50 rounded-xl p-2"><span className="text-gray-500">الشركة: </span><b>{debt.CompanyName || '-'}</b></div>
                            <div className="col-span-2 bg-violet-50 rounded-xl p-2"><span className="text-gray-500">المندوب: </span><b>{debt.DelegateName || '-'}</b></div>
                            <div className="col-span-2 bg-gray-50 rounded-xl p-2"><span className="text-gray-500">الحالة: </span><b>{debt.Status || '-'}</b></div>
                            <div className="col-span-2 bg-gray-50 rounded-xl p-2"><span className="text-gray-500">السبب: </span><b>{debt.Reason}</b></div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50"><tr>
                        <th className="px-4 py-3 text-right">المديونية</th><th className="px-4 py-3 text-right">العميل</th><th className="px-4 py-3 text-right">الهاتف</th><th className="px-4 py-3 text-right">السفرة</th><th className="px-4 py-3 text-right">الشركة</th><th className="px-4 py-3 text-right">المندوب</th><th className="px-4 py-3 text-right">أصل الدين</th><th className="px-4 py-3 text-right">المدفوع فعليًا</th><th className="px-4 py-3 text-right">تسوية إدارية</th><th className="px-4 py-3 text-right">المتبقي</th><th className="px-4 py-3 text-right">الحالة</th><th className="px-4 py-3 text-right">موعد السداد</th><th className="px-4 py-3 text-right">السبب</th>
                    </tr></thead>
                    <tbody className="divide-y">
                        {currentPageData.map(debt => (
                            <tr key={debt.DebtID} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-bold">{debt.DebtNumber}</td>
                                <td className="px-4 py-3"><div className="font-bold">{debt.NameArabic}</div><div className="text-xs text-gray-500">{debt.NationalID || '-'}</div></td>
                                <td className="px-4 py-3 font-bold text-sky-700">{debt.MainPhone || '-'}</td>
                                <td className="px-4 py-3"><div>ملف {debt.OfficeSerial || debt.PassportID}</div><span className={`text-xs font-bold ${debt.TripIsActive ? 'text-green-600' : 'text-gray-500'}`}>{debt.TripIsActive ? 'نشطة' : 'مؤرشفة'}</span></td>
                                <td className="px-4 py-3 font-bold text-indigo-700">{debt.CompanyName || '-'}</td>
                                <td className="px-4 py-3 font-bold text-violet-700">{debt.DelegateName || '-'}</td>
                                <td className="px-4 py-3 font-bold">{Number(debt.OriginalAmount || 0).toLocaleString()} {debt.OriginalCurrency === 'SAR' ? 'ر.س' : 'ج.م'}</td>
                                <td className="px-4 py-3 text-green-700 font-bold">{Number(debt.PaidAmount || 0).toLocaleString()} {debt.OriginalCurrency === 'SAR' ? 'ر.س' : 'ج.م'}</td>
                                <td className="px-4 py-3 text-amber-700 font-bold">{Number(debt.AdministrativeSettledAmount || 0).toLocaleString()} {debt.OriginalCurrency === 'SAR' ? 'ر.س' : 'ج.م'}</td>
                                <td className="px-4 py-3 text-rose-700 font-black">{Number(debt.OutstandingAmount || 0).toLocaleString()} {debt.OriginalCurrency === 'SAR' ? 'ر.س' : 'ج.م'}</td>
                                <td className="px-4 py-3 font-bold">{debt.Status || '-'}</td>
                                <td className="px-4 py-3"><div>{debt.DueType || '-'}</div><div className="text-xs text-gray-500">{debt.DueDate ? new Date(debt.DueDate).toLocaleDateString('ar-EG') : '-'}</div></td>
                                <td className="px-4 py-3 max-w-xs"><div className="truncate" title={debt.Reason}>{debt.Reason}</div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <PaginationComponent />
        </>
    );
};

const executeSearch = () => {
    // إعادة تعيين الصفحة للأولى عند البحث الجديد
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    switch (activeTab) {
        case 'clients':
            fetchClientReport(1, pagination.itemsPerPage);
            break;
        case 'delegates':
            fetchDelegateReport();
            break;
        case 'company':
            fetchCompanyReport();
            break;
        case 'treasury':
            fetchTreasuryReport();
            break;
        case 'comprehensive':
            fetchComprehensiveReport();
            break;
        case 'employees':
            fetchEmployeeReport();
            break;
        case 'debts':
            fetchDebtReport();
            break;
    }
};

    // Export Functions

const exportReport = () => {
    setIsExportModalOpen(true);
};

const handleExportToExcel = () => {
    if (!reportData || reportData.length === 0) {
        showToast('لا توجد بيانات لتصديرها', 'error');
        return;
    }
    
    // استخدام البيانات المعروضة في الصفحة الحالية فقط للتصدير السريع
    const allSourceData = activeTab === 'clients' && clientDues.length > 0 ? clientDues : reportData;
    const currentPageData = getCurrentPageData(allSourceData);
    
    console.log('بيانات التصدير السريع:', currentPageData.length); // للتأكد من العدد
    
    exportToExcelWithData(currentPageData, true); // true يعني تصدير سريع للصفحة الحالية
};

const exportToExcelWithData = (data, isQuickExport = false) => {
    try {
        // التأكد من أن البيانات المرسلة صحيحة
        console.log(`تصدير ${isQuickExport ? 'سريع' : 'متقدم'} لـ ${data.length} سجل`);
        
        const exportSummary = calculateSummaryForData(data);
        const wb = XLSX.utils.book_new();
        let worksheetData = [];
        
        const reportTitle = getReportTitle() + (isQuickExport ? ` (الصفحة ${pagination.currentPage})` : '');
        const currentDate = new Date().toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
        
        worksheetData.push([reportTitle]);
        worksheetData.push([`تاريخ التقرير: ${currentDate}`]);
        
        // إضافة معلومات الصفحة إذا كان تصدير سريع
        if (isQuickExport) {
            worksheetData.push([`الصفحة ${pagination.currentPage} من ${pagination.totalPages} (${data.length} سجل من إجمالي ${pagination.totalItems})`]);
        } else {
            worksheetData.push([`إجمالي السجلات: ${data.length}`]);
        }
        
        worksheetData.push([]);
        
        if (isQuickExport || exportOptions.includeSummary) {
            worksheetData.push(['الملخص المالي']);
            // إضافة بيانات الملخص حسب نوع التقرير
            if (activeTab === 'debts') {
                worksheetData.push(['إجمالي المديونيات', Number(debtSummary.TotalDebts || 0)]);
                worksheetData.push(['المديونيات المفتوحة', Number(debtSummary.OpenDebtCount || 0)]);
                worksheetData.push(['المديونيات المسددة', Number(debtSummary.PaidDebtCount || 0)]);
                worksheetData.push(['المتأخرة عن الموعد', Number(debtSummary.OverdueCount || 0)]);
                worksheetData.push(['المتبقي بالجنيه', `${Number(debtSummary.OutstandingEGP || 0).toLocaleString()} ج.م`]);
                worksheetData.push(['المتبقي بالريال', `${Number(debtSummary.OutstandingSAR || 0).toLocaleString()} ر.س`]);
                worksheetData.push(['بانتظار التسوية بالجنيه', `${Number(debtSummary.PendingSettlementEGP || 0).toLocaleString()} ج.م`]);
                worksheetData.push(['بانتظار التسوية بالريال', `${Number(debtSummary.PendingSettlementSAR || 0).toLocaleString()} ر.س`]);
            } else if (activeTab === 'delegates') {
                worksheetData.push(['عدد المندوبين', exportSummary.totalClients]);
                worksheetData.push(['إجمالي العملاء', exportSummary.totalExpenses]);
                worksheetData.push(['العملاء المسافرين', exportSummary.balance]);
                worksheetData.push(['إجمالي العمولات المدفوعة', `${exportSummary.totalIncome.toLocaleString()} ج.م`]);
            } else {
                worksheetData.push(['عدد السجلات', exportSummary.totalClients]);
                worksheetData.push(['إجمالي الإيداعات', `${exportSummary.totalIncome.toLocaleString()} ج.م`]);
                worksheetData.push(['إجمالي السحوبات', `${exportSummary.totalExpenses.toLocaleString()} ج.م`]);
                worksheetData.push(['الرصيد', `${exportSummary.balance.toLocaleString()} ج.م`]);
            }
            worksheetData.push([]);
        }
        
        const headers = getTableHeaders();
        worksheetData.push(headers);
        
        // إضافة البيانات - استخدام البيانات المرسلة فقط
        const dataToProcess = data; // استخدام البيانات المرسلة كما هي
            
        switch (activeTab) {
            case 'clients':
                dataToProcess.forEach((client, index) => {
                    worksheetData.push([
                        index + 1,
                        client.NameArabic || '-',
                        client.NationalID || '-',
                        client.CompanyDisplayName || client.CompanyName || '-',
                        client.DelegateName || '-',
                        client.PassportProfession || '-',
                        formatPhoneNumber(client.PhoneNumbers),
                        client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-',
                        client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-',
                        client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-',
                        client.CurrentBalance || 0,
                        client.RequiredPayment || 0,
                        client.RemainingDue || 0,
                        !client.RequiredPayment || client.RequiredPayment <= 0 
                            ? 'يلزم تحديد المبلغ المطلوب' 
                            : client.IsOverpaid 
                                ? 'يمكن سحب مبلغ زائد' 
                                : client.RemainingDue > 0 
                                    ? 'متبقي عليه مستحقات' 
                                    : 'مسدد بالكامل',
                        client.LastActiveNote || '-'
                    ]);
                });
                break;
                
            case 'delegates':
                dataToProcess.forEach((delegate, index) => {
                    worksheetData.push([
                        index + 1,
                        delegate.DelegateName || '-',
                        delegate.PhoneNumber || '-',
                        delegate.TotalClients || 0,
                        delegate.TraveledClients || 0,
                        delegate.PaidCommissionClients || 0,
                        delegate.UnpaidCommissionClients || 0,
                        delegate.TotalCommissionsPaid || 0,
                        delegate.TransactionsCount || 0,
                        delegate.LastTransactionDate ? new Date(delegate.LastTransactionDate).toLocaleDateString('ar-EG') : '-'
                    ]);
                });
                break;

            case 'debts':
                dataToProcess.forEach((debt, index) => {
                    worksheetData.push([
                        index + 1,
                        debt.DebtNumber || '-',
                        debt.NameArabic || '-',
                        debt.MainPhone || '-',
                        debt.NationalID || '-',
                        debt.OfficeSerial || debt.PassportID || '-',
                        debt.TripIsActive ? 'نشطة' : 'مؤرشفة',
                        debt.CompanyName || '-',
                        debt.DelegateName || '-',
                        Number(debt.OriginalAmount || 0),
                        Number(debt.PaidAmount || 0),
                        Number(debt.AdministrativeSettledAmount || 0),
                        Number(debt.OutstandingAmount || 0),
                        debt.Status || '-',
                        debt.DueType || '-',
                        debt.DueDate ? new Date(debt.DueDate).toLocaleDateString('ar-EG') : '-',
                        debt.Reason || '-'
                    ]);
                });
                break;
        }
        
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);
        XLSX.utils.book_append_sheet(wb, ws, reportTitle.substring(0, 30));
        
        const fileName = `${getReportTitle()}${isQuickExport ? `_الصفحة_${pagination.currentPage}` : ''}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        
        showToast(`تم تصدير ${isQuickExport ? 'البيانات المعروضة' : 'التقرير'} (${data.length} سجل) بتنسيق Excel بنجاح!`);
        
    } catch (error) {
        console.error('خطأ في التصدير:', error);
        showToast('حدث خطأ أثناء التصدير', 'error');
    }
};


const handlePrint = () => {
    if (!reportData || reportData.length === 0) {
        showToast('لا توجد بيانات للطباعة', 'error');
        return;
    }
    printReport();
};

    // نسخ رقم الهاتف
    const copyToClipboard = (text) => {
        if (!text || text === '-') return;
        
        navigator.clipboard.writeText(text)
            .then(() => {
                showToast('تم نسخ النص بنجاح');
            })
            .catch(err => {
                console.error('فشل في نسخ النص: ', err);
                showToast('فشل في نسخ النص', 'error');
            });
    };



    const exportToExcelAdvanced = async () => {
        try {
            // استخدام getExportData التي تم إصلاحها
            const dataToExport = await getExportDataAsync();
            console.log('البيانات المختارة للتصدير المتقدم:', dataToExport.length, 'سجل');
            console.log('نوع التصدير:', exportOptions.dateRange);
            
            const exportSummary = calculateSummaryForData(dataToExport);
            
            // إنشاء workbook جديد
            const wb = XLSX.utils.book_new();
            let worksheetData = [];
            
            // تحضير البيانات حسب نوع التقرير
            const reportTitle = getReportTitle();
            const currentDate = new Date().toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long'
            });
            
            // إضافة عنوان التقرير والتاريخ مع معلومات الصفحة
            let titleSuffix = '';
            if (exportOptions.dateRange === 'current_page') {
                titleSuffix = ` (الصفحة ${pagination.currentPage} من ${pagination.totalPages})`;
            } else if (exportOptions.dateRange === 'filtered') {
                titleSuffix = ' (البيانات المفلترة)';
            } else if (exportOptions.dateRange === 'all') {
                titleSuffix = ' (جميع البيانات)';
            } else if (exportOptions.dateRange === 'custom') {
                titleSuffix = ' (فترة مخصصة)';
            }
            
            // صف العنوان الرئيسي
            worksheetData.push([reportTitle + titleSuffix]);
            worksheetData.push([`تاريخ التقرير: ${currentDate}`]);
            
            // إضافة معلومات تفصيلية عن البيانات
            if (exportOptions.dateRange === 'current_page') {
                worksheetData.push([`الصفحة ${pagination.currentPage} من ${pagination.totalPages} - عدد السجلات: ${dataToExport.length} من إجمالي ${pagination.totalItems}`]);
            } else {
                worksheetData.push([`عدد السجلات: ${dataToExport.length}`]);
            }
            
            // إضافة معلومات الفترة الزمنية إذا كانت مخصصة
            if (exportOptions.dateRange === 'custom') {
                let dateInfo = 'الفترة المخصصة: ';
                if (exportOptions.customStartDate) dateInfo += `من ${exportOptions.customStartDate} `;
                if (exportOptions.customEndDate) dateInfo += `إلى ${exportOptions.customEndDate}`;
                if (!exportOptions.customStartDate && !exportOptions.customEndDate) dateInfo += 'غير محددة';
                worksheetData.push([dateInfo]);
            }
            
            worksheetData.push([]); // صف فارغ
            
            // إضافة الملخص إذا كان مطلوباً
            if (exportOptions.includeSummary) {
                worksheetData.push(['الملخص المالي']);
                
                if (activeTab === 'delegates') {
                    worksheetData.push(['عدد المندوبين', exportSummary.totalClients]);
                    worksheetData.push(['إجمالي العملاء', exportSummary.totalExpenses]);
                    worksheetData.push(['العملاء المسافرين', exportSummary.balance]);
                    worksheetData.push(['إجمالي العمولات المدفوعة', `${exportSummary.totalIncome.toLocaleString()} ج.م`]);
                } else {
                    if (activeTab === 'company') {
                        worksheetData.push(['عدد المعاملات', exportSummary.totalClients]);
                        worksheetData.push(['إجمالي الإيرادات', `${exportSummary.totalIncome.toLocaleString()} ج.م`]);
                        worksheetData.push(['إجمالي المصروفات', `${exportSummary.totalExpenses.toLocaleString()} ج.م`]);
                        worksheetData.push(['صافي الرصيد', `${exportSummary.balance.toLocaleString()} ج.م`]);
                    } else {
                        worksheetData.push(['عدد السجلات', exportSummary.totalClients]);
                        worksheetData.push(['إجمالي الإيداعات', `${exportSummary.totalIncome.toLocaleString()} ج.م`]);
                        worksheetData.push(['إجمالي السحوبات', `${exportSummary.totalExpenses.toLocaleString()} ج.م`]);
                        worksheetData.push(['الرصيد', `${exportSummary.balance.toLocaleString()} ج.م`]);
                    }
                }
                worksheetData.push([]); // صف فارغ
            }
            
            // إضافة رؤوس الجدول
            const headers = getTableHeaders();
            worksheetData.push(headers);
            
            // إضافة البيانات حسب نوع التقرير
            switch (activeTab) {
                case 'clients':
                    dataToExport.forEach((client, index) => {
                        worksheetData.push([
                            index + 1,
                            client.NameArabic || '-',
                            client.NationalID || '-',
                            client.CompanyDisplayName || client.CompanyName || '-',
                            client.DelegateName || '-',
                            client.PassportProfession || '-',
                            formatPhoneNumber(client.PhoneNumbers),
                            client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-',
                            client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-',
                            client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-',
                            client.CurrentBalance || 0,
                            client.RequiredPayment || 0,
                            client.RemainingDue || 0,
                            !client.RequiredPayment || client.RequiredPayment <= 0 
                                ? 'يلزم تحديد المبلغ المطلوب' 
                                : client.IsOverpaid 
                                    ? 'يمكن سحب مبلغ زائد' 
                                    : client.RemainingDue > 0 
                                        ? 'متبقي عليه مستحقات' 
                                        : 'مسدد بالكامل',
                            client.LastActiveNote || '-'
                        ]);
                    });
                    break;
                    
                case 'delegates':
                    dataToExport.forEach((delegate, index) => {
                        worksheetData.push([
                            index + 1,
                            delegate.DelegateName || '-',
                            delegate.PhoneNumber || '-',
                            delegate.TotalClients || 0,
                            delegate.TraveledClients || 0,
                            delegate.PaidCommissionClients || 0,
                            delegate.UnpaidCommissionClients || 0,
                            delegate.TotalCommissionsPaid || 0,
                            delegate.TransactionsCount || 0,
                            delegate.LastTransactionDate ? new Date(delegate.LastTransactionDate).toLocaleDateString('ar-EG') : '-'
                        ]);
                    });
                    break;
                    
                case 'company':
                    dataToExport.forEach((transaction, index) => {
                        worksheetData.push([
                            index + 1,
                            transaction.TransactionDate ? new Date(transaction.TransactionDate).toLocaleDateString('ar-EG') : '-',
                            transaction.Type === 'income' ? 'إيراد' : 'مصروف',
                            transaction.CategoryName || '-',
                            transaction.Description || '-',
                            transaction.Amount || 0,
                            transaction.PaymentMethodName || '-',
                            transaction.Notes || '-',
                            transaction.CreatedBy || '-'
                        ]);
                    });
                    break;
                    
                case 'treasury':
                    dataToExport.forEach((transaction, index) => {
                        worksheetData.push([
                            index + 1,
                            new Date(transaction.TransactionDate).toLocaleDateString('ar-EG'),
                            transaction.TransactionType || '-',
                            transaction.Amount || 0,
                            transaction.Balance || 0,
                            transaction.ClientName || '-',
                            transaction.Notes || '-',
                            transaction.CreatedBy || '-'
                        ]);
                    });
                    break;
                    
                case 'comprehensive':
                    dataToExport.forEach((transaction, index) => {
                        worksheetData.push([
                            index + 1,
                            transaction.typeName || '-',
                            transaction.name || '-',
                            transaction.company || '-',
                            transaction.paymentMethod || 'غير محدد',
                            transaction.amount || 0,
                            transaction.date ? new Date(transaction.date).toLocaleDateString('ar-EG') : '-',
                            transaction.notes || 'لا توجد ملاحظات'
                        ]);
                    });
                    break;
                    
                case 'employees':
                    dataToExport.forEach((employee, index) => {
                        worksheetData.push([
                            index + 1,
                            employee.EmployeeName || '-',
                            employee.Position || '-',
                            employee.BaseSalary || 0,
                            employee.TotalSalaries || 0,
                            employee.TotalAdvances || 0,
                            employee.TotalBonuses || 0,
                            employee.TransactionsCount || 0,
                            employee.LastTransactionDate ? new Date(employee.LastTransactionDate).toLocaleDateString('ar-EG') : '-',
                            'إجراءات'
                        ]);
                    });
                    break;
            }
            
            // إنشاء worksheet
            const ws = XLSX.utils.aoa_to_sheet(worksheetData);
            
            // تحديد نطاق البيانات
            const range = XLSX.utils.decode_range(ws['!ref']);
            
            // تطبيق التنسيق
            // عرض الأعمدة
            const colWidths = [];
            for (let i = 0; i <= range.e.c; i++) {
                if (i === 0) {
                    colWidths.push({ wch: 5 }); // عمود الرقم
                } else if (i === 1) {
                    colWidths.push({ wch: 25 }); // عمود الاسم
                } else if (i >= range.e.c - 2) {
                    colWidths.push({ wch: 20 }); // الأعمدة الأخيرة
                } else {
                    colWidths.push({ wch: 15 }); // باقي الأعمدة
                }
            }
            ws['!cols'] = colWidths;
            
            // تنسيق الخلايا
            for (let row = 0; row <= range.e.r; row++) {
                for (let col = 0; col <= range.e.c; col++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                    if (!ws[cellAddress]) continue;
                    
                    // إنشاء كائن التنسيق
                    ws[cellAddress].s = {};
                    
                    // العنوان الرئيسي
                    if (row === 0) {
                        ws[cellAddress].s = {
                            font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
                            fill: { fgColor: { rgb: "4472C4" } },
                            alignment: { horizontal: "center", vertical: "center" },
                            border: {
                                top: { style: "thick", color: { rgb: "000000" } },
                                bottom: { style: "thick", color: { rgb: "000000" } },
                                left: { style: "thick", color: { rgb: "000000" } },
                                right: { style: "thick", color: { rgb: "000000" } }
                            }
                        };
                    }
                    // تاريخ التقرير
                    else if (row === 1 || row === 2) {
                        ws[cellAddress].s = {
                            font: { bold: true, sz: 12 },
                            fill: { fgColor: { rgb: "E8F1FF" } },
                            alignment: { horizontal: "center" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                    }
                    // الملخص
                    else if (exportOptions.includeSummary && row >= 4 && row <= (exportOptions.includeSummary ? 9 : 4)) {
                        ws[cellAddress].s = {
                            font: { bold: true, sz: 11 },
                            fill: { fgColor: { rgb: "FFF2CC" } },
                            alignment: { horizontal: "center" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                    }
                    // رؤوس الجدول
                    else if (row === (exportOptions.includeSummary ? 11 : 4)) {
                        ws[cellAddress].s = {
                            font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
                            fill: { fgColor: { rgb: "70AD47" } },
                            alignment: { horizontal: "center", vertical: "center" },
                            border: {
                                top: { style: "thick", color: { rgb: "000000" } },
                                bottom: { style: "thick", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                    }
                    // بيانات الجدول
                    else if (row > (exportOptions.includeSummary ? 11 : 4)) {
                        let fillColor = "FFFFFF";
                        
                        // تلوين الصفوف بالتناوب
                        if ((row - (exportOptions.includeSummary ? 12 : 5)) % 2 === 1) {
                            fillColor = "F8F9FA";
                        }
                        
                        // تلوين خاص للأرقام الموجبة/السالبة في تقارير المالية
                        if (activeTab === 'clients' && (col === 10 || col === 12)) { // عمود الرصيد والمتبقي
                            const value = ws[cellAddress].v;
                            if (typeof value === 'number') {
                                if (value > 0) {
                                    fillColor = "E8F5E8"; // أخضر فاتح للموجب
                                } else if (value < 0) {
                                    fillColor = "FFE8E8"; // أحمر فاتح للسالب
                                }
                            }
                        }
                        
                        ws[cellAddress].s = {
                            font: { sz: 10 },
                            fill: { fgColor: { rgb: fillColor } },
                            alignment: { horizontal: "center", vertical: "center" },
                            border: {
                                top: { style: "thin", color: { rgb: "D0D0D0" } },
                                bottom: { style: "thin", color: { rgb: "D0D0D0" } },
                                left: { style: "thin", color: { rgb: "D0D0D0" } },
                                right: { style: "thin", color: { rgb: "D0D0D0" } }
                            }
                        };
                        
                        // تنسيق الأرقام
                        if (typeof ws[cellAddress].v === 'number' && ws[cellAddress].v > 1000) {
                            ws[cellAddress].z = '#,##0';
                        }
                    }
                }
            }
            
            // دمج خلايا العنوان
            ws['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: range.e.c } }, // العنوان الرئيسي
                { s: { r: 1, c: 0 }, e: { r: 1, c: range.e.c } }, // تاريخ التقرير
                { s: { r: 2, c: 0 }, e: { r: 2, c: range.e.c } }  // معلومات الصفحة
            ];
            
            // إضافة الملخص للدمج إذا كان موجوداً
            if (exportOptions.includeSummary) {
                ws['!merges'].push({ s: { r: 4, c: 0 }, e: { r: 4, c: range.e.c } }); // عنوان الملخص
            }
            
            // إضافة worksheet إلى workbook
            XLSX.utils.book_append_sheet(wb, ws, reportTitle.substring(0, 30));
            
            // تحديد اسم الملف
            let fileName = '';
            if (exportOptions.dateRange === 'current_page') {
                fileName = `${reportTitle.replace(/\s+/g, '_')}_الصفحة_${pagination.currentPage}_${new Date().toISOString().split('T')[0]}.xlsx`;
            } else if (exportOptions.dateRange === 'filtered') {
                fileName = `${reportTitle.replace(/\s+/g, '_')}_مفلتر_${new Date().toISOString().split('T')[0]}.xlsx`;
            } else if (exportOptions.dateRange === 'custom') {
                fileName = `${reportTitle.replace(/\s+/g, '_')}_مخصص_${new Date().toISOString().split('T')[0]}.xlsx`;
            } else {
                fileName = `${reportTitle.replace(/\s+/g, '_')}_كامل_${new Date().toISOString().split('T')[0]}.xlsx`;
            }
            
            // حفظ الملف
            XLSX.writeFile(wb, fileName);
            
            // رسالة نجاح مفصلة
            let successMessage = 'تم تصدير التقرير بتنسيق Excel احترافي بنجاح!';
            if (exportOptions.dateRange === 'current_page') {
                successMessage = `تم تصدير الصفحة ${pagination.currentPage} (${dataToExport.length} سجل) بتنسيق Excel بنجاح!`;
            }
            
            showToast(successMessage);
            
            // طباعة معلومات التأكيد في console
            console.log('تم إنجاز التصدير:', {
                نوع_التصدير: exportOptions.dateRange,
                عدد_السجلات: dataToExport.length,
                اسم_الملف: fileName,
                نوع_التقرير: activeTab
            });
            
        } catch (error) {
            console.error('خطأ في التصدير:', error);
            showToast('حدث خطأ أثناء التصدير: ' + error.message, 'error');
        }
    };

// دالة إنشاء HTML مع تنسيق Excel كامل
const createExcelHTML = (data, summary) => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
    });

    const reportTitle = getReportTitle();
    const tableHeaders = getTableHeaders();
    const tableRows = createTableRows(data);

    // تحديد النص المناسب للملخص حسب نوع التقرير بدون النص الإضافي
    let summaryText = '';
    if (activeTab === 'delegates') {
        summaryText = `عدد المندوبين ${summary.totalClients} | إجمالي العملاء ${summary.totalExpenses.toLocaleString()} | العملاء المسافرين ${summary.balance.toLocaleString()} | إجمالي العمولات المدفوعة ${summary.totalIncome.toLocaleString()} ج.م`;
   } else {
    if (activeTab === 'company') {
        summaryText = `عدد المعاملات ${summary.totalClients} | إجمالي الإيرادات ${summary.totalIncome.toLocaleString()} ج.م | إجمالي المصروفات ${summary.totalExpenses.toLocaleString()} ج.م | صافي الرصيد ${summary.balance.toLocaleString()} ج.م`;
    } else {
        summaryText = `عدد السجلات ${summary.totalClients} | إجمالي الإيداعات ${summary.totalIncome.toLocaleString()} ج.م | إجمالي السحوبات ${summary.totalExpenses.toLocaleString()} ج.م | الرصيد ${summary.balance.toLocaleString()} ج.م`;
    }
}

    return `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
            <meta charset="UTF-8">
            <!--[if gte mso 9]>
            <xml>
                <x:ExcelWorkbook>
                    <x:ExcelWorksheets>
                        <x:ExcelWorksheet>
                            <x:Name>${reportTitle}</x:Name>
                            <x:WorksheetOptions>
                                <x:Print>
                                    <x:ValidPrinterInfo/>
                                    <x:PaperSizeIndex>9</x:PaperSizeIndex>
                                    <x:HorizontalResolution>600</x:HorizontalResolution>
                                    <x:VerticalResolution>600</x:VerticalResolution>
                                    <x:Scale>100</x:Scale>
                                    <x:FitToPage/>
                                    <x:FitWidth>1</x:FitWidth>
                                    <x:FitHeight>32767</x:FitHeight>
                                    <x:Orientation>Portrait</x:Orientation>
                                    <x:TopMargin>0.75</x:TopMargin>
                                    <x:BottomMargin>0.75</x:BottomMargin>
                                    <x:LeftMargin>0.7</x:LeftMargin>
                                    <x:RightMargin>0.7</x:RightMargin>
                                    <x:HeaderMargin>0.3</x:HeaderMargin>
                                    <x:FooterMargin>0.3</x:FooterMargin>
                                </x:Print>
                                <x:Selected/>
                                <x:ProtectContents>False</x:ProtectContents>
                                <x:ProtectObjects>False</x:ProtectObjects>
                                <x:ProtectScenarios>False</x:ProtectScenarios>
                            </x:WorksheetOptions>
                        </x:ExcelWorksheet>
                    </x:ExcelWorksheets>
                </x:ExcelWorkbook>
            </xml>
            <![endif]-->
            <style>
                @page {
                    size: A4 landscape;
                    margin: 0.75in 0.7in 0.75in 0.7in;
                    mso-page-orientation: landscape;
                    mso-header-margin: 0.3in;
                    mso-footer-margin: 0.3in;
                }
                
                table { 
                    border-collapse: collapse; 
                    width: 100%; 
                    direction: rtl;
                    font-family: Calibri, Arial, sans-serif;
                    page-break-inside: avoid;
                }
                
                td {
                    border: 2px solid #000000 !important;
                    padding: 8px !important;
                    vertical-align: middle !important;
                    text-align: center !important;
                    color: #000000 !important;
                    font-weight: normal !important;
                }
                
                .header-main {
                    background-color: #ADD8E6 !important;
                    font-size: 22pt !important;
                    font-weight: bold !important;
                    text-align: center !important;
                    padding: 12px !important;
                    height: 45px !important;
                    color: #000000 !important;
                }
                
                .summary-row {
                    background-color: #ADD8E6 !important;
                    font-size: 18pt !important;
                    font-weight: bold !important;
                    text-align: center !important;
                    padding: 12px !important;
                    height: 40px !important;
                    color: #000000 !important;
                }
                
                .table-header {
                    background-color: #D3D3D3 !important;
                    font-size: 16pt !important;
                    font-weight: bold !important;
                    text-align: center !important;
                    padding: 10px !important;
                    height: 35px !important;
                    color: #000000 !important;
                }
                
                .data-cell {
                    font-size: 14pt !important;
                    text-align: center !important;
                    padding: 8px !important;
                    height: 30px !important;
                    background-color: #FFFFFF !important;
                    color: #000000 !important;
                    font-weight: normal !important;
                }
                
                .row-number {
                    background-color: #F0F0F0 !important;
                    font-weight: bold !important;
                    font-size: 14pt !important;
                    color: #000000 !important;
                }
                
                .positive-amount {
                    color: #0F7B0F !important;
                    font-weight: bold !important;
                }
                
                .negative-amount {
                    color: #B91C1C !important;
                    font-weight: bold !important;
                }
                
     

           @media (max-width: 768px) {
               .financial-mobile-root {
                   background: #f3f6fb;
                   width: 100%;
                   max-width: 100vw;
               }

               .financial-mobile-root * {
                   max-width: 100%;
                   box-sizing: border-box;
               }

               .financial-mobile-header h1 svg {
                   width: 22px;
                   height: 22px;
               }

               .financial-report-tabs button {
                   white-space: nowrap;
                   flex: 0 0 auto;
               }

               .financial-report-toolbar button,
               .financial-report-toolbar a {
                   width: 100%;
                   min-width: 0;
                   white-space: normal;
                   line-height: 1.35;
               }

               .financial-report-filter-panel input,
               .financial-report-filter-panel select,
               .financial-report-filter-panel textarea {
                   min-height: 44px;
                   font-size: 16px !important;
                   border-radius: 14px;
                   background: #fff;
                   width: 100% !important;
                   min-width: 0 !important;
                   box-sizing: border-box !important;
               }

               .financial-report-filter-panel input[type="date"] {
                   min-height: 40px !important;
                   font-size: 13px !important;
                   padding: 4px 6px !important;
                   -webkit-appearance: none;
                   appearance: none;
               }

               .financial-report-filter-panel label {
                   font-size: 13px !important;
                   white-space: normal !important;
               }

               .financial-report-filter-panel .grid {
                   gap: 10px !important;
               }

               .financial-report-filter-panel > .space-y-4 > div {
                   width: 100% !important;
                   overflow: visible !important;
               }

               .financial-report-filter-panel .bg-orange-50 {
                   overflow: visible !important;
               }

               .financial-summary-grid > div {
                   min-height: 96px;
                   overflow: hidden;
               }

               .financial-summary-grid svg {
                   width: 28px;
                   height: 28px;
               }

               .financial-report-content {
                   background: transparent !important;
                   box-shadow: none !important;
                   overflow: visible !important;
               }

               .financial-report-content > .p-4.border-b {
                   background: #fff;
                   border-radius: 18px;
                   margin-bottom: 10px;
                   padding: 14px !important;
                   box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06);
               }

               .financial-report-content .overflow-x-auto {
                   overflow: visible !important;
               }

               .financial-report-content table {
                   display: block;
                   border: 0 !important;
                   min-width: 0 !important;
                   width: 100% !important;
                   background: transparent !important;
               }

               .financial-report-content thead {
                   display: none !important;
               }

               .financial-report-content tbody {
                   display: grid;
                   gap: 12px;
                   width: 100%;
               }

               .financial-report-content tbody tr {
                   display: block;
                   background: #fff !important;
                   border: 1px solid #e5e7eb;
                   border-radius: 18px;
                   padding: 12px;
                   box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
                   overflow: hidden;
               }

               .financial-report-content tbody tr td {
                   display: flex;
                   align-items: center;
                   justify-content: space-between;
                   gap: 12px;
                   padding: 8px 0 !important;
                   border: 0 !important;
                   border-bottom: 1px dashed #e5e7eb !important;
                   font-size: 13px !important;
                   text-align: left !important;
                   color: #111827;
                   white-space: normal !important;
                   overflow: visible !important;
               }

               .financial-report-content tbody tr td:last-child {
                   border-bottom: 0 !important;
                   padding-bottom: 0 !important;
               }

               .financial-report-content tbody tr td::before {
                   content: "بيان";
                   color: #6b7280;
                   font-size: 12px;
                   font-weight: 700;
                   min-width: 92px;
                   text-align: right;
               }

               .financial-report-content tbody tr td span,
               .financial-report-content tbody tr td div,
               .financial-report-content tbody tr td button {
                   max-width: 60%;
               }

               .financial-report-content tbody tr td button {
                   justify-content: center;
                   min-height: 38px;
                   border-radius: 12px;
               }

               .financial-report-clients tbody tr td:nth-child(1)::before { content: "اسم العميل"; }
               .financial-report-clients tbody tr td:nth-child(2)::before { content: "الرقم القومي"; }
               .financial-report-clients tbody tr td:nth-child(3)::before { content: "الشركة"; }
               .financial-report-clients tbody tr td:nth-child(4)::before { content: "المندوب"; }
               .financial-report-clients tbody tr td:nth-child(5)::before { content: "الرصيد الحالي"; }
               .financial-report-clients tbody tr td:nth-child(6)::before { content: "المبلغ المطلوب"; }
               .financial-report-clients tbody tr td:nth-child(7)::before { content: "المتبقي"; }
               .financial-report-clients tbody tr td:nth-child(8)::before { content: "إجراءات"; }

               .financial-report-delegates tbody tr td:nth-child(1)::before { content: "م"; }
               .financial-report-delegates tbody tr td:nth-child(2)::before { content: "اسم المندوب"; }
               .financial-report-delegates tbody tr td:nth-child(3)::before { content: "رقم الهاتف"; }
               .financial-report-delegates tbody tr td:nth-child(4)::before { content: "إجمالي العملاء"; }
               .financial-report-delegates tbody tr td:nth-child(5)::before { content: "المسافرين"; }
               .financial-report-delegates tbody tr td:nth-child(6)::before { content: "عمولة مدفوعة"; }
               .financial-report-delegates tbody tr td:nth-child(7)::before { content: "غير مدفوعة"; }
               .financial-report-delegates tbody tr td:nth-child(8)::before { content: "إجمالي مدفوع"; }
               .financial-report-delegates tbody tr td:nth-child(9)::before { content: "إجراءات"; }

               .financial-report-company tbody tr td:nth-child(1)::before { content: "التاريخ"; }
               .financial-report-company tbody tr td:nth-child(2)::before { content: "النوع"; }
               .financial-report-company tbody tr td:nth-child(3)::before { content: "التصنيف"; }
               .financial-report-company tbody tr td:nth-child(4)::before { content: "الوصف"; }
               .financial-report-company tbody tr td:nth-child(5)::before { content: "المبلغ"; }
               .financial-report-company tbody tr td:nth-child(6)::before { content: "طريقة الدفع"; }
               .financial-report-company tbody tr td:nth-child(7)::before { content: "الملاحظات"; }
               .financial-report-company tbody tr td:nth-child(8)::before { content: "بواسطة"; }

               .financial-report-treasury tbody tr td:nth-child(1)::before { content: "التاريخ"; }
               .financial-report-treasury tbody tr td:nth-child(2)::before { content: "النوع"; }
               .financial-report-treasury tbody tr td:nth-child(3)::before { content: "المبلغ"; }
               .financial-report-treasury tbody tr td:nth-child(4)::before { content: "الرصيد"; }
               .financial-report-treasury tbody tr td:nth-child(5)::before { content: "العميل"; }
               .financial-report-treasury tbody tr td:nth-child(6)::before { content: "الملاحظات"; }
               .financial-report-treasury tbody tr td:nth-child(7)::before { content: "بواسطة"; }

               .financial-report-comprehensive tbody tr td:nth-child(1)::before { content: "نوع المعاملة"; }
               .financial-report-comprehensive tbody tr td:nth-child(2)::before { content: "الاسم/الوصف"; }
               .financial-report-comprehensive tbody tr td:nth-child(3)::before { content: "الشركة/التصنيف"; }
               .financial-report-comprehensive tbody tr td:nth-child(4)::before { content: "طريقة الدفع"; }
               .financial-report-comprehensive tbody tr td:nth-child(5)::before { content: "المبلغ"; }
               .financial-report-comprehensive tbody tr td:nth-child(6)::before { content: "التاريخ"; }
               .financial-report-comprehensive tbody tr td:nth-child(7)::before { content: "الملاحظات"; }

               .financial-report-employees tbody tr td:nth-child(1)::before { content: "م"; }
               .financial-report-employees tbody tr td:nth-child(2)::before { content: "اسم الموظف"; }
               .financial-report-employees tbody tr td:nth-child(3)::before { content: "المنصب"; }
               .financial-report-employees tbody tr td:nth-child(4)::before { content: "الراتب"; }
               .financial-report-employees tbody tr td:nth-child(5)::before { content: "إجمالي الرواتب"; }
               .financial-report-employees tbody tr td:nth-child(6)::before { content: "السلف"; }
               .financial-report-employees tbody tr td:nth-child(7)::before { content: "البونص"; }
               .financial-report-employees tbody tr td:nth-child(8)::before { content: "عدد المعاملات"; }
               .financial-report-employees tbody tr td:nth-child(9)::before { content: "آخر معاملة"; }
               .financial-report-employees tbody tr td:nth-child(10)::before { content: "إجراءات"; }

               .financial-mobile-root .financial-mobile-modal {
                   align-items: stretch !important;
                   justify-content: stretch !important;
                   padding: 0 !important;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white {
                   width: 100vw !important;
                   height: 100dvh !important;
                   max-width: 100vw !important;
                   max-height: 100dvh !important;
                   margin: 0 !important;
                   border-radius: 0 !important;
                   overflow-y: auto !important;
                   display: flex;
                   flex-direction: column;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex:first-child,
               .financial-mobile-root .financial-mobile-modal > .bg-white > div:first-child {
                   position: sticky;
                   top: 0;
                   z-index: 10;
                   background: #fff;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex:first-child {
                   flex-direction: column !important;
                   align-items: stretch !important;
                   gap: 12px !important;
                   padding: 14px !important;
                   border-radius: 0 !important;
                   background: linear-gradient(135deg, #eef4ff, #ffffff) !important;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex:first-child > .flex {
                   width: 100% !important;
                   flex-wrap: wrap !important;
                   align-items: center !important;
                   justify-content: space-between !important;
                   gap: 10px !important;
               }

               .financial-mobile-root .financial-mobile-modal h2 {
                   font-size: 1.15rem !important;
                   line-height: 1.55 !important;
                   word-break: break-word !important;
               }

               .financial-mobile-root .financial-mobile-modal h3 {
                   font-size: 1.05rem !important;
                   line-height: 1.5 !important;
               }

               .financial-mobile-root .financial-mobile-modal .text-2xl {
                   font-size: 1.25rem !important;
                   line-height: 1.55 !important;
               }

               .financial-mobile-root .financial-mobile-modal .text-3xl {
                   font-size: 1.45rem !important;
                   line-height: 1.4 !important;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex:first-child button {
                   flex: 1 1 40% !important;
                   justify-content: center !important;
                   min-height: 44px !important;
                   padding: 8px 10px !important;
                   font-size: 13px !important;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex:first-child button[title="إغلاق"],
               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex:first-child button.text-gray-400 {
                   flex: 0 0 44px !important;
                   width: 44px !important;
                   padding: 0 !important;
                   background: #f3f4f6 !important;
                   border-radius: 14px !important;
                   color: #374151 !important;
               }

               .financial-mobile-root .financial-mobile-modal > .bg-white > div:nth-child(2),
               .financial-mobile-root .financial-mobile-modal > .bg-white > .flex-1 {
                   padding: 12px !important;
               }

               .financial-mobile-root .financial-mobile-modal .p-6 {
                   padding: 12px !important;
               }

               .financial-mobile-root .financial-mobile-modal .p-5 {
                   padding: 12px !important;
               }

               .financial-mobile-root .financial-mobile-modal .mb-4 {
                   margin-bottom: 10px !important;
               }

               .financial-mobile-root .financial-mobile-modal .gap-4 {
                   gap: 10px !important;
               }

               .financial-mobile-root .financial-mobile-modal .flex.justify-end.mb-4.gap-3 {
                   display: grid !important;
                   grid-template-columns: 1fr 1fr !important;
                   gap: 10px !important;
               }

               .financial-mobile-root .financial-mobile-modal .flex.justify-end.mb-4.gap-3 button {
                   width: 100% !important;
                   justify-content: center !important;
                   padding: 10px !important;
                   font-size: 13px !important;
               }

               .financial-mobile-root .financial-mobile-modal .flex.justify-between.items-start {
                   flex-direction: column !important;
                   align-items: stretch !important;
                   gap: 12px !important;
               }

               .financial-mobile-root .financial-mobile-modal .flex.items-center.gap-4 {
                   align-items: flex-start !important;
                   gap: 10px !important;
               }

               .financial-mobile-root .financial-mobile-modal .h-16.w-16 {
                   width: 48px !important;
                   height: 48px !important;
                   min-width: 48px !important;
               }

               .financial-mobile-root .financial-mobile-modal .text-right {
                   text-align: right !important;
               }

               .financial-mobile-root .financial-mobile-modal .overflow-x-auto {
                   overflow: visible !important;
               }

               .financial-mobile-root .financial-mobile-modal table {
                   display: block !important;
                   width: 100% !important;
                   min-width: 0 !important;
                   border: 0 !important;
                   background: transparent !important;
               }

               .financial-mobile-root .financial-mobile-modal thead {
                   display: none !important;
               }

               .financial-mobile-root .financial-mobile-modal tbody {
                   display: grid !important;
                   gap: 12px !important;
                   width: 100% !important;
               }

               .financial-mobile-root .financial-mobile-modal tbody tr {
                   display: block !important;
                   background: #ffffff !important;
                   border: 1px solid #e5e7eb !important;
                   border-radius: 18px !important;
                   padding: 12px !important;
                   box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06) !important;
                   overflow: hidden !important;
               }

               .financial-mobile-root .financial-mobile-modal tbody tr td {
                   display: flex !important;
                   align-items: center !important;
                   justify-content: space-between !important;
                   gap: 10px !important;
                   padding: 8px 0 !important;
                   border: 0 !important;
                   border-bottom: 1px dashed #e5e7eb !important;
                   text-align: left !important;
                   font-size: 13px !important;
                   white-space: normal !important;
                   overflow: visible !important;
                   color: #111827 !important;
               }

               .financial-mobile-root .financial-mobile-modal tbody tr td:last-child {
                   border-bottom: 0 !important;
               }

               .financial-mobile-root .financial-mobile-modal tbody tr td::before {
                   content: "بيان";
                   color: #6b7280;
                   font-size: 12px;
                   font-weight: 800;
                   min-width: 80px;
                   text-align: right;
               }

               .financial-mobile-root .financial-mobile-modal tbody tr td:nth-child(1)::before { content: "التاريخ"; }
               .financial-mobile-root .financial-mobile-modal tbody tr td:nth-child(2)::before { content: "النوع"; }
               .financial-mobile-root .financial-mobile-modal tbody tr td:nth-child(3)::before { content: "المبلغ"; }
               .financial-mobile-root .financial-mobile-modal tbody tr td:nth-child(4)::before { content: "الرصيد"; }
               .financial-mobile-root .financial-mobile-modal tbody tr td:nth-child(5)::before { content: "ملاحظات"; }
               .financial-mobile-root .financial-mobile-modal tbody tr td:nth-child(6)::before { content: "الحالة"; }

               .financial-mobile-root .financial-mobile-modal tbody tr td > * {
                   max-width: 62% !important;
               }

               .financial-mobile-root .financial-mobile-modal input,
               .financial-mobile-root .financial-mobile-modal select,
               .financial-mobile-root .financial-mobile-modal textarea {
                   min-height: 44px;
                   font-size: 16px;
                   border-radius: 14px;
               }

               .financial-mobile-root .financial-mobile-modal button {
                   min-height: 40px;
                   border-radius: 12px;
               }

               .financial-mobile-root .financial-mobile-modal .grid {
                   grid-template-columns: 1fr !important;
               }

               .financial-mobile-root .mt-8.bg-gradient-to-r {
                   margin-top: 12px !important;
                   padding: 12px !important;
                   border-radius: 18px !important;
               }

               .financial-mobile-root .mt-8.bg-gradient-to-r .grid {
                   grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                   gap: 8px !important;
               }

               .financial-mobile-root .mt-8.bg-gradient-to-r .text-3xl,
               .financial-mobile-root .mt-8.bg-gradient-to-r .text-4xl {
                   font-size: 1.25rem !important;
               }

               .financial-mobile-root .no-scrollbar::-webkit-scrollbar {
                   display: none;
               }
               .financial-mobile-root .no-scrollbar {
                   -ms-overflow-style: none;
                   scrollbar-width: none;
               }

               /* ========== تفاصيل الحساب - موبايل فقط ========== */
               .financial-account-dialog {
                   position: fixed !important;
                   inset: 0 !important;
                   z-index: 9999 !important;
                   overflow-y: auto !important;
                   -webkit-overflow-scrolling: touch !important;
               }

               .financial-account-dialog > div {
                   min-height: 100% !important;
                   display: flex !important;
                   align-items: flex-start !important;
                   justify-content: center !important;
                   padding: 0 !important;
               }

               .financial-account-dialog > div > div {
                   width: 100% !important;
                   max-width: 100% !important;
                   border-radius: 0 !important;
                   min-height: 100dvh !important;
               }

               /* Header ثابت في الأعلى */
               .financial-account-dialog .sticky {
                   position: sticky !important;
                   top: 0 !important;
                   z-index: 20 !important;
                   background: #fff !important;
                   box-shadow: 0 1px 6px rgba(0,0,0,0.07) !important;
               }

               /* منع أي CSS عام يؤثر على الـ grid داخل الـ dialog */
               .financial-account-dialog .grid {
                   grid-template-columns: 1fr !important;
               }

               /* سجل المعاملات - كل معاملة card منفصل */
               .financial-account-dialog .divide-y > div {
                   padding: 14px !important;
                   border-bottom: 1px solid #f3f4f6 !important;
               }

               .financial-account-dialog .divide-y > div:last-child {
                   border-bottom: none !important;
               }

               /* الصف الأول في المعاملة: تاريخ + نوع + حالة */
               .financial-account-dialog .divide-y > div > .flex.items-center.justify-between.mb-2 {
                   flex-wrap: wrap !important;
                   gap: 6px !important;
                   margin-bottom: 10px !important;
               }

               /* الصف الثاني: المبلغ + الرصيد */
               .financial-account-dialog .divide-y > div > .flex.items-center.justify-between:not(.mb-2) {
                   display: flex !important;
                   justify-content: space-between !important;
                   align-items: flex-end !important;
               }

               /* أزرار الإجراءات */
               .financial-account-dialog .grid.grid-cols-1 button {
                   min-height: 52px !important;
                   font-size: 15px !important;
                   border-radius: 14px !important;
                   font-weight: 600 !important;
               }
           }

           @media print {
                    table {
                        page-break-inside: avoid;
                    }
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    td {
                        page-break-inside: avoid;
                    }
                }
            </style>
        </head>
        <body>
            <table>
                <!-- تاريخ التقرير -->
                <tr>
                    <td colspan="${tableHeaders.length}" class="header-main">
                        ${reportTitle} - تاريخ التقرير: ${formattedDate}
                    </td>
                </tr>
                
                ${exportOptions.includeSummary ? `
                <!-- الملخص المالي -->
                <tr>
                    <td colspan="${tableHeaders.length}" class="summary-row">
                        ${summaryText}
                    </td>
                </tr>
                ` : ''}
                
                <!-- رؤوس الجدول -->
                <tr>
                    ${tableHeaders.map(header => `<td class="table-header">${header}</td>`).join('')}
                </tr>
                
                <!-- بيانات الجدول -->
                ${tableRows}
            </table>
        </body>
        </html>
    `;
};

const exportToCSV = async () => {
   const dataToExport = await getExportDataAsync();
   const clientsData = activeTab === 'clients' && clientDues.length > 0 ? clientDues : dataToExport;
   
   // إنشاء محتوى CSV
   let csvContent = "\uFEFF"; // BOM للدعم العربي
   
 // إضافة معلومات التقرير والتاريخ
csvContent += `${getReportTitle()}\n`;
csvContent += `تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')}\n`;
const recordCount = activeTab === 'company' ? 'عدد المعاملات' : 'عدد السجلات';
csvContent += `${recordCount}: ${clientsData.length}\n\n`;
   
   // إضافة الملخص إذا كان مطلوباً
   if (exportOptions.includeSummary) {
       const exportSummary = calculateSummaryForData(dataToExport);
       csvContent += "الملخص المالي:\n";
       
       if (activeTab === 'delegates') {
           csvContent += `عدد المندوبين,${exportSummary.totalClients}\n`;
           csvContent += `إجمالي العملاء,${exportSummary.totalExpenses.toLocaleString()}\n`;
           csvContent += `العملاء المسافرين,${exportSummary.balance.toLocaleString()}\n`;
           csvContent += `إجمالي العمولات المدفوعة,${exportSummary.totalIncome.toLocaleString()} ج.م\n\n`;
      } else {
    if (activeTab === 'company') {
        csvContent += `عدد المعاملات,${exportSummary.totalClients}\n`;
        csvContent += `إجمالي الإيرادات,${exportSummary.totalIncome.toLocaleString()} ج.م\n`;
        csvContent += `إجمالي المصروفات,${exportSummary.totalExpenses.toLocaleString()} ج.م\n`;
        csvContent += `صافي الرصيد,${exportSummary.balance.toLocaleString()} ج.م\n\n`;
    } else {
        csvContent += `عدد السجلات,${exportSummary.totalClients}\n`;
        csvContent += `إجمالي الإيداعات,${exportSummary.totalIncome.toLocaleString()} ج.م\n`;
        csvContent += `إجمالي السحوبات,${exportSummary.totalExpenses.toLocaleString()} ج.م\n`;
        csvContent += `الرصيد,${exportSummary.balance.toLocaleString()} ج.م\n\n`;
    }
}
   }
   
   // إضافة رؤوس الجدول
   const headers = getTableHeaders().slice(1); // إزالة عمود الرقم
   csvContent += headers.join(',') + '\n';
   
   // إضافة البيانات
   switch (activeTab) {
       case 'clients':
           clientsData.forEach(client => {
               const row = [
                   `"${client.NameArabic || ''}"`,
                   `"${client.NationalID || ''}"`,
                   `"${client.CompanyName || ''}"`,
                   `"${client.DelegateName || ''}"`,
                   `"${client.PassportProfession || ''}"`,
                   `"${formatPhoneNumber(client.PhoneNumbers)}"`,
                   `"${client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : ''}"`,
                   `"${client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : ''}"`,
                   `"${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : ''}"`,
                   `"${(client.CurrentBalance || 0).toLocaleString()} ج.م"`,
                   `"${(client.RequiredPayment || 0).toLocaleString()} ج.م"`,
                   `"${client.IsOverpaid ? '+' : ''}${(client.RemainingDue || 0).toLocaleString()} ج.م"`,
                   `"${!client.RequiredPayment || client.RequiredPayment <= 0 ? 'يلزم تحديد المبلغ المطلوب' : client.IsOverpaid ? 'يمكن سحب مبلغ زائد' : client.RemainingDue > 0 ? 'متبقي عليه مستحقات' : 'مسدد بالكامل'}"`,
                   `"${client.LastActiveNote || ''}"`
               ];
               csvContent += row.join(',') + '\n';
           });
           break;
       
       case 'delegates':
           dataToExport.forEach(delegate => {
               const row = [
                   `"${delegate.DelegateName || ''}"`,
                   `"${delegate.PhoneNumber || ''}"`,
                   `"${delegate.TotalClients || '0'}"`,
                   `"${delegate.TraveledClients || '0'}"`,
                   `"${delegate.PaidCommissionClients || '0'}"`,
                   `"${delegate.UnpaidCommissionClients || '0'}"`,
                   `"${(delegate.TotalCommissionsPaid || 0).toLocaleString()} ج.م"`,
                   `"${delegate.TransactionsCount || '0'}"`,
                   `"${delegate.LastTransactionDate ? new Date(delegate.LastTransactionDate).toLocaleDateString('ar-EG') : ''}"`
               ];
               csvContent += row.join(',') + '\n';
           });
           break;
       
    case 'company':
    dataToExport.forEach(transaction => {
        const row = [
            `"${transaction.TransactionDate ? new Date(transaction.TransactionDate).toLocaleDateString('ar-EG') : ''}"`,
            `"${transaction.Type === 'income' ? 'إيراد' : 'مصروف'}"`,
            `"${transaction.CategoryName || ''}"`,
            `"${transaction.Description || ''}"`,
            `"${(transaction.Amount || 0).toLocaleString()} ج.م"`,
            `"${transaction.PaymentMethodName || ''}"`,
            `"${transaction.Notes || ''}"`,
            `"${transaction.CreatedBy || ''}"`
        ];
        csvContent += row.join(',') + '\n';
    });
    break;
       
       case 'treasury':
           dataToExport.forEach(transaction => {
               const row = [
                   `"${new Date(transaction.TransactionDate).toLocaleDateString('ar-EG')}"`,
                   `"${transaction.TransactionType || ''}"`,
                   `"${(transaction.Amount || 0).toLocaleString()} ج.م"`,
                   `"${(transaction.Balance || 0).toLocaleString()} ج.م"`,
                   `"${transaction.ClientName || ''}"`,
                   `"${transaction.Notes || ''}"`,
                   `"${transaction.CreatedBy || ''}"`
               ];
               csvContent += row.join(',') + '\n';
           });
           break;



           case 'comprehensive':
    dataToExport.forEach(transaction => {
        const row = [
            `"${transaction.typeName || ''}"`,
            `"${transaction.name || ''}"`,
            `"${transaction.company || ''}"`,
            `"${transaction.paymentMethod || 'غير محدد'}"`,
            `"${(transaction.amount || 0) >= 0 ? '+' : ''}${(transaction.amount || 0).toLocaleString()} ج.م"`,
            `"${transaction.date ? new Date(transaction.date).toLocaleDateString('ar-EG') : ''}"`,
            `"${transaction.notes || 'لا توجد ملاحظات'}"`
        ];
        csvContent += row.join(',') + '\n';
    });
    break;

   }
   
   // تحميل الملف
   const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
   const link = document.createElement('a');
   const url = URL.createObjectURL(blob);
   link.setAttribute('href', url);
   const fileName = activeTab === 'company' ? 
    `تقرير_ايرادات_ومصروفات_الشركة_${new Date().toISOString().split('T')[0]}.csv` :
    `${getReportTitle().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
link.setAttribute('download', fileName);   link.style.visibility = 'hidden';
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(url);
   
   showToast('تم تصدير ملف CSV بنجاح');
};

const exportToPDF = () => {
    // استخدام البيانات المعروضة في الصفحة الحالية فقط
    const allSourceData = activeTab === 'clients' && clientDues.length > 0 ? clientDues : reportData;
    const dataToExport = getCurrentPageData(allSourceData);
    const exportSummary = calculateSummaryForData(dataToExport);
    
    // التأكد من وجود بيانات للتصدير
    if (!dataToExport || dataToExport.length === 0) {
        showToast('لا توجد بيانات لتصدير PDF في الصفحة الحالية', 'error');
        return;
    }
    
    console.log('تصدير PDF للبيانات المعروضة:', dataToExport.length, 'سجل من الصفحة', pagination.currentPage);
    
    // إنشاء نافذة جديدة للطباعة
    const printWindow = window.open('', '_blank');
    
    // تحديد محتوى الملخص حسب نوع التقرير مع الحفاظ على الألوان
    let summarySection = '';
    if (activeTab === 'delegates') {
        summarySection = `
            <div class="summary-grid">
                <div class="summary-item">
                    <div class="summary-label">عدد المندوبين المعروضين</div>
                    <div class="summary-value count">${exportSummary.totalClients}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-label">إجمالي العملاء</div>
                    <div class="summary-value balance">${exportSummary.totalExpenses.toLocaleString()}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-label">العملاء المسافرين</div>
                    <div class="summary-value income">${exportSummary.balance.toLocaleString()}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-label">إجمالي العمولات المدفوعة</div>
                    <div class="summary-value income">${exportSummary.totalIncome.toLocaleString()} ج.م</div>
                </div>
            </div>
        `;
    } else {
        if (activeTab === 'company') {
            summarySection = `
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">عدد المعاملات المعروضة</div>
                        <div class="summary-value count">${exportSummary.totalClients}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي الإيرادات</div>
                        <div class="summary-value income">${exportSummary.totalIncome.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي المصروفات</div>
                        <div class="summary-value expense">${exportSummary.totalExpenses.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">صافي الرصيد</div>
                        <div class="summary-value balance">${exportSummary.balance.toLocaleString()} ج.م</div>
                    </div>
                </div>
            `;
        } else {
            summarySection = `
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">عدد السجلات المعروضة</div>
                        <div class="summary-value count">${exportSummary.totalClients}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي الإيداعات</div>
                        <div class="summary-value income">${exportSummary.totalIncome.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي السحوبات</div>
                        <div class="summary-value expense">${exportSummary.totalExpenses.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">الرصيد</div>
                        <div class="summary-value balance">${exportSummary.balance.toLocaleString()} ج.م</div>
                    </div>
                </div>
            `;
        }
    }
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>${getReportTitle()} - الصفحة ${pagination.currentPage}</title>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    direction: rtl;
                    background: white;
                    padding: 20px;
                    font-size: 12px;
                }
                .header {
                    text-align: center;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #3b82f6;
                    padding-bottom: 20px;
                }
                .company-name { 
                    font-size: 28px; 
                    font-weight: bold; 
                    color: #1e40af;
                    margin-bottom: 10px;
                }
                .report-title { 
                    font-size: 22px; 
                    color: #374151;
                    margin-bottom: 10px;
                }
                .report-date { 
                    color: #6b7280; 
                    font-size: 16px;
                }
                
                .page-info {
                    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                    border: 2px solid #0288d1;
                    border-radius: 10px;
                    padding: 15px;
                    margin: 20px 0;
                    text-align: center;
                    font-weight: bold;
                    font-size: 14px;
                    color: #01579b;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .summary-section {
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 30px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }
                .summary-title {
                    font-size: 18px;
                    font-weight: bold;
                    color: #1e40af;
                    margin-bottom: 15px;
                    text-align: center;
                    padding-bottom: 10px;
                    border-bottom: 2px solid #3b82f6;
                }
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                    text-align: center;
                }
                .summary-item {
                    background: white;
                    padding: 15px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                    transition: transform 0.2s;
                }
                .summary-item:hover {
                    transform: translateY(-2px);
                }
                .summary-label {
                    font-weight: bold;
                    color: #374151;
                    margin-bottom: 8px;
                    font-size: 13px;
                }
                .summary-value {
                    font-size: 18px;
                    font-weight: bold;
                }
                .income { color: #10b981; }
                .expense { color: #ef4444; }
                .balance { color: #3b82f6; }
                .count { color: #8b5cf6; }
                
                .table-container {
                    margin-top: 20px;
                    overflow-x: auto;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    border-radius: 8px;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    font-size: 10px;
                }
                th, td { 
                    padding: 8px 4px; 
                    text-align: center;
                    border: 1px solid #e2e8f0;
                }
                th { 
                    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                    font-weight: bold;
                    color: #334155;
                    font-size: 11px;
                }
                tr:nth-child(even) { 
                    background: #f8fafc; 
                }
                tr:hover { 
                    background: #f1f5f9; 
                }
                
                .positive { 
                    color: #10b981; 
                    font-weight: bold; 
                }
                .negative { 
                    color: #ef4444; 
                    font-weight: bold; 
                }
                .warning { 
                    color: #f59e0b; 
                    font-weight: bold; 
                }
                
                .footer {
                    margin-top: 30px;
                    text-align: center;
                    color: #6b7280;
                    font-size: 12px;
                    border-top: 2px solid #e2e8f0;
                    padding-top: 15px;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    border-radius: 8px;
                    padding: 20px;
                }
                
                @media print {
                    body { padding: 10px; font-size: 10px; }
                    .header { margin-bottom: 20px; }
                    .summary-section { margin-bottom: 20px; }
                    th, td { padding: 6px 3px; font-size: 9px; }
                    .page-info { font-size: 12px; }
                    @page { size: A4 landscape; margin: 10mm; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div class="company-name">🏢 النظام المالي</div>
                <div class="report-title">${getReportTitle()}</div>
                <div class="report-date">
                    📅 تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}
                </div>
            </div>
            
            <div class="page-info">
                📄 البيانات المعروضة: الصفحة ${pagination.currentPage} من ${pagination.totalPages}
                <br>
                📊 عدد السجلات المصدرة: ${dataToExport.length} من إجمالي ${pagination.totalItems} سجل
            </div>

            <div class="summary-section">
                <div class="summary-title">📈 الملخص المالي للبيانات المعروضة في الصفحة الحالية</div>
                ${summarySection}
            </div>

            <div class="table-container">
                ${createPDFTable(dataToExport)}
            </div>

            <div class="footer">
                🏢 تم إنشاء هذا التقرير بواسطة نظام التقارير المالية | 
                📅 ${new Date().toLocaleString('ar-EG')}
                <br>
                <strong>⚠️ تنبيه:</strong> هذا التقرير يعرض البيانات المعروضة في الصفحة ${pagination.currentPage} فقط
                <br>
                <small>💡 للحصول على تقرير شامل، استخدم خيار "جميع البيانات" من قائمة التصدير</small>
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // انتظار تحميل المحتوى ثم الطباعة
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
    
    showToast(`✅ تم إنشاء ملف PDF للصفحة ${pagination.currentPage} (${dataToExport.length} سجل) للطباعة`);
};

// إصلاح دالة إنشاء جدول PDF
const createPDFTable = (data) => {
    const headers = getTableHeaders();
    
    // استخدام البيانات الممررة فقط وليس أي متغير آخر
    switch (activeTab) {
        case 'delegates':
            return `
                <table>
                    <thead>
                        <tr>
                            ${headers.map(header => `<th>${header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map((delegate, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${delegate.DelegateName || '-'}</td>
                                <td>${delegate.PhoneNumber || '-'}</td>
                                <td class="positive">${delegate.TotalClients || '0'}</td>
                                <td class="positive">${delegate.TraveledClients || '0'}</td>
                                <td class="positive">${delegate.PaidCommissionClients || '0'}</td>
                                <td class="negative">${delegate.UnpaidCommissionClients || '0'}</td>
                                <td class="positive">${(delegate.TotalCommissionsPaid || 0).toLocaleString()} ج.م</td>
                                <td>${delegate.TransactionsCount || '0'}</td>
                                <td>${delegate.LastTransactionDate ? new Date(delegate.LastTransactionDate).toLocaleDateString('ar-EG') : '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        
        case 'clients':
            return `
                <table>
                    <thead>
                        <tr>
                            ${headers.map(header => `<th>${header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map((client, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${client.NameArabic || '-'}</td>
                                <td>${client.NationalID || '-'}</td>
                                <td>${client.CompanyDisplayName || client.CompanyName || '-'}</td>
                                <td>${client.DelegateName || '-'}</td>
                                <td>${client.PassportProfession || '-'}</td>
                                <td>${formatPhoneNumber(client.PhoneNumbers)}</td>
                                <td>${client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-'}</td>
                                <td>${client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-'}</td>
                                <td>${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-'}</td>
                                <td class="${client.CurrentBalance >= 0 ? 'positive' : 'negative'}">${(client.CurrentBalance || 0).toLocaleString()} ج.م</td>
                                <td>${(client.RequiredPayment || 0).toLocaleString()} ج.م</td>
                                <td class="${client.IsOverpaid ? 'positive' : (client.RemainingDue > 0 ? 'negative' : 'positive')}">${client.IsOverpaid ? '+' : ''}${(client.RemainingDue || 0).toLocaleString()} ج.م</td>
                                <td class="${!client.RequiredPayment || client.RequiredPayment <= 0 ? 'warning' : client.IsOverpaid ? 'positive' : (client.RemainingDue > 0 ? 'negative' : 'positive')}">${!client.RequiredPayment || client.RequiredPayment <= 0 ? 'يلزم تحديد المبلغ المطلوب' : client.IsOverpaid ? 'يمكن سحب مبلغ زائد' : client.RemainingDue > 0 ? 'متبقي عليه مستحقات' : 'مسدد بالكامل'}</td>
                                <td>${client.LastActiveNote || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        
        case 'company':
            return `
                <table>
                    <thead>
                        <tr>
                            ${headers.map(header => `<th>${header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map((transaction, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${transaction.TransactionDate ? new Date(transaction.TransactionDate).toLocaleDateString('ar-EG') : '-'}</td>
                                <td class="${transaction.Type === 'income' ? 'positive' : 'negative'}">${transaction.Type === 'income' ? 'إيراد' : 'مصروف'}</td>
                                <td>${transaction.CategoryName || '-'}</td>
                                <td>${transaction.Description || '-'}</td>
                                <td class="${transaction.Type === 'income' ? 'positive' : 'negative'}">${(transaction.Amount || 0).toLocaleString()} ج.م</td>
                                <td>${transaction.PaymentMethodName || '-'}</td>
                                <td>${transaction.Notes || '-'}</td>
                                <td>${transaction.CreatedBy || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        
        case 'treasury':
            return `
                <table>
                    <thead>
                        <tr>
                            ${headers.map(header => `<th>${header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map((transaction, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${new Date(transaction.TransactionDate).toLocaleDateString('ar-EG')}</td>
                                <td>${transaction.TransactionType || '-'}</td>
                                <td class="${transaction.TransactionType === 'إيداع' ? 'positive' : 'negative'}">${(transaction.Amount || 0).toLocaleString()} ج.م</td>
                                <td>${(transaction.Balance || 0).toLocaleString()} ج.م</td>
                                <td>${transaction.ClientName || '-'}</td>
                                <td>${transaction.Notes || '-'}</td>
                                <td>${transaction.CreatedBy || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;

        case 'comprehensive':
            return `
                <table>
                    <thead>
                        <tr>
                            ${headers.map(header => `<th>${header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map((transaction, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td class="${(transaction.amount || 0) >= 0 ? 'positive' : 'negative'}">${transaction.typeName || '-'}</td>
                                <td>${transaction.name || '-'}</td>
                                <td>${transaction.company || '-'}</td>
                                <td>${transaction.paymentMethod || 'غير محدد'}</td>
                                <td class="${(transaction.amount || 0) >= 0 ? 'positive' : 'negative'}">
                                    ${(transaction.amount || 0) >= 0 ? '+' : ''}${(transaction.amount || 0).toLocaleString()} ج.م
                                </td>
                                <td>${transaction.date ? new Date(transaction.date).toLocaleDateString('ar-EG') : '-'}</td>
                                <td>${transaction.notes || 'لا توجد ملاحظات'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        
        case 'debts':
            return `
                <table>
                    <thead><tr>${headers.map(header => `<th>${header}</th>`).join('')}</tr></thead>
                    <tbody>
                        ${data.map((debt, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${debt.DebtNumber || '-'}</td>
                                <td>${debt.NameArabic || '-'}</td>
                                <td>${debt.MainPhone || '-'}</td>
                                <td>${debt.NationalID || '-'}</td>
                                <td>${debt.OfficeSerial || debt.PassportID || '-'}</td>
                                <td>${debt.TripIsActive ? 'نشطة' : 'مؤرشفة'}</td>
                                <td>${debt.CompanyName || '-'}</td>
                                <td>${debt.DelegateName || '-'}</td>
                                <td>${Number(debt.OriginalAmount || 0).toLocaleString()} ج.م</td>
                                <td class="positive">${Number(debt.PaidAmount || 0).toLocaleString()} ج.م</td>
                                <td class="warning">${Number(debt.AdministrativeSettledAmount || 0).toLocaleString()} ج.م</td>
                                <td class="negative">${Number(debt.OutstandingAmount || 0).toLocaleString()} ج.م</td>
                                <td>${debt.Status || '-'}</td>
                                <td>${debt.DueType || '-'}</td>
                                <td>${debt.DueDate ? new Date(debt.DueDate).toLocaleDateString('ar-EG') : '-'}</td>
                                <td>${debt.Reason || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;

        default:
            return '<p>لا توجد بيانات للعرض</p>';
    }
};

// تنفيذ التصدير حسب النوع المختار
const executeExport = async () => {
    if (exportOptions.format === 'excel') {
        await exportToExcelAdvanced();
    } else if (exportOptions.format === 'csv') {
        await exportToCSV();
    } else if (exportOptions.format === 'pdf') {
        exportToPDF();
    }
    setIsExportModalOpen(false);
};

const printReport = () => {
    // استخدام البيانات المعروضة في الصفحة الحالية فقط
    const allSourceData = activeTab === 'clients' && clientDues.length > 0 ? clientDues : reportData;
    const dataToExport = getCurrentPageData(allSourceData); // البيانات المعروضة فقط
    const printSummary = calculateSummaryForData(dataToExport);
    
    // التأكد من وجود بيانات للطباعة
    if (!dataToExport || dataToExport.length === 0) {
        showToast('لا توجد بيانات للطباعة في الصفحة الحالية', 'error');
        return;
    }
    
    console.log('طباعة البيانات المعروضة:', dataToExport.length, 'سجل من الصفحة', pagination.currentPage);
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        showToast('اسمح بالنوافذ المنبثقة لاستخدام الطباعة', 'error');
        return;
    }
    
    let summarySection = '';
    if (activeTab === 'delegates') {
        summarySection = `
            <div class="summary-grid">
                <div class="summary-item">
                    <div class="summary-label">عدد المندوبين المعروضين</div>
                    <div class="summary-value">${printSummary.totalClients}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-label">إجمالي العملاء</div>
                    <div class="summary-value">${printSummary.totalExpenses.toLocaleString()}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-label">العملاء المسافرين</div>
                    <div class="summary-value">${printSummary.balance.toLocaleString()}</div>
                </div>
                <div class="summary-item">
                    <div class="summary-label">إجمالي العمولات المدفوعة</div>
                    <div class="summary-value">${printSummary.totalIncome.toLocaleString()} ج.م</div>
                </div>
            </div>
        `;
    } else {
        if (activeTab === 'debts') {
            summarySection = `
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">عدد المديونيات المعروضة</div>
                        <div class="summary-value">${printSummary.totalClients}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">المدفوع فعليًا</div>
                        <div class="summary-value">${printSummary.totalIncome.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">التسويات الإدارية</div>
                        <div class="summary-value">${printSummary.totalExpenses.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي المتبقي</div>
                        <div class="summary-value">${printSummary.balance.toLocaleString()} ج.م</div>
                    </div>
                </div>
            `;
        } else if (activeTab === 'company') {
            summarySection = `
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">عدد المعاملات المعروضة</div>
                        <div class="summary-value">${printSummary.totalClients}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي الإيرادات</div>
                        <div class="summary-value">${printSummary.totalIncome.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي المصروفات</div>
                        <div class="summary-value">${printSummary.totalExpenses.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">صافي الرصيد</div>
                        <div class="summary-value">${printSummary.balance.toLocaleString()} ج.م</div>
                    </div>
                </div>
            `;
        } else {
            summarySection = `
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">عدد السجلات المعروضة</div>
                        <div class="summary-value">${printSummary.totalClients}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي الإيداعات</div>
                        <div class="summary-value">${printSummary.totalIncome.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي السحوبات</div>
                        <div class="summary-value">${printSummary.totalExpenses.toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">الرصيد</div>
                        <div class="summary-value">${printSummary.balance.toLocaleString()} ج.م</div>
                    </div>
                </div>
            `;
        }
    }
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>${getReportTitle()} - الصفحة ${pagination.currentPage}</title>
            <style>
                @page {
                    size: A4 landscape;
                    margin: 8mm 5mm;
                }
                
                * { 
                    margin: 0; 
                    padding: 0; 
                    box-sizing: border-box; 
                }
                
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    direction: rtl;
                    background: white;
                    color: black;
                    font-size: 16px;
                    line-height: 1.4;
                }
                
                .print-container {
                    max-width: 100%;
                    margin: 0;
                    background: white;
                    padding: 8px;
                }
                
                .report-header {
                    text-align: center;
                    margin-bottom: 20px;
                    border-bottom: 3px solid black;
                    padding-bottom: 15px;
                }
                
                .company-name {
                    font-size: 32px;
                    font-weight: bold;
                    color: black;
                    margin-bottom: 8px;
                }
                
                .report-title {
                    font-size: 24px;
                    color: black;
                    margin-bottom: 8px;
                }
                
                .report-date {
                    font-size: 18px;
                    color: black;
                }
                
                .page-info {
                    background: #e3f2fd;
                    border: 2px solid #1976d2;
                    padding: 12px;
                    margin: 15px 0;
                    border-radius: 8px;
                    font-weight: bold;
                    text-align: center;
                    color: #0d47a1;
                    font-size: 16px;
                }
                
                .summary-section {
                    background: white;
                    border: 2px solid black;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                
                .summary-title {
                    font-size: 24px;
                    font-weight: bold;
                    color: black;
                    margin-bottom: 15px;
                    text-align: center;
                    border-bottom: 1px solid black;
                    padding-bottom: 8px;
                }
                
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                    text-align: center;
                }
                
                .summary-item {
                    background: #f8f9fa;
                    padding: 12px;
                    border: 1px solid black;
                    border-radius: 6px;
                }
                
                .summary-label {
                    font-weight: bold;
                    color: black;
                    margin-bottom: 6px;
                    font-size: 14px;
                }
                
                .summary-value {
                    font-size: 20px;
                    font-weight: bold;
                    color: #1976d2;
                }
                
                .table-container {
                    margin-top: 15px;
                }
                
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 2px solid black;
                }
                
                th, td { 
                    padding: 8px 4px;
                    text-align: center;
                    border: 1px solid black;
                    font-size: 12px;
                    color: black;
                }
                
                th { 
                    background: #e0e0e0;
                    font-weight: bold;
                    color: black;
                    font-size: 13px;
                }
                
                tr:nth-child(even) { 
                    background: #f5f5f5; 
                }
                
                tr:nth-child(odd) { 
                    background: white; 
                }
                
                .positive { 
                    color: #2e7d32 !important; 
                    font-weight: bold; 
                }
                
                .negative { 
                    color: #d32f2f !important; 
                    font-weight: bold; 
                }
                
                .warning { 
                    color: #f57c00 !important; 
                    font-weight: bold; 
                }
                
                .footer {
                    margin-top: 20px;
                    text-align: center;
                    color: #666;
                    font-size: 12px;
                    border-top: 2px solid black;
                    padding-top: 10px;
                }
                
                @media print {
                    body { 
                        font-size: 11px;
                    }
                    
                    .print-container {
                        padding: 5px;
                    }
                    
                    th, td { 
                        padding: 6px 3px; 
                        font-size: 10px;
                    }
                    
                    th {
                        font-size: 11px;
                    }
                    
                    .page-info {
                        font-size: 13px;
                    }
                    
                    .summary-value {
                        font-size: 16px;
                    }
                    
                    table {
                        page-break-inside: avoid;
                    }
                    
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    
                    @page {
                        margin: 8mm 3mm;
                    }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <div class="report-header">
                    <div class="company-name">النظام المالي</div>
                    <div class="report-title">${getReportTitle()}</div>
                    <div class="report-date">
                        تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'long'
                        })}
                    </div>
                </div>
                
                <div class="page-info">
                    📄 البيانات المعروضة: الصفحة ${pagination.currentPage} من ${pagination.totalPages}
                    <br>
                    📊 عدد السجلات في هذه الصفحة: ${dataToExport.length} من إجمالي ${pagination.totalItems} سجل
                </div>

                <div class="summary-section">
                    <div class="summary-title">📈 الملخص المالي للبيانات المعروضة في الصفحة الحالية</div>
                    ${summarySection}
                </div>

                <div class="table-container">
                    ${createPDFTable(dataToExport)}
                </div>

                <div class="footer">
                    🏢 تم إنشاء هذا التقرير بواسطة نظام التقارير المالية | 
                    📅 ${new Date().toLocaleString('ar-EG')}
                    <br>
                    <strong>⚠️ تنبيه:</strong> هذا التقرير يعرض البيانات المعروضة في الصفحة ${pagination.currentPage} فقط
                </div>
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    const triggerPrint = () => {
        setTimeout(() => {
            try {
                printWindow.focus();
                printWindow.print();
            } finally {
                setTimeout(() => printWindow.close(), 500);
            }
        }, 700);
    };
    if (printWindow.document.readyState === 'complete') triggerPrint();
    else printWindow.onload = triggerPrint;
    
    showToast(`✅ تم إعداد طباعة الصفحة ${pagination.currentPage} (${dataToExport.length} سجل) بنجاح`);
};





// تحديث دالة تصدير PDF للتقرير الشامل
const exportDelegateDetailsToPDF = () => {
    if (!selectedDelegateDetails) return;
    
    const printWindow = window.open('', '_blank');
    const isCommissionsTab = activeDetailsTab === 'commissions';
    
    let sectionContent = '';
    let sectionTitle = '';
    
    if (isCommissionsTab) {
        sectionTitle = 'تفاصيل العمولات المدفوعة';
        sectionContent = createCommissionsPrintContent();
    } else {
        sectionTitle = 'عملاء المندوب';
        sectionContent = createClientsPrintContent();
    }
    
    // نفس محتوى الطباعة مع تحسينات للـ PDF
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>${sectionTitle} - ${selectedDelegateDetails.delegateName}</title>
            <style>
                @page {
                    size: A4;
                    margin: 10mm;
                }
                
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    direction: rtl;
                    background: white;
                    padding: 15px;
                    font-size: 13px;
                    color: #000;
                }
                .header {
                    text-align: center;
                    margin-bottom: 25px;
                    border-bottom: 2px solid #3b82f6;
                    padding-bottom: 15px;
                }
                .delegate-name { 
                    font-size: 24px; 
                    font-weight: bold; 
                    color: #1e40af;
                    margin-bottom: 8px;
                }
                .section-title {
                    font-size: 20px;
                    color: #374151;
                    margin-bottom: 8px;
                }
                .summary-section {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 15px;
                    text-align: center;
                }
                .summary-item {
                    background: white;
                    padding: 12px;
                    border-radius: 6px;
                    border: 1px solid #e2e8f0;
                }
                .summary-label {
                    font-weight: bold;
                    color: #374151;
                    margin-bottom: 5px;
                    font-size: 12px;
                }
                .summary-value {
                    font-size: 16px;
                    font-weight: bold;
                    color: #1e40af;
                }
                .commission-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 15px;
                    page-break-inside: avoid;
                }
                .commission-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #f3f4f6;
                }
                .commission-amount {
                    font-size: 18px;
                    font-weight: bold;
                    color: #10b981;
                }
                .payment-method {
                    background: #ddd6fe;
                    padding: 8px;
                    border-radius: 6px;
                    margin-bottom: 10px;
                }
                .clients-section {
                    background: #f9fafb;
                    padding: 10px;
                    border-radius: 6px;
                    margin-bottom: 10px;
                }
                .client-item {
                    padding: 5px;
                    border-bottom: 1px solid #e5e7eb;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 15px;
                    font-size: 11px;
                }
                th, td { 
                    padding: 6px 4px; 
                    text-align: center;
                    border: 1px solid #e2e8f0;
                }
                th { 
                    background: #f1f5f9;
                    font-weight: bold;
                    color: #334155;
                }
                tr:nth-child(even) { background: #f8fafc; }
            </style>
        </head>
        <body>
            <div class="header">
                <div class="delegate-name">${sectionTitle}: ${selectedDelegateDetails.delegateName}</div>
                <div class="section-title">
                    تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')}
                </div>
            </div>

            ${createSummarySection(isCommissionsTab)}

            ${sectionContent}

            <div style="margin-top: 20px; text-align: center; color: #6b7280; font-size: 11px; border-top: 1px solid #e2e8f0; padding-top: 10px;">
                تم إنشاء هذا التقرير بواسطة نظام التقارير المالية | ${new Date().toLocaleString('ar-EG')}
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
    
    showToast(`تم إنشاء PDF لـ ${sectionTitle} بنجاح`);
};


// تحديث دالة تصدير Excel
// تحديث دالة تصدير Excel - العودة للتنسيق الأصلي مع إضافة الملاحظات
// تحديث دالة تصدير Excel - خلية العملاء مربعة ومتوازنة
const exportDelegateDetailsToExcel = () => {
    if (!selectedDelegateDetails) return;
    
    const isCommissionsTab = activeDetailsTab === 'commissions';
    let sectionTitle = isCommissionsTab ? 'تفاصيل العمولات المدفوعة' : 'عملاء المندوب';
    
    let tableContent = '';
    
    if (isCommissionsTab) {
        // تحليل أسماء العملاء للعرض في Excel
        const parseClientsForExcel = (clientsData) => {
            if (!clientsData || clientsData === '-') return 'لا يوجد عملاء';
            
            const clients = clientsData.split(',').map(item => {
                const trimmed = item.trim();
                const parts = trimmed.split('(');
                const clientName = parts[0]?.trim() || trimmed;
                const companyName = parts[1]?.replace(')', '').trim() || 'غير محدد';
                return `${clientName} (${companyName})`;
            });
            
            return clients.join('\n');
        };
        
        tableContent = `
            <tr><td colspan="7" class="section-header">${sectionTitle} (${selectedDelegateDetails.commissionDetails?.length || 0} معاملة)</td></tr>
            <tr>
                <td class="table-header">م</td>
                <td class="table-header">تاريخ الدفع</td>
                <td class="table-header">مبلغ العمولة</td>
                <td class="table-header">عدد العملاء</td>
                <td class="table-header">أسماء العملاء والشركات</td>
                <td class="table-header">طريقة الدفع</td>
                <td class="table-header">الملاحظات</td>
            </tr>
            ${selectedDelegateDetails.commissionDetails?.map((commission, index) => `
                <tr>
                    <td>${index + 1}</td>
                    <td>${new Date(commission.CommissionDate).toLocaleDateString('ar-EG')}</td>
                    <td class="commission">${(commission.CommissionAmount || 0).toLocaleString()} ج.م</td>
                    <td>${commission.ClientsCount || 0}</td>
                    <td class="clients-cell">${parseClientsForExcel(commission.ClientsNames)}</td>
                    <td class="positive">${commission.CompanyName || 'الخزينة الرئيسية'}</td>
                    <td class="notes-cell">${commission.CommissionNotes || 'لا توجد ملاحظات'}</td>
                </tr>
            `).join('') || '<tr><td colspan="7">لا توجد عمولات مدفوعة</td></tr>'}
        `;
    } else {
        // محتوى العملاء
        tableContent = `
            <tr><td colspan="12" class="section-header">${sectionTitle} (${selectedDelegateDetails.clients?.length || 0} عميل)</td></tr>
            <tr>
                <td class="table-header">م</td>
                <td class="table-header">اسم العميل</td>
                <td class="table-header">الرقم القومي</td>
                <td class="table-header">رقم الجواز</td>
                <td class="table-header">الشركة</td>
                <td class="table-header">مهنة السفر</td>
                <td class="table-header">تاريخ النت</td>
                <td class="table-header">تاريخ التأشير</td>
                <td class="table-header">تاريخ السفر</td>
                <td class="table-header">حالة العمولة</td>
                <td class="table-header">الرصيد</td>
                <td class="table-header">الحالة</td>
            </tr>
            ${selectedDelegateDetails.clients?.map((client, index) => `
                <tr>
                    <td>${index + 1}</td>
                    <td>"${client.NameArabic || ''}"</td>
                    <td>"${client.NationalID || ''}"</td>
                    <td>"${client.PassportNumber || ''}"</td>
                    <td>"${client.CompanyDisplayName || client.CompanyName || ''}"</td>
                    <td>"${client.ProfessionName || ''}"</td>
                    <td>"${client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : ''}"</td>
                    <td>"${client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : ''}"</td>
                    <td>"${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : ''}"</td>
                    <td>"${client.CommissionStatus || ''}"</td>
                    <td>"${(client.CurrentBalance || 0).toLocaleString()} ج.م"</td>
                    <td>"${client.ClientStatus || ''}"</td>
                </tr>
            `).join('') || '<tr><td colspan="12">لا يوجد عملاء مسجلين</td></tr>'}
        `;
    }
    
    const htmlContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head>
            <meta charset="UTF-8">
            <style>
                table { border-collapse: collapse; width: 100%; direction: rtl; }
                td { border: 1px solid #000000; padding: 8px; text-align: center; vertical-align: top; }
                .header-main { background-color: #ADD8E6; font-size: 18pt; font-weight: bold; }
                .section-header { background-color: #D3D3D3; font-size: 14pt; font-weight: bold; }
                .table-header { background-color: #F0F0F0; font-weight: bold; }
                .positive { color: #0F7B0F; font-weight: bold; }
                .commission { color: #8B5CF6; font-weight: bold; }
                .clients-cell { 
                    text-align: right; 
                    white-space: pre-line; 
                    word-wrap: break-word;
                    width: auto;
                    height: auto;
                }
                .notes-cell {
                    text-align: right; 
                    white-space: pre-line; 
                    word-wrap: break-word;
                }
            </style>
        </head>
        <body>
            <table>
                <tr>
                    <td colspan="${isCommissionsTab ? '7' : '12'}" class="header-main">${sectionTitle} للمندوب: ${selectedDelegateDetails.delegateName} - تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')}</td>
                </tr>
                
                <!-- الملخص الإحصائي -->
                <tr><td colspan="${isCommissionsTab ? '7' : '12'}" class="section-header">الملخص الإحصائي</td></tr>
                ${isCommissionsTab ? `
                <tr>
                    <td class="table-header">عدد العمولات</td>
                    <td class="table-header">إجمالي مبلغ العمولات</td>
                    <td class="table-header">عدد العملاء المرتبطين</td>
                    <td colspan="4"></td>
                </tr>
                <tr>
                    <td>${selectedDelegateDetails.commissionDetails?.length || 0}</td>
                    <td class="commission">${(selectedDelegateDetails.summary?.totalCommissionsPaid || 0).toLocaleString()} ج.م</td>
                    <td>${selectedDelegateDetails.summary?.clientsWithCommission || 0}</td>
                    <td colspan="4"></td>
                </tr>
                ` : `
                <tr>
                    <td class="table-header">إجمالي العملاء</td>
                    <td class="table-header">العملاء المسافرين</td>
                    <td class="table-header">الرصيد الإجمالي</td>
                    <td colspan="9"></td>
                </tr>
                <tr>
                    <td>${selectedDelegateDetails.summary?.totalClients || 0}</td>
                    <td>${selectedDelegateDetails.summary?.traveledClients || 0}</td>
                    <td class="positive">${(selectedDelegateDetails.summary?.currentBalance || 0).toLocaleString()} ج.م</td>
                    <td colspan="9"></td>
                </tr>
                `}
                
                <!-- فاصل -->
                <tr><td colspan="${isCommissionsTab ? '7' : '12'}"></td></tr>
                
                <!-- المحتوى الرئيسي -->
                ${tableContent}
            </table>
        </body>
        </html>
    `;
    
    const blob = new Blob([htmlContent], {
        type: 'application/vnd.ms-excel;charset=utf-8'
    });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${sectionTitle}_${selectedDelegateDetails.delegateName}_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showToast(`تم تصدير ${sectionTitle} بتنسيق Excel بنجاح`);
};


// دالة طباعة منفصلة للتقرير الشامل
const printDelegateDetails = () => {
    if (!selectedDelegateDetails) return;
    
    const printWindow = window.open('', '_blank');
    const isCommissionsTab = activeDetailsTab === 'commissions';
    
    // تحديد المحتوى حسب التبويب النشط
    let sectionContent = '';
    let sectionTitle = '';
    
    if (isCommissionsTab) {
        sectionTitle = 'تفاصيل العمولات المدفوعة';
        sectionContent = createCommissionsPrintContent();
    } else {
        sectionTitle = 'عملاء المندوب';
        sectionContent = createClientsPrintContent();
    }
    
    const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8">
            <title>${sectionTitle} - ${selectedDelegateDetails.delegateName}</title>
            <style>
                @page {
                    size: A4;
                    margin: 15mm;
                }
                
                * { 
                    margin: 0; 
                    padding: 0; 
                    box-sizing: border-box; 
                }
                
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    direction: rtl;
                    background: white;
                    color: black;
                    font-size: 14px;
                    line-height: 1.5;
                }
                
                .print-container {
                    max-width: 100%;
                    margin: 0;
                    background: white;
                    padding: 10px;
                }
                
                .report-header {
                    text-align: center;
                    margin-bottom: 25px;
                    border-bottom: 3px solid #000;
                    padding-bottom: 15px;
                }
                
                .company-name {
                    font-size: 28px;
                    font-weight: bold;
                    color: #000;
                    margin-bottom: 8px;
                }
                
                .report-title {
                    font-size: 22px;
                    color: #000;
                    margin-bottom: 8px;
                }
                
                .delegate-info {
                    font-size: 16px;
                    color: #000;
                    margin-bottom: 5px;
                }
                
                .summary-section {
                    background: white;
                    border: 2px solid #000;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 20px;
                }
                
                .summary-title {
                    font-size: 20px;
                    font-weight: bold;
                    color: #000;
                    margin-bottom: 15px;
                    text-align: center;
                    border-bottom: 1px solid #000;
                    padding-bottom: 8px;
                }
                
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 15px;
                    text-align: center;
                    margin-bottom: 15px;
                }
                
                .summary-item {
                    background: white;
                    padding: 12px;
                    border: 1px solid #000;
                    border-radius: 6px;
                }
                
                .summary-label {
                    font-weight: bold;
                    color: #000;
                    margin-bottom: 6px;
                    font-size: 14px;
                }
                
                .summary-value {
                    font-size: 18px;
                    font-weight: bold;
                    color: #000;
                }
                
                .section-content {
                    margin-top: 20px;
                }
                
                .commission-card {
                    background: white;
                    border: 2px solid #000;
                    border-radius: 8px;
                    padding: 15px;
                    margin-bottom: 15px;
                    page-break-inside: avoid;
                }
                
                .commission-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #000;
                }
                
                .commission-title {
                    font-size: 18px;
                    font-weight: bold;
                    color: #000;
                }
                
                .commission-amount {
                    font-size: 20px;
                    font-weight: bold;
                    color: #000;
                    border: 1px solid #000;
                    padding: 5px 10px;
                    border-radius: 5px;
                }
                
                .commission-date {
                    color: #333;
                    font-size: 14px;
                    margin-top: 5px;
                }
                
                .payment-method {
                    background: #f5f5f5;
                    border: 1px solid #000;
                    padding: 8px;
                    border-radius: 6px;
                    margin-bottom: 10px;
                }
                
                .clients-section {
                    background: #f9f9f9;
                    border: 1px solid #000;
                    padding: 10px;
                    border-radius: 6px;
                    margin-bottom: 10px;
                }
                
                .client-item {
                    padding: 5px;
                    border-bottom: 1px solid #ccc;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .client-name {
                    font-weight: bold;
                    color: #000;
                }
                
                .client-company {
                    font-size: 12px;
                    color: #333;
                    background: #e5e5e5;
                    padding: 2px 6px;
                    border-radius: 4px;
                }
                
                table { 
                    width: 100%; 
                    border-collapse: collapse;
                    background: white;
                    border: 2px solid #000;
                    margin-bottom: 15px;
                    font-size: 12px;
                }
                
                th, td { 
                    padding: 8px 4px;
                    text-align: center;
                    border: 1px solid #000;
                    color: #000;
                }
                
                th { 
                    background: #f0f0f0;
                    font-weight: bold;
                    font-size: 13px;
                }
                
                tr:nth-child(even) { 
                    background: #f8f8f8; 
                }
                
                .footer {
                    margin-top: 20px;
                    text-align: center;
                    color: #000;
                    font-size: 12px;
                    border-top: 1px solid #000;
                    padding-top: 10px;
                }
                
                @media print {
                    body { 
                        font-size: 12px;
                    }
                    
                    .print-container {
                        padding: 5px;
                    }
                    
                    th, td { 
                        padding: 6px 3px; 
                        font-size: 11px;
                    }
                    
                    .commission-card {
                        page-break-inside: avoid;
                        margin-bottom: 10px;
                    }
                    
                    @page {
                        margin: 10mm;
                    }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <div class="report-header">
                    <div class="company-name">نظام التقارير المالية</div>
                    <div class="report-title">${sectionTitle}</div>
                    <div class="delegate-info">المندوب: ${selectedDelegateDetails.delegateName}</div>
                    ${selectedDelegateDetails.delegateInfo?.PhoneNumber ? 
                        `<div class="delegate-info">الهاتف: ${selectedDelegateDetails.delegateInfo.PhoneNumber}</div>` : ''}
                    <div class="delegate-info">
                        تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'long'
                        })}
                    </div>
                </div>

                ${createSummarySection(isCommissionsTab)}

                <div class="section-content">
                    ${sectionContent}
                </div>

                <div class="footer">
                    تم إنشاء هذا التقرير بواسطة نظام التقارير المالية | 
                    ${new Date().toLocaleString('ar-EG')}
                </div>
            </div>
        </body>
        </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
    
    showToast(`تم إعداد طباعة ${sectionTitle} بنجاح`);
};

// دالة إنشاء قسم الملخص
const createSummarySection = (isCommissionsTab) => {
    if (isCommissionsTab) {
        return `
            <div class="summary-section">
                <div class="summary-title">ملخص العمولات</div>
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">عدد العمولات المدفوعة</div>
                        <div class="summary-value">${selectedDelegateDetails.commissionDetails?.length || 0}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">إجمالي مبلغ العمولات</div>
                        <div class="summary-value">${(selectedDelegateDetails.summary?.totalCommissionsPaid || 0).toLocaleString()} ج.م</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">عدد العملاء المرتبطين</div>
                        <div class="summary-value">${selectedDelegateDetails.summary?.clientsWithCommission || 0}</div>
                    </div>
                </div>
            </div>
        `;
    } else {
        return `
            <div class="summary-section">
                <div class="summary-title">ملخص العملاء</div>
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-label">إجمالي العملاء</div>
                        <div class="summary-value">${selectedDelegateDetails.summary?.totalClients || 0}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">العملاء المسافرين</div>
                        <div class="summary-value">${selectedDelegateDetails.summary?.traveledClients || 0}</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-label">الرصيد الإجمالي</div>
                        <div class="summary-value">${(selectedDelegateDetails.summary?.currentBalance || 0).toLocaleString()} ج.م</div>
                    </div>
                </div>
            </div>
        `;
    }
};

// دالة إنشاء محتوى العمولات للطباعة
const createCommissionsPrintContent = () => {
    if (!selectedDelegateDetails.commissionDetails?.length) {
        return '<div style="text-align: center; padding: 40px; color: #666;">لا توجد عمولات مدفوعة</div>';
    }
    
    return selectedDelegateDetails.commissionDetails.map((commission, index) => {
        const parseClients = (clientsData) => {
            if (!clientsData || clientsData === '-') return [];
            return clientsData.split(',').map(item => {
                const trimmed = item.trim();
                const parts = trimmed.split('(');
                const clientName = parts[0]?.trim() || trimmed;
                const companyName = parts[1]?.replace(')', '').trim() || 'غير محدد';
                return { name: clientName, company: companyName };
            });
        };
        
        const clients = parseClients(commission.ClientsNames);
        
        return `
            <div class="commission-card">
                <div class="commission-header">
                    <div>
                        <div class="commission-title">عمولة رقم ${index + 1}</div>
                        <div class="commission-date">التاريخ: ${new Date(commission.CommissionDate).toLocaleDateString('ar-EG')}</div>
                        <div class="commission-date">الوقت: ${new Date(commission.CommissionDate).toLocaleTimeString('ar-EG')}</div>
                    </div>
                    <div class="commission-amount">${(commission.CommissionAmount || 0).toLocaleString()} ج.م</div>
                </div>
                
                <div class="payment-method">
                    <strong>طريقة الدفع:</strong> ${commission.CompanyName || 'الخزينة الرئيسية'}
                </div>
                
                ${clients.length > 0 ? `
                    <div class="clients-section">
                        <div style="font-weight: bold; margin-bottom: 8px; color: #000;">
                            العملاء المرتبطين (${commission.ClientsCount} عملاء):
                        </div>
                        ${clients.map((client, clientIndex) => `
                            <div class="client-item">
                                <span class="client-name">${clientIndex + 1}. ${client.name}</span>
                                <span class="client-company">${client.company}</span>
                            </div>
                        `).join('')}
                    </div>
                ` : ''}
                
                ${commission.CommissionNotes ? `
                    <div style="background: #f0f0f0; border: 1px solid #000; padding: 8px; border-radius: 6px; margin-top: 8px;">
                        <strong>ملاحظات:</strong> ${commission.CommissionNotes}
                    </div>
                ` : ''}
                
                <div style="text-align: left; margin-top: 10px; padding-top: 8px; border-top: 1px solid #ccc; color: #333; font-size: 11px;">
                    تم بواسطة: ${commission.CreatedBy || 'النظام'} | معاملة #${commission.TransactionID}
                </div>
            </div>
        `;
    }).join('');
};

// دالة إنشاء محتوى العملاء للطباعة
const createClientsPrintContent = () => {
    if (!selectedDelegateDetails.clients?.length) {
        return '<div style="text-align: center; padding: 40px; color: #666;">لا يوجد عملاء مسجلين</div>';
    }
    
    return `
        <table>
            <thead>
                <tr>
                    <th>م</th>
                    <th>اسم العميل</th>
                    <th>الرقم القومي</th>
                    <th>رقم الجواز</th>
                    <th>الشركة</th>
                    <th>مهنة السفر</th>
                    <th>تاريخ النت</th>
                    <th>تاريخ التأشير</th>
                    <th>تاريخ السفر</th>
                    <th>حالة العمولة</th>
                    <th>الرصيد</th>
                    <th>الحالة</th>
                </tr>
            </thead>
            <tbody>
                ${selectedDelegateDetails.clients.map((client, index) => `
                    <tr>
                        <td>${index + 1}</td>
                        <td>${client.NameArabic || '-'}</td>
                        <td>${client.NationalID || '-'}</td>
                        <td>${client.PassportNumber || '-'}</td>
                        <td>${client.CompanyDisplayName || client.CompanyName || '-'}</td>
                        <td>${client.ProfessionName || '-'}</td>
                        <td>${client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td>${client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td>${client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td>${client.CommissionStatus || '-'}</td>
                        <td>${(client.CurrentBalance || 0).toLocaleString()} ج.م</td>
                        <td>${client.ClientStatus || '-'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
};



    // عرض فلاتر العملاء
    const renderClientFilters = () => (
        <div className="space-y-4">
            {/* الصف الأول من الفلاتر - الشركة، المندوب، المهنة */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        الشركة
                    </label>
                    <select
                        value={clientFilters.companyId}
                        onChange={(e) => setClientFilters({...clientFilters, companyId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل الشركات</option>
                        {companies.map(company => (
                            <option key={company.CompanyID} value={company.CompanyID}>
                                {company.CompanyName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        المندوب
                    </label>
                    <select
                        value={clientFilters.delegateId}
                        onChange={(e) => setClientFilters({...clientFilters, delegateId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل المندوبين</option>
                        {delegates.map(delegate => (
                            <option key={delegate.DelegateID} value={delegate.DelegateID}>
                                {delegate.DelegateName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        مهنة السفر
                    </label>
                    <select
                        value={clientFilters.professionId}
                        onChange={(e) => setClientFilters({...clientFilters, professionId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل المهن</option>
                        {professions.map(profession => (
                        <option key={profession.ProfessionID} value={profession.ProfessionID}>
                            {profession.ProfessionName}
                        </option>
                    ))}
                </select>
            </div>
        </div>

        {/* ✅ فلتر القوائم المحفوظة */}
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <label className="block text-sm font-semibold text-orange-800 mb-2">
                📋 القوائم المحفوظة (يمكن اختيار أكثر من قائمة)
            </label>
            {/* أزرار التحكم */}
            <div className="flex gap-2 mb-2">
                <button
                    type="button"
                    onClick={() => setClientFilters(prev => ({
                        ...prev,
                        listIds: savedLists.map(l => l.ListID.toString())
                    }))}
                    className="bg-orange-100 text-orange-600 px-3 py-1 rounded-lg hover:bg-orange-200 text-sm"
                >
                    تحديد كل القوائم
                </button>
                <button
                    type="button"
                    onClick={() => setClientFilters(prev => ({ ...prev, listIds: [] }))}
                    className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 text-sm"
                >
                    إلغاء التحديد
                </button>
                {clientFilters.listIds?.length > 0 && (
                    <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs flex items-center">
                        {clientFilters.listIds.length} قائمة محددة
                    </span>
                )}
            </div>
            {/* البحث في القوائم */}
            <div className="mb-2 relative">
                <Search className="absolute right-3 top-2.5 text-gray-400" size={16} />
                <input
                    type="text"
                    placeholder="ابحث في القوائم..."
                    value={savedListSearchTerm}
                    onChange={e => setSavedListSearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg py-2 px-8 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                />
                {savedListSearchTerm && (
                    <button
                        type="button"
                        onClick={() => setSavedListSearchTerm('')}
                        className="absolute left-2 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
            {/* قائمة القوائم */}
            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 bg-white">
                {savedLists.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-2">لا توجد قوائم محفوظة</p>
                ) : (
                    savedLists
                        .filter(list =>
                            !savedListSearchTerm ||
                            list.ListName.toLowerCase().includes(savedListSearchTerm.toLowerCase())
                        )
                        .map(list => (
                            <label
                                key={list.ListID}
                                className="flex items-center gap-2 p-1 hover:bg-orange-50 rounded cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                                    checked={clientFilters.listIds?.includes(list.ListID.toString()) || false}
                                    onChange={e => {
                                        const id = list.ListID.toString();
                                        const current = clientFilters.listIds || [];
                                        const next = e.target.checked
                                            ? [...current, id]
                                            : current.filter(x => x !== id);
                                        setClientFilters(prev => ({ ...prev, listIds: next }));
                                    }}
                                />
                                <span className="text-sm">{list.ListName}</span>
                            </label>
                        ))
                )}
            </div>
         
        </div>
        
        {/* الصف الثاني من الفلاتر - تاريخ المعاملات والرقم القومي */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ المعاملات (من)
                </label>
                <input
                    type="date"
                    value={clientFilters.dateFrom}
                    onChange={(e) => setClientFilters({...clientFilters, dateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ المعاملات (إلى)
                </label>
                <input
                    type="date"
                    value={clientFilters.dateTo}
                    onChange={(e) => setClientFilters({...clientFilters, dateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    الرقم القومي
                </label>
                <input
                    type="text"
                    placeholder="أدخل الرقم القومي للبحث"
                    value={clientFilters.nationalId}
                    onChange={(e) => setClientFilters({...clientFilters, nationalId: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>
        
        {/* تواريخ الفلترة - مضغوطة في صفين */}
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 mb-2 text-right">📅 فلترة بالتواريخ</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                {/* تاريخ النت */}
                <div className="bg-white rounded-md border border-gray-200 p-2">
                    <label className="block text-xs font-medium text-blue-600 mb-1 text-right">📌 تاريخ النت</label>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">من</span>
                            <input
                                type="date"
                                value={clientFilters.netWorkDateFrom}
                                onChange={(e) => setClientFilters({...clientFilters, netWorkDateFrom: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-blue-400"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">إلى</span>
                            <input
                                type="date"
                                value={clientFilters.netWorkDateTo}
                                onChange={(e) => setClientFilters({...clientFilters, netWorkDateTo: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-blue-400"
                            />
                        </div>
                    </div>
                </div>

                {/* تاريخ التأشير */}
                <div className="bg-white rounded-md border border-gray-200 p-2">
                    <label className="block text-xs font-medium text-orange-600 mb-1 text-right">🛂 تاريخ التأشير</label>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">من</span>
                            <input
                                type="date"
                                value={clientFilters.visaDateFrom}
                                onChange={(e) => setClientFilters({...clientFilters, visaDateFrom: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-orange-400"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">إلى</span>
                            <input
                                type="date"
                                value={clientFilters.visaDateTo}
                                onChange={(e) => setClientFilters({...clientFilters, visaDateTo: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-orange-400"
                            />
                        </div>
                    </div>
                </div>

                {/* تاريخ السفر */}
                <div className="bg-white rounded-md border border-gray-200 p-2">
                    <label className="block text-xs font-medium text-green-600 mb-1 text-right">✈️ تاريخ السفر</label>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">من</span>
                            <input
                                type="date"
                                value={clientFilters.travelDateFrom}
                                onChange={(e) => setClientFilters({...clientFilters, travelDateFrom: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-green-400"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">إلى</span>
                            <input
                                type="date"
                                value={clientFilters.travelDateTo}
                                onChange={(e) => setClientFilters({...clientFilters, travelDateTo: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-green-400"
                            />
                        </div>
                    </div>
                </div>

                {/* تاريخ البصمة */}
                <div className="bg-white rounded-md border border-gray-200 p-2">
                    <label className="block text-xs font-medium text-purple-600 mb-1 text-right">🖐️ تاريخ البصمة</label>
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">من</span>
                            <input
                                type="date"
                                value={clientFilters.fingerprintDateFrom}
                                onChange={(e) => setClientFilters({...clientFilters, fingerprintDateFrom: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-purple-400"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">إلى</span>
                            <input
                                type="date"
                                value={clientFilters.fingerprintDateTo}
                                onChange={(e) => setClientFilters({...clientFilters, fingerprintDateTo: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-purple-400"
                            />
                        </div>
                    </div>
                </div>

                {/* تاريخ دخول القنصلية - يأخذ عرض كامل في الصف التالي أو عمودين */}
                <div className="bg-white rounded-md border border-gray-200 p-2 sm:col-span-2 md:col-span-2">
                    <label className="block text-xs font-medium text-red-600 mb-1 text-right">🏛️ تاريخ دخول القنصلية</label>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">من</span>
                            <input
                                type="date"
                                value={clientFilters.consulateDateFrom}
                                onChange={(e) => setClientFilters({...clientFilters, consulateDateFrom: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-red-400"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 w-6 text-center shrink-0">إلى</span>
                            <input
                                type="date"
                                value={clientFilters.consulateDateTo}
                                onChange={(e) => setClientFilters({...clientFilters, consulateDateTo: e.target.value})}
                                className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-red-400"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* الصف السادس من الفلاتر - حالة المعاملات وحالة الرصيد */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة المعاملات
                </label>
                <select
                    value={clientFilters.hasTransactions}
                    onChange={(e) => setClientFilters({...clientFilters, hasTransactions: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">الكل</option>
                    <option value="true">لديه معاملات</option>
                    <option value="false">ليس لديه معاملات</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة الرصيد
                </label>
                <select
                    value={clientFilters.balanceType}
                    onChange={(e) => setClientFilters({...clientFilters, balanceType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">الكل</option>
                    <option value="positive">رصيد إيجابي</option>
                    <option value="negative">رصيد سلبي</option>
                    <option value="zero">رصيد صفري</option>
                </select>
            </div>
        </div>
        
        <div className="flex justify-end gap-4">
            <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
                إعادة تعيين
            </button>
            <button
                type="button"
                onClick={executeSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
                <Search size={16} />
                بحث
            </button>
        </div>
    </div>
);

    // عرض فلاتر المندوبين
    const renderDelegateFilters = () => {
        // فلترة المندوبين حسب البحث
        const filteredDelegates = delegates.filter(d =>
            d.DelegateName?.toLowerCase().includes(delegateSearchTerm.toLowerCase()) ||
            d.DelegateName?.includes(delegateSearchTerm)
        );

        const selectedDelegate = delegates.find(d => String(d.DelegateID) === String(delegateFilters.delegateId));

        return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div ref={delegateDropdownRef} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        المندوب
                    </label>
                    {/* حقل العرض / البحث */}
                    <div
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between cursor-pointer bg-white focus-within:ring-2 focus-within:ring-blue-500"
                        onClick={() => setShowDelegateDropdown(prev => !prev)}
                    >
                        <span className={`text-sm truncate ${selectedDelegate ? 'text-gray-900' : 'text-gray-400'}`}>
                            {selectedDelegate ? selectedDelegate.DelegateName : 'كل المندوبين'}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                            {delegateFilters.delegateId && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDelegateFilters({...delegateFilters, delegateId: ''});
                                        setDelegateSearchTerm('');
                                    }}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            )}
                            <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${showDelegateDropdown ? 'rotate-180' : ''}`} />
                        </div>
                    </div>

                    {/* القائمة المنسدلة */}
                    {showDelegateDropdown && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                            style={{minWidth: '220px'}}>
                            {/* حقل البحث */}
                            <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                    <Search size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        autoFocus
                                        placeholder="ابحث عن مندوب..."
                                        value={delegateSearchTerm}
                                        onChange={(e) => setDelegateSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-full border border-gray-200 rounded-md pr-7 pl-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                                    />
                                    {delegateSearchTerm && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setDelegateSearchTerm(''); }}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-400"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* قائمة المندوبين */}
                            <div className="max-h-52 overflow-y-auto">
                                {/* خيار الكل */}
                                <div
                                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center gap-2 ${!delegateFilters.delegateId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                    onClick={() => {
                                        setDelegateFilters({...delegateFilters, delegateId: ''});
                                        setDelegateSearchTerm('');
                                        setShowDelegateDropdown(false);
                                    }}
                                >
                                    <Users size={13} className="text-gray-400 shrink-0" />
                                    كل المندوبين
                                </div>

                                {filteredDelegates.length > 0 ? filteredDelegates.map(delegate => (
                                    <div
                                        key={delegate.DelegateID}
                                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center gap-2 ${String(delegateFilters.delegateId) === String(delegate.DelegateID) ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-800'}`}
                                        onClick={() => {
                                            setDelegateFilters({...delegateFilters, delegateId: delegate.DelegateID});
                                            setDelegateSearchTerm('');
                                            setShowDelegateDropdown(false);
                                        }}
                                    >
                                        <User size={13} className="text-gray-400 shrink-0" />
                                        <span className="truncate">{delegate.DelegateName}</span>
                                    </div>
                                )) : (
                                    <div className="px-3 py-4 text-sm text-gray-400 text-center">
                                        لا توجد نتائج لـ "{delegateSearchTerm}"
                                    </div>
                                )}
                            </div>

                            {/* عداد النتائج */}
                            {delegateSearchTerm && (
                                <div className="px-3 py-1.5 border-t border-gray-100 text-xs text-gray-400 text-center bg-gray-50">
                                    {filteredDelegates.length} نتيجة من {delegates.length}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        من تاريخ
                    </label>
                    <input
                        type="date"
                        value={delegateFilters.dateFrom}
                        onChange={(e) => setDelegateFilters({...delegateFilters, dateFrom: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        إلى تاريخ
                    </label>
                    <input
                        type="date"
                        value={delegateFilters.dateTo}
                        onChange={(e) => setDelegateFilters({...delegateFilters, dateTo: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
            
            <div className="flex justify-end gap-4">
                <button
                    type="button"
                    onClick={resetFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    إعادة تعيين
                </button>
                <button
                    type="button"
                    onClick={executeSearch}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Search size={16} />
                    بحث
                </button>
            </div>
        </div>
        );
    };

    // عرض فلاتر الشركة
   // عرض فلاتر الشركة
const renderCompanyFilters = () => (
    <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    من تاريخ
                </label>
                <input
                    type="date"
                    value={companyFilters.dateFrom}
                    onChange={(e) => setCompanyFilters({...companyFilters, dateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    إلى تاريخ
                </label>
                <input
                    type="date"
                    value={companyFilters.dateTo}
                    onChange={(e) => setCompanyFilters({...companyFilters, dateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    نوع المعاملة
                </label>
                <select
                    value={companyFilters.transactionType}
                    onChange={(e) => setCompanyFilters({...companyFilters, transactionType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">الكل</option>
                    <option value="income">إيرادات</option>
                    <option value="expense">مصروفات</option>
                </select>
            </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    التصنيف
                </label>
                <select
                    value={companyFilters.categoryId}
                    onChange={(e) => setCompanyFilters({...companyFilters, categoryId: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">جميع التصنيفات</option>
                {companyCategories.map(category => (
    <option key={category.id} value={category.id}>
        {category.name} ({category.type === 'income' ? 'إيراد' : 'مصروف'})
    </option>
))}
                </select>
            </div>
        </div>
        
        <div className="flex justify-end gap-4">
            <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
                إعادة تعيين
            </button>
            <button
                type="button"
                onClick={executeSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
                <Search size={16} />
                بحث
            </button>
        </div>
    </div>
);

    // عرض فلاتر الخزينة
    const renderTreasuryFilters = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        من تاريخ
                    </label>
                    <input
                        type="date"
                        value={treasuryFilters.dateFrom}
                        onChange={(e) => setTreasuryFilters({...treasuryFilters, dateFrom: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        إلى تاريخ
                    </label>
                    <input
                        type="date"
                        value={treasuryFilters.dateTo}
                        onChange={(e) => setTreasuryFilters({...treasuryFilters, dateTo: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع المعاملة
                    </label>
                    <select
                        value={treasuryFilters.transactionType}
                        onChange={(e) => setTreasuryFilters({...treasuryFilters, transactionType: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">الكل</option>
                        <option value="deposit">إيداع</option>
                        <option value="withdrawal">سحب</option>
                    </select>
                </div>
            </div>
            
            <div className="flex justify-end gap-4">
                <button
                    type="button"
                    onClick={resetFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    إعادة تعيين
                </button>
                <button
                    type="button"
                    onClick={executeSearch}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Search size={16} />
                    بحث
                </button>
            </div>
        </div>
    );



    // عرض فلاتر التقرير الشامل
// عرض فلاتر التقرير الشامل - محسن
const renderComprehensiveFilters = () => (
    <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    نوع المعاملة
                </label>
                <select
                    value={comprehensiveFilters.transactionType}
                    onChange={(e) => setComprehensiveFilters({...comprehensiveFilters, transactionType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">جميع الأنواع</option>
                    <optgroup label="معاملات العملاء">
                        <option value="client_deposit">إيداع عميل</option>
                        <option value="client_withdrawal">سحب عميل</option>
                    </optgroup>
                    <optgroup label="معاملات المندوبين">
                        <option value="delegate_commission">سحب عمولة مندوب</option>
                        <option value="delegate_deposit">إيداع مندوب</option>
                        <option value="delegate_withdrawal">سحب مندوب</option>
                    </optgroup>
                    <optgroup label="معاملات الموظفين">
                        <option value="employee_salary">راتب موظف</option>
                        <option value="employee_bonus">بونص موظف</option>
                        <option value="employee_advance">سلفة موظف</option>
                        <option value="employee_installment">دفع قسط موظف</option>
                        <option value="employee_deduction">خصم موظف</option>
                    </optgroup>
                    <optgroup label="معاملات الشركة">
                        <option value="company_income">إيرادات الشركة</option>
                        <option value="company_expense">مصروفات الشركة</option>
                    </optgroup>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    من تاريخ
                </label>
                <input
                    type="date"
                    value={comprehensiveFilters.dateFrom}
                    onChange={(e) => setComprehensiveFilters({...comprehensiveFilters, dateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    إلى تاريخ
                </label>
                <input
                    type="date"
                    value={comprehensiveFilters.dateTo}
                    onChange={(e) => setComprehensiveFilters({...comprehensiveFilters, dateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    بحث في الأسماء
                </label>
                <input
                    type="text"
                    placeholder="اسم العميل، المندوب، الوصف..."
                    value={comprehensiveFilters.searchTerm}
                    onChange={(e) => setComprehensiveFilters({...comprehensiveFilters, searchTerm: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>
        
        <div className="flex justify-end gap-4">
            <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
                إعادة تعيين
            </button>
            <button
                type="button"
                onClick={executeSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
                <Search size={16} />
                بحث
            </button>
        </div>
    </div>
);


// عرض فلاتر الموظفين
const renderEmployeeFilters = () => (
    <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    نوع المعاملة
                </label>
                <select
                    value={employeeFilters.transactionType}
                    onChange={(e) => setEmployeeFilters({...employeeFilters, transactionType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">جميع الأنواع</option>
                    <option value="راتب">راتب</option>
                    <option value="بونص">بونص</option>
                    <option value="سلفة">سلفة</option>
                    <option value="دفع قسط">دفع قسط</option>
                    <option value="خصم">خصم</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة الموظف
                </label>
                <select
                    value={employeeFilters.paymentStatus}
                    onChange={(e) => setEmployeeFilters({...employeeFilters, paymentStatus: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">الكل</option>
                    <option value="active">نشط</option>
                    <option value="inactive">غير نشط</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    من تاريخ
                </label>
                <input
                    type="date"
                    value={employeeFilters.dateFrom}
                    onChange={(e) => setEmployeeFilters({...employeeFilters, dateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    إلى تاريخ
                </label>
                <input
                    type="date"
                    value={employeeFilters.dateTo}
                    onChange={(e) => setEmployeeFilters({...employeeFilters, dateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>
        
        <div className="flex justify-end gap-4">
            <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
                إعادة تعيين
            </button>
            <button
                type="button"
                onClick={executeSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
                <Search size={16} />
                بحث
            </button>
        </div>
    </div>
);


const MobileField = ({ label, value, valueClassName = '', fullWidth = false }) => (
    <div className={`${fullWidth ? 'col-span-2' : ''} bg-gray-50 border border-gray-100 rounded-2xl p-3`}>
        <div className="text-[11px] font-medium text-gray-500 mb-1">{label}</div>
        <div className={`text-sm font-semibold text-gray-900 leading-6 break-words ${valueClassName}`}>{value !== undefined && value !== null ? value : '-'}</div>
    </div>
);

const formatMobileDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
        return new Date(dateValue).toLocaleDateString('ar-EG');
    } catch {
        return dateValue;
    }
};


    // عرض محتوى التقرير حسب التبويب النشط
    const renderReportContent = () => {
        switch (activeTab) {
            case 'clients':
                return renderClientReport();
            case 'delegates':
                return renderDelegateReport();
            case 'company':
                return renderCompanyReport();
          case 'treasury':
    return renderTreasuryReport();
    case 'comprehensive':
        return renderComprehensiveReport();
    case 'employees':
        return renderEmployeeReport();
    case 'debts':
        return renderDebtReport();
    default:
        return null;
        }
    };

    const memoizedClientReport = useMemo(() => {
        if (!reportData || reportData.length === 0) {
            return (
                <div className="p-8 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
                </div>
            );
        }

        const clientsData = clientDues.length > 0 ? clientDues : reportData;

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">اسم العميل</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الرقم القومي</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الشركة</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">المندوب</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الرصيد الحالي</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 bg-yellow-50">المبلغ المطلوب</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 bg-yellow-50">المتبقي</th>
                            <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {clientsData.map((client, index) => (
                            <tr key={client.PassportID} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-base text-gray-900">{client.NameArabic || '-'}</td>
                                <td className="px-6 py-4 text-base text-gray-500">{client.NationalID || '-'}</td>
                                <td className="px-6 py-4 text-base text-gray-500">{client.CompanyName || '-'}</td>
                                <td className="px-6 py-4 text-base text-gray-500">{client.DelegateName || '-'}</td>
                                <td className={`px-6 py-4 text-base ${
                                    client.CurrentBalance > 0 ? 'text-green-600' :
                                    client.CurrentBalance < 0 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                    {formatCurrency(client.CurrentBalance)}
                                </td>
                                <td className="px-6 py-4 text-base text-gray-800 bg-yellow-50 font-medium">
                                    {client.RequiredPayment > 0 
                                        ? formatCurrency(client.RequiredPayment) 
                                        : <span className="text-gray-400">لا يوجد</span>}
                                </td>
                                <td className={`px-6 py-4 text-base bg-yellow-50 ${
                                    client.IsOverpaid 
                                        ? 'text-blue-600 font-bold' 
                                        : (client.RemainingDue || 0) > 0 
                                            ? 'text-red-600 font-bold' 
                                            : 'text-green-600'
                                }`}>
                                    {client.IsOverpaid 
                                        ? `+${formatCurrency(client.RemainingDue || 0)}`
                                        : formatCurrency(client.RemainingDue || 0)}
                                </td>
                                <td className="px-6 py-4 text-base text-center">
                                    <button
                                        onClick={() => showClientAccount(client.PassportID)}
                                        className="inline-flex items-center px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                                    >
                                        <Eye size={14} className="mr-1" />
                                        معاينة
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }, [reportData, clientDues]);


const renderClientReport = () => {
    if (!reportData || reportData.length === 0) {
        return (
            <div className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
            </div>
        );
    }

    const clientsData = clientDues.length > 0 ? clientDues : reportData;
    const currentPageData = getCurrentPageData(clientsData);

    return (
        <>
            <div className="md:hidden space-y-3 p-2">
                {currentPageData.map((client) => {
                    const remainingValue = client.IsOverpaid
                        ? `+${formatCurrency(client.RemainingDue || 0)}`
                        : formatCurrency(client.RemainingDue || 0);
                    const remainingClass = client.IsOverpaid
                        ? 'text-blue-600'
                        : (client.RemainingDue || 0) > 0
                            ? 'text-red-600'
                            : 'text-green-600';
                    return (
                        <div key={client.PassportID} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-4 py-3 bg-gradient-to-l from-blue-50 to-indigo-50 border-b border-blue-100 flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-extrabold text-gray-900 leading-7">{client.NameArabic || '-'}</h3>
                                    <div className="text-xs text-gray-500 mt-1">الرقم القومي: {client.NationalID || '-'}</div>
                                </div>
                                <div className={`px-3 py-1.5 rounded-2xl text-xs font-bold ${client.CurrentBalance > 0 ? 'bg-green-100 text-green-700' : client.CurrentBalance < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {formatCurrency(client.CurrentBalance)}
                                </div>
                            </div>

                            <div className="p-4 grid grid-cols-2 gap-3">
                                <MobileField label="الشركة" value={client.CompanyName || '-'} />
                                <MobileField label="المندوب" value={client.DelegateName || '-'} />
                                <MobileField label="المبلغ المطلوب" value={client.RequiredPayment > 0 ? formatCurrency(client.RequiredPayment) : 'لا يوجد'} valueClassName="text-amber-700" />
                                <MobileField label="المتبقي" value={remainingValue} valueClassName={remainingClass} />
                            </div>

                            <div className="px-4 pb-4">
                                <button
                                    onClick={() => showClientAccount(client.PassportID)}
                                    className="w-full bg-blue-600 text-white rounded-2xl py-3 font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
                                >
                                    <Eye size={16} />
                                    عرض الحساب
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">اسم العميل</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الرقم القومي</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الشركة</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">المندوب</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الرصيد الحالي</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 bg-yellow-50">المبلغ المطلوب</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 bg-yellow-50">المتبقي</th>
                            <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentPageData.map((client) => (
                            <tr key={client.PassportID} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-base text-gray-900">{client.NameArabic || '-'}</td>
                                <td className="px-6 py-4 text-base text-gray-500">{client.NationalID || '-'}</td>
                                <td className="px-6 py-4 text-base text-gray-500">{client.CompanyName || '-'}</td>
                                <td className="px-6 py-4 text-base text-gray-500">{client.DelegateName || '-'}</td>
                                <td className={`px-6 py-4 text-base ${
                                    client.CurrentBalance > 0 ? 'text-green-600' :
                                    client.CurrentBalance < 0 ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                    {formatCurrency(client.CurrentBalance)}
                                </td>
                                <td className="px-6 py-4 text-base text-gray-800 bg-yellow-50 font-medium">
                                    {client.RequiredPayment > 0 
                                        ? formatCurrency(client.RequiredPayment) 
                                        : <span className="text-gray-400">لا يوجد</span>}
                                </td>
                                <td className={`px-6 py-4 text-base bg-yellow-50 ${
                                    client.IsOverpaid 
                                        ? 'text-blue-600 font-bold' 
                                        : (client.RemainingDue || 0) > 0 
                                            ? 'text-red-600 font-bold' 
                                            : 'text-green-600'
                                }`}>
                                    {client.IsOverpaid 
                                        ? `+${formatCurrency(client.RemainingDue || 0)}`
                                        : formatCurrency(client.RemainingDue || 0)}
                                </td>
                                <td className="px-6 py-4 text-base text-center">
                                    <button
                                        onClick={() => showClientAccount(client.PassportID)}
                                        className="inline-flex items-center px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                                    >
                                        <Eye size={14} className="mr-1" />
                                        معاينة
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <PaginationComponent />
        </>
    );
};






    // عرض تقرير المندوبين
// في دالة renderDelegateReport - استبدال الجدول الحالي بهذا:

const renderDelegateReport = () => {
   if (!reportData || reportData.length === 0) {
       return (
           <div className="p-8 text-center">
               <FileText size={48} className="mx-auto text-gray-300 mb-4" />
               <p className="text-gray-500 text-lg">لا توجد بيانات متاحة. يرجى الضغط على زر البحث لجلب تقرير المندوبين.</p>
           </div>
       );
   }

   return (
       <>
           <div className="md:hidden space-y-3 p-2">
               {reportData.map((delegate, index) => (
                   <div key={index} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                       <div className="px-4 py-3 bg-gradient-to-l from-blue-50 to-indigo-50 border-b border-blue-100 flex items-center justify-between gap-3">
                           <div>
                               <div className="text-xs text-gray-500 mb-1">المندوب #{index + 1}</div>
                               <h3 className="text-base font-extrabold text-gray-900">{delegate.DelegateName || '-'}</h3>
                           </div>
                           <div className="flex items-center gap-2 bg-white rounded-2xl px-3 py-2 border border-blue-100">
                               <Phone size={14} className="text-blue-600" />
                               <span className="text-sm font-bold text-gray-800">{delegate.PhoneNumber || '-'}</span>
                               {delegate.PhoneNumber && (
                                   <button onClick={() => copyToClipboard(delegate.PhoneNumber)} className="text-blue-500" title="نسخ رقم الهاتف">
                                       <Copy size={15} />
                                   </button>
                               )}
                           </div>
                       </div>
                       <div className="p-4 grid grid-cols-2 gap-3">
                           <MobileField label="إجمالي العملاء" value={delegate.TotalClients || '0'} valueClassName="text-blue-700" />
                           <MobileField label="العملاء المسافرين" value={delegate.TraveledClients || '0'} valueClassName="text-green-700" />
                           <MobileField label="عمولة مدفوعة" value={delegate.PaidCommissionClients || '0'} valueClassName="text-emerald-700" />
                           <MobileField label="غير مدفوعة" value={delegate.UnpaidCommissionClients || '0'} valueClassName="text-red-700" />
                           <MobileField label="إجمالي العمولات المدفوعة" value={`${(delegate.TotalCommissionsPaid || 0).toLocaleString()} ج.م`} fullWidth valueClassName="text-green-700" />
                       </div>
                       <div className="px-4 pb-4">
                           <button onClick={() => showDelegateDetails(delegate.DelegateID, delegate.DelegateName)} className="w-full bg-blue-600 text-white rounded-2xl py-3 font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors">
                               <Eye size={16} />
                               عرض التفاصيل الشاملة
                           </button>
                       </div>
                   </div>
               ))}
           </div>

           <div className="hidden md:block overflow-x-auto">
               <table className="min-w-full bg-white border">
                   <thead className="bg-gray-50">
                       <tr>
                           <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">#</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">اسم المندوب</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">رقم الهاتف</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">إجمالي العملاء</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">العملاء المسافرين</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">عمولة مدفوعة</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">عمولة غير مدفوعة</th>
                           <th className="px-6 py-4 text-right text-sm font-bold text-gray-700">إجمالي العمولات المدفوعة</th>
                           <th className="px-6 py-4 text-center text-sm font-bold text-gray-700">إجراءات</th>
                       </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-200">
                       {reportData.map((delegate, index) => (
                           <tr key={index} className="hover:bg-gray-50">
                               <td className="px-4 py-4 text-base font-medium text-gray-600">{index + 1}</td>
                               <td className="px-6 py-4 text-base text-gray-900 font-bold">{delegate.DelegateName || '-'}</td>
                               <td className="px-6 py-4 text-base text-gray-700">
                                   <div className="flex items-center gap-2">
                                       <span className="font-medium">{delegate.PhoneNumber || '-'}</span>
                                       {delegate.PhoneNumber && (
                                           <button onClick={() => copyToClipboard(delegate.PhoneNumber)} className="text-blue-500 hover:text-blue-700 transition-colors" title="نسخ رقم الهاتف">
                                               <Copy size={16} />
                                           </button>
                                       )}
                                   </div>
                               </td>
                               <td className="px-6 py-4 text-base text-center"><span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-blue-100 text-blue-800">{delegate.TotalClients || '0'}</span></td>
                               <td className="px-6 py-4 text-base text-center"><span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-green-100 text-green-800">{delegate.TraveledClients || '0'}</span></td>
                               <td className="px-6 py-4 text-base text-center"><span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-emerald-100 text-emerald-800"><CheckCircle size={14} className="mr-1" />{delegate.PaidCommissionClients || '0'}</span></td>
                               <td className="px-6 py-4 text-base text-center"><span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-red-100 text-red-800"><AlertCircle size={14} className="mr-1" />{delegate.UnpaidCommissionClients || '0'}</span></td>
                               <td className="px-6 py-4 text-lg text-green-600 font-bold text-center">{(delegate.TotalCommissionsPaid || 0).toLocaleString()} ج.م</td>
                               <td className="px-6 py-4 text-base text-center"><button onClick={() => showDelegateDetails(delegate.DelegateID, delegate.DelegateName)} className="inline-flex items-center px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm transition-colors" title="عرض التفاصيل الشاملة للمندوب"><Eye size={16} className="mr-1" />تفاصيل شاملة</button></td>
                           </tr>
                       ))}
                   </tbody>
               </table>
           </div>

           <div className="mt-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4 border border-blue-200">
               <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">ملخص إحصائي للمندوبين</h3>
               <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                   <div className="text-center bg-white rounded-lg p-3 shadow-sm"><div className="text-2xl font-bold text-blue-600 mb-1">{reportData.reduce((sum, d) => sum + (d.TotalClients || 0), 0)}</div><div className="text-sm font-medium text-gray-600">إجمالي العملاء</div></div>
                   <div className="text-center bg-white rounded-lg p-3 shadow-sm"><div className="text-2xl font-bold text-green-600 mb-1">{reportData.reduce((sum, d) => sum + (d.TraveledClients || 0), 0)}</div><div className="text-sm font-medium text-gray-600">العملاء المسافرين</div></div>
                   <div className="text-center bg-white rounded-lg p-3 shadow-sm"><div className="text-2xl font-bold text-emerald-600 mb-1">{reportData.reduce((sum, d) => sum + (d.PaidCommissionClients || 0), 0)}</div><div className="text-sm font-medium text-gray-600">عمولة مدفوعة</div></div>
                   <div className="text-center bg-white rounded-lg p-3 shadow-sm"><div className="text-2xl font-bold text-red-600 mb-1">{reportData.reduce((sum, d) => sum + (d.UnpaidCommissionClients || 0), 0)}</div><div className="text-sm font-medium text-gray-600">عمولة غير مدفوعة</div></div>
               </div>
               <div className="mt-4 text-center bg-green-100 rounded-lg p-4">
                   <div className="text-2xl font-bold text-green-700 mb-1">{reportData.reduce((sum, d) => sum + (d.TotalCommissionsPaid || 0), 0).toLocaleString()} ج.م</div>
                   <div className="text-sm font-bold text-green-800">إجمالي العمولات المدفوعة لجميع المندوبين</div>
               </div>
           </div>
       </>
   );
};


    // عرض تقرير الشركة
  // عرض تقرير الشركة

const renderCompanyReport = () => {
    if (!reportData || reportData.length === 0) {
        return (
            <div className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
            </div>
        );
    }

    return (
        <>
            <div className="md:hidden space-y-3 p-2">
                {reportData.map((transaction, index) => (
                    <div key={transaction.TransactionID || index} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${transaction.Type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {transaction.Type === 'income' ? <TrendingUp size={14} className="ml-1" /> : <TrendingDown size={14} className="ml-1" />}
                                    {transaction.Type === 'income' ? 'إيراد' : 'مصروف'}
                                </span>
                                <span className="text-xs text-gray-500">{formatMobileDate(transaction.TransactionDate)}</span>
                            </div>
                            <div className={`text-sm font-extrabold ${transaction.Type === 'income' ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(transaction.Amount)}</div>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                            <MobileField label="التصنيف" value={transaction.CategoryName || '-'} />
                            <MobileField label="طريقة الدفع" value={transaction.PaymentMethodName || '-'} />
                            <MobileField label="تم بواسطة" value={transaction.CreatedBy || '-'} />
                            <MobileField label="الوصف" value={transaction.Description || '-'} fullWidth />
                            <MobileField label="الملاحظات" value={transaction.Notes || '-'} fullWidth />
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">التاريخ</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">نوع المعاملة</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">التصنيف</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الوصف</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">المبلغ</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">طريقة الدفع</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الملاحظات</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">تم بواسطة</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {reportData.map((transaction, index) => (
                            <tr key={transaction.TransactionID || index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-900">{transaction.TransactionDate ? new Date(transaction.TransactionDate).toLocaleDateString('ar-EG') : '-'}</td>
                                <td className="px-6 py-4"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${transaction.Type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{transaction.Type === 'income' ? <TrendingUp size={14} className="mr-1" /> : <TrendingDown size={14} className="mr-1" />}{transaction.Type === 'income' ? 'إيراد' : 'مصروف'}</span></td>
                                <td className="px-6 py-4 text-sm text-gray-900">{transaction.CategoryName || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{transaction.Description || '-'}</td>
                                <td className={`px-6 py-4 text-sm font-medium ${transaction.Type === 'income' ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(transaction.Amount)}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{transaction.PaymentMethodName || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={transaction.Notes || '-'}>{transaction.Notes || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{transaction.CreatedBy || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};


    // عرض تقرير الخزينة

const renderTreasuryReport = () => {
    if (!reportData || reportData.length === 0) {
        return (
            <div className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
            </div>
        );
    }

    return (
        <>
            <div className="md:hidden space-y-3 p-2">
                {reportData.map((transaction, index) => (
                    <div key={index} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${transaction.TransactionType === 'إيداع' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {transaction.TransactionType === 'إيداع' ? <TrendingUp size={14} className="ml-1" /> : <TrendingDown size={14} className="ml-1" />}
                                    {transaction.TransactionType}
                                </span>
                                <span className="text-xs text-gray-500">{formatMobileDate(transaction.TransactionDate)}</span>
                            </div>
                            <div className={`text-sm font-extrabold ${transaction.TransactionType === 'إيداع' ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(transaction.Amount)}</div>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                            <MobileField label="الرصيد بعد العملية" value={formatCurrency(transaction.Balance)} valueClassName="text-blue-700" />
                            <MobileField label="اسم العميل" value={transaction.ClientName || '-'} />
                            <MobileField label="تم بواسطة" value={transaction.CreatedBy || '-'} />
                            <MobileField label="الملاحظات" value={transaction.Notes || '-'} fullWidth />
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">التاريخ</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">نوع المعاملة</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المبلغ</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرصيد</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم العميل</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الملاحظات</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تم بواسطة</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {reportData.map((transaction, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{new Date(transaction.TransactionDate).toLocaleDateString('ar-SA')}</td>
                                <td className="px-4 py-3"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${transaction.TransactionType === 'إيداع' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{transaction.TransactionType === 'إيداع' ? <TrendingUp size={14} className="mr-1" /> : <TrendingDown size={14} className="mr-1" />}{transaction.TransactionType}</span></td>
                                <td className={`px-4 py-3 text-sm ${transaction.TransactionType === 'إيداع' ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(transaction.Amount)}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(transaction.Balance)}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{transaction.ClientName || '-'}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{transaction.Notes || '-'}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{transaction.CreatedBy || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};




    // عرض التقرير الشامل

const renderComprehensiveReport = () => {
    if (!reportData || reportData.length === 0) {
        return (
            <div className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">لا توجد بيانات متاحة. يرجى الضغط على زر البحث لجلب التقرير الشامل.</p>
            </div>
        );
    }

    return (
        <>
            <div className="md:hidden space-y-3 p-2">
                {reportData.map((transaction, index) => (
                    <div key={transaction.id || index} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-1 ${transaction.amount >= 0 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                {transaction.entityType === 'client' && <User size={12} className="text-blue-600" />}
                                {transaction.entityType === 'delegate' && <Briefcase size={12} className="text-purple-600" />}
                                {transaction.entityType === 'company' && <Building size={12} className="text-gray-600" />}
                                {transaction.amount >= 0 ? <TrendingUp size={12} className="text-green-600" /> : <TrendingDown size={12} className="text-red-600" />}
                                {transaction.typeName}
                            </span>
                            <div className={`text-sm font-extrabold ${(transaction.amount || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{(transaction.amount || 0) >= 0 ? '+' : ''}{(transaction.amount || 0).toLocaleString()} ج.م</div>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                            <MobileField label="الاسم / الوصف" value={transaction.name} />
                            <MobileField label="الشركة / التصنيف" value={transaction.company} />
                            <MobileField label="طريقة الدفع" value={transaction.paymentMethod || 'غير محدد'} />
                            <MobileField label="التاريخ" value={formatMobileDate(transaction.date)} />
                            <MobileField label="الملاحظات" value={transaction.notes || 'لا توجد ملاحظات'} fullWidth />
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-4 text-right text-sm font-medium text-gray-500">نوع المعاملة</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الاسم/الوصف</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الشركة/التصنيف</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">طريقة الدفع</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">المبلغ</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">التاريخ</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الملاحظات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {reportData.map((transaction, index) => (
                            <tr key={transaction.id || index} className="hover:bg-gray-50">
                                <td className="px-4 py-4"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1 ${transaction.amount >= 0 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{transaction.entityType === 'client' && <User size={12} className="text-blue-600" />}{transaction.entityType === 'delegate' && <Briefcase size={12} className="text-purple-600" />}{transaction.entityType === 'company' && <Building size={12} className="text-gray-600" />}{transaction.amount >= 0 ? <TrendingUp size={12} className="text-green-600" /> : <TrendingDown size={12} className="text-red-600" />}{transaction.typeName}</span></td>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{transaction.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{transaction.company}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{transaction.paymentMethod || 'غير محدد'}</td>
                                <td className="px-6 py-4 text-sm font-medium"><span className={`px-3 py-1 rounded-lg font-bold ${(transaction.amount || 0) >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{(transaction.amount || 0) >= 0 ? '+' : ''}{(transaction.amount || 0).toLocaleString()} ج.م</span></td>
                                <td className="px-6 py-4 text-sm text-gray-500">{new Date(transaction.date).toLocaleDateString('ar-EG')}</td>
                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={transaction.notes}>{transaction.notes || 'لا توجد ملاحظات'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};




// عرض تقرير الموظفين

const renderEmployeeReport = () => {
    if (!reportData || reportData.length === 0) {
        return (
            <div className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
            </div>
        );
    }

    return (
        <>
            <div className="md:hidden space-y-3 p-2">
                {reportData.map((employee, index) => (
                    <div key={employee.EmployeeId} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 bg-gradient-to-l from-indigo-50 to-blue-50 border-b border-blue-100 flex items-center justify-between gap-3">
                            <div>
                                <div className="text-xs text-gray-500 mb-1">الموظف #{index + 1}</div>
                                <h3 className="text-base font-extrabold text-gray-900">{employee.EmployeeName}</h3>
                                <div className="text-sm text-gray-500 mt-1">{employee.Position || '-'}</div>
                            </div>
                            <div className="px-3 py-1.5 rounded-2xl bg-blue-100 text-blue-700 text-sm font-extrabold">{formatCurrency(employee.BasicSalary)}</div>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                            <MobileField label="إجمالي الرواتب" value={formatCurrency(employee.TotalSalariesPaid)} valueClassName="text-green-700" />
                            <MobileField label="إجمالي السلف" value={formatCurrency(employee.TotalAdvances)} valueClassName="text-red-700" />
                            <MobileField label="إجمالي البونصات" value={formatCurrency(employee.TotalBonuses)} valueClassName="text-blue-700" />
                            <MobileField label="عدد المعاملات" value={employee.TransactionCount} />
                            <MobileField label="آخر معاملة" value={employee.LastTransactionDate ? `${employee.LastTransactionType} - ${formatMobileDate(employee.LastTransactionDate)}` : '-'} fullWidth />
                        </div>
                        <div className="px-4 pb-4">
                            <button onClick={() => showEmployeeTransactions(employee.EmployeeId, employee.EmployeeName)} className="w-full bg-blue-600 text-white rounded-2xl py-3 font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors">
                                <Eye size={16} />
                                عرض تفاصيل الموظف
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full bg-white border">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-4 text-right text-sm font-medium text-gray-500">م</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">اسم الموظف</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">المنصب</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">الراتب الأساسي</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">إجمالي الرواتب</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">إجمالي السلف</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">إجمالي البونصات</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">عدد المعاملات</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">آخر معاملة</th>
                            <th className="px-6 py-4 text-center text-sm font-medium text-gray-500">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {reportData.map((employee, index) => (
                            <tr key={employee.EmployeeId} className="hover:bg-gray-50">
                                <td className="px-4 py-4 text-base font-medium text-gray-600">{index + 1}</td>
                                <td className="px-6 py-4 text-base text-gray-900 font-bold">{employee.EmployeeName}</td>
                                <td className="px-6 py-4 text-base text-gray-600">{employee.Position}</td>
                                <td className="px-6 py-4 text-base text-gray-900 font-medium">{formatCurrency(employee.BasicSalary)}</td>
                                <td className="px-6 py-4 text-base text-green-600 font-bold">{formatCurrency(employee.TotalSalariesPaid)}</td>
                                <td className="px-6 py-4 text-base text-red-600 font-bold">{formatCurrency(employee.TotalAdvances)}</td>
                                <td className="px-6 py-4 text-base text-blue-600 font-bold">{formatCurrency(employee.TotalBonuses)}</td>
                                <td className="px-6 py-4 text-base text-center"><span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">{employee.TransactionCount}</span></td>
                                <td className="px-6 py-4 text-base text-gray-500">{employee.LastTransactionDate ? (<div><div className="font-medium">{employee.LastTransactionType}</div><div className="text-xs text-gray-400">{new Date(employee.LastTransactionDate).toLocaleDateString('ar-EG')}</div></div>) : '-'}</td>
                                <td className="px-6 py-4 text-center"><button onClick={() => showEmployeeTransactions(employee.EmployeeId, employee.EmployeeName)} className="inline-flex items-center px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm transition-colors"><Eye size={14} className="mr-1" />معاينة</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};


    // عرض الإحصائيات
// عرض الإحصائيات
// عرض الإحصائيات
// عرض الإحصائيات
const renderSummary = () => {
   if (!reportData || reportData.length === 0) return null;

   // إحصائيات مخصصة حسب التبويب
   let summaryCards = [];

   if (activeTab === 'debts') {
       summaryCards = [
           { title: 'مديونيات مفتوحة', value: Number(debtSummary.OpenDebtCount || 0).toLocaleString(), color: 'from-rose-500 to-rose-600', icon: Wallet },
           { title: 'المتبقي بالجنيه', value: `${Number(debtSummary.OutstandingEGP || 0).toLocaleString()} ج.م`, color: 'from-red-500 to-red-600', icon: DollarSign },
           { title: 'المتبقي بالريال', value: `${Number(debtSummary.OutstandingSAR || 0).toLocaleString()} ر.س`, color: 'from-amber-500 to-amber-600', icon: DollarSign },
           { title: 'متأخرة عن الموعد', value: Number(debtSummary.OverdueCount || 0).toLocaleString(), color: 'from-violet-500 to-violet-600', icon: AlertCircle }
       ];
   } else if (activeTab === 'delegates') {
       // إحصائيات خاصة بالمندوبين (بطاقتين فقط)
       summaryCards = [
           {
               title: 'عدد المندوبين',
               value: summaryData.totalClients,
               color: 'from-blue-500 to-blue-600',
               icon: Users
           },
           {
               title: 'إجمالي العمولات المدفوعة',
               value: `${summaryData.totalBalance?.toLocaleString()} ج.م`,
               color: 'from-purple-500 to-purple-600',
               icon: DollarSign
           }
       ];
   } else {
       // إحصائيات موحدة للتبويبات الأخرى (4 بطاقات)
       summaryCards = [
           {
               title: activeTab === 'company' ? 'عدد المعاملات' : 'عدد السجلات',
               value: summaryData.totalClients,
               color: 'from-blue-500 to-blue-600',
               icon: FileText
           },
           {
               title: activeTab === 'company' ? 'إجمالي الإيرادات' : 'إجمالي الإيداعات',
               value: `${summaryData.totalDeposits?.toLocaleString()} ج.م`,
               color: 'from-green-500 to-green-600',
               icon: TrendingUp
           },
           {
               title: activeTab === 'company' ? 'إجمالي المصروفات' : 'إجمالي السحوبات',
               value: `${summaryData.totalWithdrawals?.toLocaleString()} ج.م`,
               color: 'from-red-500 to-red-600',
               icon: TrendingDown
           },
           {
               title: activeTab === 'company' ? 'صافي الرصيد' : 'الرصيد',
               value: `${summaryData.totalBalance?.toLocaleString()} ج.م`,
               color: summaryData.totalBalance >= 0 ? 
                     'from-purple-500 to-purple-600' : 
                     'from-red-500 to-red-600',
               icon: DollarSign
           }
       ];
   }

   // تحديد عدد الأعمدة بناءً على عدد البطاقات
   const gridCols = activeTab === 'delegates' ? 'md:grid-cols-2' : 'md:grid-cols-4';

   return (
       <div className={`grid grid-cols-1 ${gridCols} gap-4 mb-6 financial-summary-grid max-md:grid-cols-2 max-md:gap-2 max-md:mb-3`}>
           {summaryCards.map((card, index) => (
               <div key={index} className={`bg-gradient-to-br ${card.color} rounded-xl shadow-lg p-4 text-white max-md:p-3 max-md:rounded-2xl`}>
                   <div className="flex justify-between items-center">
                       <div>
                           <p className="text-sm opacity-90 font-medium">{card.title}</p>
                           <h3 className="text-2xl font-bold mt-1 max-md:text-lg max-md:break-words">{card.value}</h3>
                       </div>
                       <div className="opacity-20">
                           <card.icon size={36} />
                       </div>
                   </div>
               </div>
           ))}
       </div>
   );
};

    // المكون الرئيسي
    return (
        <div className="min-h-screen bg-gray-50 py-2 financial-mobile-root max-md:py-0 max-md:overflow-x-hidden" dir="rtl">
    <div className="max-w-[98%] mx-auto px-2 max-md:max-w-full max-md:px-2">
                {/* شريط التنقل */}
                <div className="flex justify-between items-center mb-3 financial-mobile-header max-md:bg-white max-md:rounded-2xl max-md:shadow-sm max-md:p-3 max-md:flex-col max-md:items-stretch max-md:gap-3">
                    <div className="flex items-center gap-4 max-md:grid max-md:grid-cols-2 max-md:gap-2">
                        <Link
                            to="/"
                            className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700 transition-colors max-md:justify-center max-md:text-sm max-md:py-2"
                        >
                            <ArrowRight size={20} />
                            العودة للرئيسية
                        </Link>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 max-md:text-xl max-md:justify-center">
                        <BarChart2 size={24} className="text-blue-600" />
                        التقارير المالية
                    </h1>
                </div>

                {/* تبويبات التقارير */}
                <div className="bg-white rounded-lg shadow-md mb-3 overflow-hidden financial-report-tabs max-md:rounded-2xl max-md:sticky max-md:top-0 max-md:z-20">
                    <div className="flex border-b max-md:overflow-x-auto max-md:no-scrollbar max-md:gap-2 max-md:p-2 max-md:border-b-0">
                        <button
                            onClick={() => setActiveTab('clients')}
                            className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors max-md:min-w-[150px] max-md:justify-center max-md:rounded-xl max-md:border max-md:px-3 max-md:py-2
                                     ${activeTab === 'clients' ? 
                                      'text-blue-600 border-b-2 border-blue-600' : 
                                      'text-gray-500 hover:text-blue-600'}`}
                        >
                            <Users size={16} />
                            حسابات العملاء
                        </button>
                        <button
                            onClick={() => setActiveTab('delegates')}
                            className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors max-md:min-w-[160px] max-md:justify-center max-md:rounded-xl max-md:border max-md:px-3 max-md:py-2
                                     ${activeTab === 'delegates' ? 
                                      'text-blue-600 border-b-2 border-blue-600' : 
                                      'text-gray-500 hover:text-blue-600'}`}
                        >
                            <Briefcase size={16} />
                            حسابات المندوبين
                        </button>
                        <button
                            onClick={() => { setActiveTab('debts'); setReportData([]); }}
                            className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors max-md:min-w-[170px] max-md:justify-center max-md:rounded-xl max-md:border max-md:px-3 max-md:py-2
                                     ${activeTab === 'debts' ?
                                      'text-rose-600 border-b-2 border-rose-600' :
                                      'text-gray-500 hover:text-rose-600'}`}
                        >
                            <Wallet size={16} />
                            المديونيات والتحصيلات
                        </button>
                      



               </div>
           </div>

           {/* شريط الأدوات */}
          <div className="bg-white rounded-lg shadow-md p-3 mb-3 financial-report-toolbar max-md:rounded-2xl max-md:p-3">
               <div className="flex items-center justify-between flex-wrap gap-4 max-md:block">
                   <div className="flex items-center gap-4 financial-toolbar-buttons">
                       <button
                           onClick={() => setShowFilters(!showFilters)}
                           className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-200 transition-colors max-md:justify-center max-md:px-2 max-md:text-sm max-md:min-h-[44px]"
                       >
                           <Filter size={18} />
                           معايير التصفية
                           <ChevronDown size={18} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                       </button>

                       <button
                           onClick={executeSearch}
                           className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors max-md:justify-center max-md:px-2 max-md:text-sm max-md:min-h-[44px]"
                       >
                           <RefreshCw size={18} />
                           تحديث
                       </button>

                       {/* زر حساب المستحقات المالية */}
                       {activeTab === 'clients' && (
                           <button
                               onClick={calculateClientDues}
                               className="bg-yellow-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-yellow-700 transition-colors max-md:justify-center max-md:px-2 max-md:text-xs max-md:min-h-[44px]"
                           >
                               <DollarSign size={18} />
                               حساب المستحقات المالية
                           </button>
                       )}

                       <button
    onClick={exportReport}
    className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-700 transition-colors max-md:justify-center max-md:px-2 max-md:text-sm max-md:min-h-[44px]"
    disabled={!reportData || reportData.length === 0}
>
    <FileSpreadsheet size={18} />
    تصدير متقدم
</button>

<button
    onClick={handleExportToExcel}
    className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors max-md:justify-center max-md:px-2 max-md:text-sm max-md:min-h-[44px]"
    disabled={!reportData || reportData.length === 0}
>
    <Download size={18} />
    Excel سريع
</button>

<button
    onClick={printReport}
    className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 transition-colors max-md:col-span-2 max-md:justify-center max-md:px-2 max-md:text-sm max-md:min-h-[44px]"
    disabled={!reportData || reportData.length === 0}
    title={`طباعة البيانات المعروضة في الصفحة ${pagination.currentPage} فقط`}
>
    <Printer size={18} />
    طباعة الصفحة الحالية
</button>


                   </div>
               </div>
           </div>

           {/* فلاتر التصفية - منفصلة عن الـ toolbar */}
           {showFilters && (
               <div className="bg-white rounded-lg shadow-md mb-3 financial-report-filter-panel max-md:rounded-2xl max-md:p-3 max-md:bg-white">
                   <div className="p-4 border-t border-gray-200 max-md:border-0 max-md:p-0">
                       {activeTab === 'clients' && renderClientFilters()}
                       {activeTab === 'delegates' && renderDelegateFilters()}
                       {activeTab === 'company' && renderCompanyFilters()}
                       {activeTab === 'treasury' && renderTreasuryFilters()}
                       {activeTab === 'comprehensive' && renderComprehensiveFilters()}
                       {activeTab === 'employees' && renderEmployeeFilters()}
                       {activeTab === 'debts' && renderDebtFilters()}
                   </div>
               </div>
           )}

           {/* الإحصائيات */}
           {renderSummary()}

           {/* محتوى التقرير */}
           <div className={`bg-white rounded-xl shadow-md overflow-hidden financial-report-content financial-report-${activeTab} max-md:rounded-2xl`} ref={printRef}>
               <div className="p-4 border-b">
                   <h2 className="text-lg font-semibold flex items-center gap-2">
                       {activeTab === 'clients' ? (
                           <>
                               <Users size={18} className="text-blue-600" />
                               تقرير حسابات العملاء
                           </>
                       ) : activeTab === 'delegates' ? (
                           <>
                               <Briefcase size={18} className="text-blue-600" />
                               تقرير حسابات المندوبين
                           </>
                       ) : activeTab === 'company' ? (
                           <>
                               <Building size={18} className="text-blue-600" />
                               تقرير إيرادات ومصروفات الشركة
                           </>
                       ) : activeTab === 'debts' ? (
                           <>
                               <Wallet size={18} className="text-rose-600" />
                               تقرير المديونيات والتحصيلات
                           </>
                       ) : (
                           <>
                               <DollarSign size={18} className="text-blue-600" />
                                التقرير المالي الشامل
                           </>

                        

                       )}
                   </h2>
                   <p className="text-sm text-gray-500 mt-1">
                       تم إنشاء التقرير بتاريخ {new Date().toLocaleDateString('ar-SA')}
                   </p>
               </div>

               {loading ? (
                   <div className="p-8 text-center">
                       <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                       <p className="text-gray-500">جاري تحميل البيانات...</p>
                   </div>
               ) : (
                   renderReportContent()
               )}
           </div>



               {/* نافذة التصدير المتقدم */}
{isExportModalOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 financial-mobile-modal">
        <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Download className="text-blue-600 ml-2" />
                خيارات التصدير المتقدمة - {getReportTitle()}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* نوع التصدير */}
                <div>
                    <h3 className="text-lg font-medium mb-3">نوع التصدير</h3>
                    <div className="space-y-2">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="excel"
                                checked={exportOptions.format === 'excel'}
                                onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
                                className="ml-2"
                            />
                            <FileSpreadsheet size={18} className="ml-2 text-green-600" />
                            ملف Excel متقدم (.xls) - مُوصى به
                        </label>
                        
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="csv"
                                checked={exportOptions.format === 'csv'}
                                onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
                                className="ml-2"
                            />
                            <FileText size={18} className="ml-2 text-blue-600" />
                            ملف CSV بسيط (.csv)
                        </label>
                 
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="pdf"
                                checked={exportOptions.format === 'pdf'}
                                onChange={(e) => setExportOptions({...exportOptions, format: e.target.value})}
                                className="ml-2"
                            />
                            <FileText size={18} className="ml-2 text-red-600" />
                            ملف PDF للطباعة
                        </label>
                    </div>
                </div>
                
{/* نطاق البيانات - مع تحديث التسميات */}
<div>
    <h3 className="text-lg font-medium mb-3">نطاق البيانات</h3>
    <div className="space-y-2">
        <label className="flex items-center">
            <input
                type="radio"
                value="current_page"
                checked={exportOptions.dateRange === 'current_page'}
                onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
                className="ml-2"
            />
            <span className="text-green-600 font-medium">
                البيانات المعروضة في الصفحة الحالية فقط 
                ({getCurrentPageData(clientDues.length > 0 ? clientDues : reportData).length} سجل)
                <span className="text-xs text-gray-500 block">الخيار الأسرع والأكثر دقة</span>
            </span>
        </label>
        <label className="flex items-center">
            <input
                type="radio"
                value="filtered"
                checked={exportOptions.dateRange === 'filtered'}
                onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
                className="ml-2"
            />
            جميع البيانات المفلترة الحالية ({reportData.length} سجل)
            <span className="text-xs text-gray-500">جميع النتائج من الفلترة الحالية</span>
        </label>
        <label className="flex items-center">
            <input
                type="radio"
                value="all"
                checked={exportOptions.dateRange === 'all'}
                onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
                className="ml-2"
            />
            جميع البيانات بدون فلترة
            <span className="text-xs text-gray-500 block">قد يستغرق وقتاً أطول</span>
        </label>
        <label className="flex items-center">
            <input
                type="radio"
                value="custom"
                checked={exportOptions.dateRange === 'custom'}
                onChange={(e) => setExportOptions({...exportOptions, dateRange: e.target.value})}
                className="ml-2"
            />
            فترة مخصصة
        </label>
    </div>
    
    {/* التاريخ المخصص */}
    {exportOptions.dateRange === 'custom' && (
        <div className="mt-3 space-y-2">
            <div>
                <label className="block text-sm text-gray-600 mb-1">من تاريخ</label>
                <input
                    type="date"
                    value={exportOptions.customStartDate}
                    onChange={(e) => setExportOptions({...exportOptions, customStartDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm text-gray-600 mb-1">إلى تاريخ</label>
                <input
                    type="date"
                    value={exportOptions.customEndDate}
                    onChange={(e) => setExportOptions({...exportOptions, customEndDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>
    )}
</div>
                
                {/* خيارات إضافية للعملاء */}
                {activeTab === 'clients' && (
                    <div>
                        <h3 className="text-lg font-medium mb-3">خيارات خاصة بالعملاء</h3>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={exportOptions.includeInactive}
                                    onChange={(e) => setExportOptions({...exportOptions, includeInactive: e.target.checked})}
                                    className="ml-2"
                                />
                                تضمين المعاملات الخاملة
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={exportOptions.groupByCompany}
                                    onChange={(e) => setExportOptions({...exportOptions, groupByCompany: e.target.checked})}
                                    className="ml-2"
                                />
                                تجميع حسب الشركة
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={exportOptions.groupByDelegate}
                                    onChange={(e) => setExportOptions({...exportOptions, groupByDelegate: e.target.checked})}
                                    className="ml-2"
                                />
                                تجميع حسب المندوب
                            </label>
                        </div>
                    </div>
                )}
                
                {/* خيارات إضافية عامة */}
                <div>
                    <h3 className="text-lg font-medium mb-3">خيارات إضافية</h3>
                    <div className="space-y-2">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={exportOptions.includeSummary}
                                onChange={(e) => setExportOptions({...exportOptions, includeSummary: e.target.checked})}
                                className="ml-2"
                            />
                            تضمين الملخص المالي
                        </label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={exportOptions.includeFilters}
                                onChange={(e) => setExportOptions({...exportOptions, includeFilters: e.target.checked})}
                                className="ml-2"
                            />
                            تضمين معلومات الفلاتر
                        </label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={exportOptions.showDetailed}
                                onChange={(e) => setExportOptions({...exportOptions, showDetailed: e.target.checked})}
                                className="ml-2"
                            />
                            عرض التفاصيل الكاملة
                        </label>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
    <h4 className="font-medium text-gray-700 mb-2">معاينة التصدير:</h4>
    <div className="text-sm text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
            <p><strong>عدد السجلات:</strong> {activeTab === 'clients' && serverPagedClients && exportOptions.dateRange !== 'current_page' ? pagination.totalItems : getExportData().length}</p>
            <p><strong>نوع التقرير:</strong> {getReportTitle()}</p>
            <p><strong>نوع الملف:</strong> {
                exportOptions.format === 'excel' ? 'Excel متقدم' : 
                exportOptions.format === 'csv' ? 'CSV بسيط' : 'PDF للطباعة'
            }</p>
        </div>
        <div>
            <p><strong>الفترة:</strong> {
                exportOptions.dateRange === 'current_page' ? 
                    `الصفحة الحالية فقط (${getCurrentPageData(clientDues.length > 0 ? clientDues : reportData).length} سجل من إجمالي ${pagination.totalItems})` :
                exportOptions.dateRange === 'filtered' ? 'البيانات المفلترة' :
                exportOptions.dateRange === 'all' ? 'جميع الفترات' :
                'فترة مخصصة'
            }</p>
            <p><strong>يتضمن الملخص:</strong> {exportOptions.includeSummary ? 'نعم' : 'لا'}</p>
            <p><strong>التفاصيل الكاملة:</strong> {exportOptions.showDetailed ? 'نعم' : 'لا'}</p>
        </div>
    </div>
    
    {/* تأكيد إضافي للصفحة الحالية */}
    {exportOptions.dateRange === 'current_page' && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-blue-700 font-medium">
                    سيتم تصدير البيانات المعروضة في الصفحة الحالية فقط 
                    (الصفحة {pagination.currentPage} من {pagination.totalPages})
                </span>
            </div>
            <p className="text-xs text-blue-600 mt-1 mr-4">
                إذا كنت تريد تصدير المزيد من البيانات، اختر "جميع البيانات المفلترة الحالية"
            </p>
        </div>
    )}
</div>
            
            <div className="flex justify-end gap-3 mt-6">
                <button
                    onClick={() => setIsExportModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                    إلغاء
                </button>
                <button
                    onClick={executeExport}
                    disabled={exportOptions.dateRange === 'custom' && (!exportOptions.customStartDate && !exportOptions.customEndDate)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Download size={18} />
                    تصدير الآن
                </button>
            </div>
        </div>
    </div>
)}

           {/* رسائل النجاح والخطأ */}
           {toast && (
               <div className={`
                   fixed bottom-4 right-4 z-50 
                   p-4 rounded-lg shadow-lg 
                   flex items-center gap-3
                   animate-slide-up
                   ${toast.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
               `}>
                   {toast.message}
                   <button 
                       onClick={() => setToast(null)} 
                       className="ml-3 hover:opacity-75"
                   >
                       <X size={16} />
                   </button>
               </div>
           )}

{/* نافذة معاينة الحساب */}
{showAccountDialog && selectedClient && (
   <div className="fixed inset-0 bg-black bg-opacity-60 z-50 financial-account-dialog flex items-center justify-center p-4">
    <div className="w-full flex items-center justify-center">
    <div className="bg-white rounded-2xl w-full shadow-2xl flex flex-col" style={{maxWidth:'480px', maxHeight:'90vh'}}>

            {/* رأس النافذة - sticky */}
            <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b border-gray-100 px-4 py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
                        <User size={18} className="text-blue-600" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base font-bold text-gray-900 truncate">
                            {selectedClient.NameArabic}
                        </h3>
                        <p className="text-xs text-gray-500">تفاصيل الحساب</p>
                    </div>
                </div>
                <button
                    onClick={() => setShowAccountDialog(false)}
                    className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
                >
                    <X size={20} />
                </button>
            </div>

            {/* محتوى قابل للتمرير */}
          <div className="p-4 space-y-3 overflow-y-auto flex-1">

                {/* معلومات العميل */}
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Clipboard size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500 flex-shrink-0">الرقم القومي:</span>
                        <span className="font-medium break-all">{selectedClient.NationalID || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Briefcase size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500 flex-shrink-0">المندوب:</span>
                        <span className="font-medium">{selectedClient.DelegateName || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Phone size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-gray-500 flex-shrink-0">الهاتف:</span>
                        <span className="font-medium">{formatPhoneNumber(selectedClient.PhoneNumbers)}</span>
                    </div>
                    {selectedClient.CompanyName && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Building size={14} className="text-gray-400 flex-shrink-0" />
                            <span className="text-gray-500 flex-shrink-0">الشركة:</span>
                            <span className="font-medium">{selectedClient.CompanyName}</span>
                        </div>
                    )}
                </div>

                {/* الرصيد النشط */}
                <div className="bg-blue-600 text-white rounded-xl p-4 flex items-center justify-between">
                    <div>
                        <p className="text-xs opacity-80 flex items-center gap-1 mb-1">
                            <Wallet size={13} /> الرصيد النشط
                        </p>
                        <p className={`text-2xl font-bold ${selectedClient.CurrentBalance >= 0 ? 'text-white' : 'text-red-200'}`}>
                            {selectedClient.CurrentBalance?.toLocaleString() || 0} ج.م
                        </p>
                    </div>
                    <div className="bg-blue-500 p-2.5 rounded-full">
                        <Zap size={20} className="text-white" />
                    </div>
                </div>

                {/* المستحقات */}
                <div className={`rounded-xl p-4 text-white ${
                    selectedClient.CurrentBalance > selectedClient.RequiredPayment
                        ? 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                        : selectedClient.RequiredPayment > 0 && selectedClient.CurrentBalance < selectedClient.RequiredPayment
                            ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                            : 'bg-gradient-to-br from-green-500 to-green-600'
                }`}>
                    <p className="text-xs opacity-80 flex items-center gap-1 mb-3">
                        <DollarSign size={13} />
                        {selectedClient.CurrentBalance > selectedClient.RequiredPayment ? 'مبلغ زائد يمكن سحبه' : 'المستحقات المالية'}
                    </p>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm opacity-90">المبلغ المطلوب:</span>
                            <span className="font-semibold">{selectedClient.RequiredPayment?.toLocaleString() || 0} ج.م</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm opacity-90">صافي المبلغ المدفوع:</span>
                            <span className="font-semibold">{selectedClient.CurrentBalance?.toLocaleString() || 0} ج.م</span>
                        </div>
                        <div className="border-t border-white border-opacity-30 my-1"></div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm opacity-90">
                                {selectedClient.CurrentBalance > selectedClient.RequiredPayment ? 'المبلغ الزائد:' : 'المبلغ المتبقي:'}
                            </span>
                            <span className="text-xl font-bold">
                                {Math.abs(selectedClient.CurrentBalance - selectedClient.RequiredPayment)?.toLocaleString() || 0} ج.م
                            </span>
                        </div>
                    </div>
                </div>

                {/* أزرار الإجراءات */}
                <div style={{display:'grid', gridTemplateColumns:'1fr', gap:'10px'}}>
                    <button
                        onClick={async () => {
                            const history = await fetchPaymentHistory(selectedClient.PassportID);
                            setPaymentHistory(history);
                            setShowPaymentHistory(true);
                        }}
                        style={{width:'100%', background:'rgb(var(--bg-violet-600, 166 122 40))', color:'#fff', padding:'14px', borderRadius:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontSize:'15px', fontWeight:'600', border:'none', cursor:'pointer'}}
                    >
                        <FileText size={18} />
                        سجل تعديلات المبلغ
                    </button>
                    <button
                        onClick={() => {
                            setPaymentForm({
                                RequiredPayment: selectedClient.RequiredPayment?.toString() || '',
                                Notes: selectedClient.Notes || ''
                            });
                            setShowPaymentForm(true);
                        }}
                        style={{width:'100%', background:'rgb(var(--bg-amber-600, 217 119 6))', color:'#fff', padding:'14px', borderRadius:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontSize:'15px', fontWeight:'600', border:'none', cursor:'pointer'}}
                    >
                        <DollarSign size={18} />
                        تعديل المبلغ المطلوب
                    </button>
                    <button
                        onClick={() => setShowAccountDialog(false)}
                        style={{width:'100%', background:'rgb(var(--bg-gray-200, 229 231 235))', color:'rgb(var(--text-gray-700, 55 65 81))', padding:'14px', borderRadius:'14px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontSize:'15px', fontWeight:'600', border:'none', cursor:'pointer'}}
                    >
                        <X size={18} />
                        إغلاق
                    </button>
                </div>

                {/* سجل المعاملات */}
                <div className="rounded-xl border border-gray-200 overflow-hidden" style={{marginTop:'4px'}}>
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-bold text-gray-700">سجل المعاملات (للعرض فقط)</h3>
                    </div>

                    {selectedClient.transactions && selectedClient.transactions.length > 0 ? (
                        <div style={{display:'block', width:'100%'}}>
                            {selectedClient.transactions.map((transaction) => (
                                <div key={transaction.TransactionID}
                                    style={{
                                        display:'block',
                                        padding:'14px 16px',
                                        borderBottom:'1px solid #f3f4f6',
                                        background: !transaction.IsActive ? '#f9fafb' : '#fff',
                                        opacity: !transaction.IsActive ? 0.7 : 1,
                                    }}>
                                    {/* الصف الأول: التاريخ + نوع المعاملة + الحالة */}
                                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'6px', marginBottom:'10px'}}>
                                        <span style={{fontSize:'12px', color:'rgb(var(--text-gray-500, 107 114 128))'}}>
                                            {new Date(transaction.TransactionDate).toLocaleDateString('ar-EG')}
                                        </span>
                                        <div style={{display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap'}}>
                                            <span style={{
                                                display:'inline-flex', alignItems:'center', gap:'3px',
                                                padding:'2px 10px', borderRadius:'999px', fontSize:'12px', fontWeight:'600',
                                                background: transaction.TransactionType === 'إيداع' ? '#dcfce7' : '#fee2e2',
                                                color: transaction.TransactionType === 'إيداع' ? '#15803d' : '#b91c1c',
                                            }}>
                                                {transaction.TransactionType === 'إيداع'
                                                    ? <TrendingUp size={11} />
                                                    : <TrendingDown size={11} />}
                                                {transaction.TransactionType}
                                            </span>
                                            {transaction.IsActive ? (
                                                <span style={{display:'inline-flex',alignItems:'center',gap:'3px',padding:'2px 8px',borderRadius:'999px',fontSize:'11px',background:'rgb(var(--bg-green-100, 220 252 231))',color:'rgb(var(--text-green-700, 21 128 61))'}}>
                                                    <CheckCircle size={11} /> نشط
                                                </span>
                                            ) : (
                                                <span style={{display:'inline-flex',alignItems:'center',gap:'3px',padding:'2px 8px',borderRadius:'999px',fontSize:'11px',background:'rgb(var(--bg-gray-100, 243 244 246))',color:'rgb(var(--text-gray-500, 107 114 128))'}}>
                                                    <X size={11} /> خامل
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {/* الصف الثاني: المبلغ + الرصيد */}
                                    <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between'}}>
                                        <div>
                                            <p style={{fontSize:'11px', color:'rgb(var(--text-gray-400, 156 163 175))', marginBottom:'2px'}}>المبلغ</p>
                                            <p style={{fontSize:'17px', fontWeight:'700', color: transaction.TransactionType === 'إيداع' ? '#16a34a' : '#dc2626'}}>
                                                {transaction.Amount?.toLocaleString()} ج.م
                                            </p>
                                        </div>
                                        <div style={{textAlign:'left'}}>
                                            <p style={{fontSize:'11px', color:'rgb(var(--text-gray-400, 156 163 175))', marginBottom:'2px'}}>الرصيد</p>
                                            <p style={{fontSize:'15px', fontWeight:'600', color:'rgb(var(--text-gray-800, 31 41 55))'}}>
                                                {transaction.IsActive === false
                                                    ? 'غير محتسب'
                                                    : transaction.Balance != null
                                                        ? `${transaction.Balance.toLocaleString()} ج.م`
                                                        : '0 ج.م'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* ملاحظات إن وجدت */}
                                    {transaction.Notes && (
                                        <p style={{marginTop:'8px', fontSize:'12px', color:'rgb(var(--text-gray-500, 107 114 128))', background:'rgb(var(--bg-gray-50, 249 250 251))', borderRadius:'8px', padding:'6px 10px'}}>
                                            {transaction.Notes}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{display:'flex', flexDirection:'column', alignItems:'center', padding:'40px 16px', color:'rgb(var(--text-gray-400, 156 163 175))'}}>
                            <AlertCircle size={36} style={{color:'rgb(var(--text-gray-300, 209 213 219))', marginBottom:'8px'}} />
                            <p style={{fontSize:'14px'}}>لا توجد معاملات لهذا العميل</p>
                        </div>
                    )}
                </div>

            </div>{/* end p-4 */}
        </div>
        </div>
    </div>
)}

{/* نافذة تعديل المبلغ المطلوب */}
{showPaymentForm && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 financial-mobile-modal">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                    <DollarSign size={20} className="text-blue-600" />
                    تحديد المبلغ المطلوب للعميل
                </h2>
                <button
                    onClick={() => setShowPaymentForm(false)}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <X size={24} />
                </button>
            </div>
            
            <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                    <AlertCircle size={24} className="text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-bold text-orange-800">تنبيه هام</h4>
                        <p className="text-sm text-orange-700">
                            تعديل المبلغ المطلوب سيؤثر على حسابات العميل وتقارير المستحقات المالية.
                            الرجاء التأكد من صحة المبلغ قبل الحفظ.
                        </p>
                    </div>
                </div>
            </div>
            
            <form className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-1">المبلغ المطلوب (ج.م)</label>
                    <input
                        type="number"
                        value={paymentForm.RequiredPayment}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || !isNaN(parseFloat(value))) {
                                setPaymentForm({...paymentForm, RequiredPayment: value});
                            }
                        }}
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                        required
                        placeholder="أدخل المبلغ المطلوب"
                        step="0.01"
                        min="0"
                    />
                </div>
                
                <div>
                    <label className="block text-sm font-medium mb-1">ملاحظات</label>
                    <textarea
                        value={paymentForm.Notes}
                        onChange={(e) => setPaymentForm({...paymentForm, Notes: e.target.value})}
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="إضافة ملاحظات (اختياري)"
                    ></textarea>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <button
                        type="button"
                        onClick={saveClientPayment}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Save size={16} />
                        حفظ
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowPaymentForm(false)}
                        className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors"
                    >
                        إلغاء
                    </button>
                </div>
            </form>
        </div>
    </div>
)}


{/* نافذة سجل تعديلات المبلغ المطلوب */}
{showPaymentHistory && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 financial-mobile-modal">
        <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                    <FileText size={20} className="text-purple-600" />
                    سجل تعديلات المبلغ المطلوب
                </h2>
                <button
                    onClick={() => setShowPaymentHistory(false)}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <X size={24} />
                </button>
            </div>
            
            <div className="max-h-[70vh] overflow-y-auto">
                {paymentHistory.length === 0 ? (
                    <div className="text-center p-8 text-gray-500">
                        <div className="flex flex-col items-center justify-center">
                            <AlertCircle size={40} className="text-gray-300 mb-2" />
                            <p>لا يوجد سجل تعديلات للمبلغ المطلوب</p>
                        </div>
                    </div>
                ) : (
                    <table className="min-w-full bg-white border rounded-lg overflow-hidden">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">التاريخ</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">المبلغ القديم</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">المبلغ الجديد</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">الملاحظات</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">بواسطة</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paymentHistory.map((record) => (
                                <tr key={record.HistoryID} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {new Date(record.CreatedAt).toLocaleDateString('ar-EG', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {record.OldAmount?.toLocaleString() || '0'} ج.م
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium text-blue-600">
                                        {record.NewAmount?.toLocaleString()} ج.م
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {record.Notes || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {record.CreatedBy}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            
            <div className="mt-4 flex justify-end">
                <button
                    onClick={() => setShowPaymentHistory(false)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                >
                    إغلاق
                </button>
            </div>
        </div>
    </div>
)}



{loading && (
                    <LoadingSpinner 
                        message={
                            activeTab === 'clients' ? "جاري جلب بيانات العملاء..." :
                            activeTab === 'delegates' ? "جاري جلب بيانات المندوبين..." :
                            activeTab === 'company' ? "جاري جلب بيانات الشركة..." :
                            activeTab === 'debts' ? "جاري جلب بيانات المديونيات..." :
                            activeTab === 'comprehensive' ? "جاري جلب التقرير الشامل..." :
                            "جاري تحميل البيانات..."
                        } 
                    />
                )}



       </div>

       {/* أضف CSS الإضافي للرسوم المتحركة */}
       <style dangerouslySetInnerHTML={{
           __html: `
           @keyframes slideUp {
               from { opacity: 0; transform: translateY(20px); }
               to { opacity: 1; transform: translateY(0); }
           }
           .animate-slide-up {
               animation: slideUp 0.3s ease-out forwards;
           }
           


           /* MASAR_STRONG_MOBILE_FINANCIAL_FIX */
           @media (max-width: 768px) {
               html, body, #root {
                   max-width: 100vw !important;
                   overflow-x: hidden !important;
               }

               .financial-mobile-root {
                   width: 100vw !important;
                   max-width: 100vw !important;
                   overflow-x: hidden !important;
                   background: #f3f6fb !important;
               }

               .financial-mobile-root > div {
                   width: 100% !important;
                   max-width: 100vw !important;
                   overflow-x: hidden !important;
               }

               .financial-report-toolbar {
                   overflow: hidden !important;
               }

               .financial-toolbar-buttons {
                   display: grid !important;
                   grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                   gap: 10px !important;
                   width: 100% !important;
                   align-items: stretch !important;
               }

               .financial-toolbar-buttons > button:last-child {
                   grid-column: span 2 !important;
               }

               .financial-report-toolbar button {
                   width: 100% !important;
                   min-width: 0 !important;
                   min-height: 50px !important;
                   justify-content: center !important;
                   white-space: normal !important;
                   line-height: 1.35 !important;
                   padding: 10px 8px !important;
                   font-size: 13px !important;
                   border-radius: 16px !important;
                   text-align: center !important;
               }

               .financial-report-toolbar button svg {
                   flex-shrink: 0 !important;
               }

               .financial-report-toolbar > div > div > button:nth-child(3),
               .financial-report-toolbar > div > div > button:nth-child(6) {
                   grid-column: span 2 !important;
               }

               .financial-report-filter-panel {
                   width: 100% !important;
                   overflow: visible !important;
                   padding: 16px !important;
               }

               .financial-report-filter-panel .space-y-4 > * {
                   width: 100% !important;
               }

               .financial-report-filter-panel .grid {
                   grid-template-columns: 1fr !important;
                   gap: 12px !important;
               }

               .financial-report-filter-panel > div > .border.border-gray-200 .grid {
                   grid-template-columns: 1fr !important;
               }

               .financial-report-filter-panel input,
               .financial-report-filter-panel select,
               .financial-report-filter-panel textarea {
                   width: 100% !important;
                   min-width: 0 !important;
                   font-size: 16px !important;
                   box-sizing: border-box !important;
               }

               .financial-report-filter-panel input[type="date"] {
                   font-size: 13px !important;
                   min-height: 40px !important;
               }

               .financial-mobile-modal {
                   position: fixed !important;
                   inset: 0 !important;
                   top: 0 !important;
                   right: 0 !important;
                   bottom: 0 !important;
                   left: 0 !important;
                   width: 100vw !important;
                   height: 100dvh !important;
                   max-width: 100vw !important;
                   max-height: 100dvh !important;
                   margin: 0 !important;
                   padding: 0 !important;
                   display: block !important;
                   overflow: hidden !important;
                   transform: none !important;
                   z-index: 9999 !important;
               }

               .financial-mobile-modal > .bg-white {
                   position: absolute !important;
                   inset: 0 !important;
                   top: 0 !important;
                   right: 0 !important;
                   bottom: 0 !important;
                   left: 0 !important;
                   width: 100vw !important;
                   height: 100dvh !important;
                   max-width: 100vw !important;
                   max-height: 100dvh !important;
                   min-width: 0 !important;
                   margin: 0 !important;
                   border-radius: 0 !important;
                   padding: 0 !important;
                   overflow-y: auto !important;
                   overflow-x: hidden !important;
                   display: flex !important;
                   flex-direction: column !important;
                   transform: none !important;
               }

               .financial-mobile-modal > .bg-white > .flex:first-child,
               .financial-mobile-modal > .bg-white > div:first-child {
                   position: sticky !important;
                   top: 0 !important;
                   z-index: 20 !important;
                   width: 100% !important;
                   margin: 0 !important;
                   border-radius: 0 !important;
                   padding: 12px !important;
                   background: linear-gradient(135deg, #eef4ff, #ffffff) !important;
                   box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08) !important;
                   display: grid !important;
                   grid-template-columns: 1fr !important;
                   gap: 10px !important;
                   align-items: stretch !important;
               }

               .financial-mobile-modal > .bg-white > .flex:first-child > .flex,
               .financial-mobile-modal > .bg-white > div:first-child > .flex {
                   width: 100% !important;
                   max-width: 100% !important;
                   display: grid !important;
                   grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                   gap: 8px !important;
                   align-items: stretch !important;
               }

               .financial-mobile-modal > .bg-white > .flex:first-child > .flex:first-child,
               .financial-mobile-modal > .bg-white > div:first-child > .flex:first-child {
                   grid-template-columns: 42px 1fr !important;
               }

               .financial-mobile-modal h2,
               .financial-mobile-modal h3 {
                   font-size: 18px !important;
                   line-height: 1.55 !important;
                   margin: 0 !important;
                   word-break: break-word !important;
                   white-space: normal !important;
               }

               .financial-mobile-modal p,
               .financial-mobile-modal span,
               .financial-mobile-modal div {
                   max-width: 100% !important;
               }

               .financial-mobile-modal > .bg-white > .flex:first-child button,
               .financial-mobile-modal > .bg-white > div:first-child button {
                   width: 100% !important;
                   min-width: 0 !important;
                   min-height: 42px !important;
                   justify-content: center !important;
                   padding: 9px 8px !important;
                   font-size: 13px !important;
                   border-radius: 14px !important;
                   white-space: normal !important;
                   line-height: 1.25 !important;
               }

               .financial-mobile-modal button[title="إغلاق"],
               .financial-mobile-modal button.text-gray-400 {
                   width: 42px !important;
                   height: 42px !important;
                   min-height: 42px !important;
                   padding: 0 !important;
                   background: #f3f4f6 !important;
                   color: #374151 !important;
                   border-radius: 14px !important;
               }

               .financial-mobile-modal > .bg-white > .flex-1,
               .financial-mobile-modal > .bg-white > div:nth-child(2) {
                   flex: 1 1 auto !important;
                   overflow-y: auto !important;
                   overflow-x: hidden !important;
                   padding: 12px !important;
                   width: 100% !important;
               }

               .financial-mobile-modal .p-6,
               .financial-mobile-modal .p-5,
               .financial-mobile-modal .p-4 {
                   padding: 12px !important;
               }

               .financial-mobile-modal .grid {
                   grid-template-columns: 1fr !important;
                   gap: 10px !important;
               }

               .financial-mobile-modal .grid.grid-cols-2,
               .financial-mobile-modal .grid.grid-cols-3,
               .financial-mobile-modal .grid.grid-cols-4,
               .financial-mobile-modal .grid.grid-cols-6 {
                   grid-template-columns: 1fr !important;
               }

               .financial-mobile-modal .flex.justify-end,
               .financial-mobile-modal .flex.items-center.gap-2,
               .financial-mobile-modal .flex.items-center.gap-3 {
                   flex-wrap: wrap !important;
               }

               .financial-mobile-modal .overflow-x-auto {
                   overflow-x: hidden !important;
                   width: 100% !important;
               }

               .financial-mobile-modal table {
                   display: block !important;
                   width: 100% !important;
                   min-width: 0 !important;
                   border: 0 !important;
                   background: transparent !important;
               }

               .financial-mobile-modal thead {
                   display: none !important;
               }

               .financial-mobile-modal tbody {
                   display: grid !important;
                   gap: 12px !important;
                   width: 100% !important;
               }

               .financial-mobile-modal tbody tr {
                   display: block !important;
                   background: #fff !important;
                   border: 1px solid #e5e7eb !important;
                   border-radius: 18px !important;
                   padding: 14px !important;
                   box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
                   overflow: hidden !important;
               }

               .financial-mobile-modal tbody tr td {
                   display: flex !important;
                   align-items: center !important;
                   justify-content: space-between !important;
                   gap: 10px !important;
                   padding: 9px 0 !important;
                   border: 0 !important;
                   border-bottom: 1px dashed #f3f4f6 !important;
                   font-size: 13px !important;
                   text-align: left !important;
                   white-space: normal !important;
                   overflow: visible !important;
                   color: #111827 !important;
                   min-height: 36px !important;
               }

               .financial-mobile-modal tbody tr td:last-child {
                   border-bottom: 0 !important;
               }

               .financial-mobile-modal tbody tr td::before {
                   content: "";
                   min-width: 90px;
                   color: #6b7280;
                   font-size: 12px;
                   font-weight: 700;
                   text-align: right;
                   flex-shrink: 0;
               }

               .financial-mobile-modal tbody tr td:nth-child(1)::before { content: "التاريخ"; }
               .financial-mobile-modal tbody tr td:nth-child(2)::before { content: "نوع المعاملة"; }
               .financial-mobile-modal tbody tr td:nth-child(3)::before { content: "المبلغ"; }
               .financial-mobile-modal tbody tr td:nth-child(4)::before { content: "الرصيد"; }
               .financial-mobile-modal tbody tr td:nth-child(5)::before { content: "ملاحظات"; }
               .financial-mobile-modal tbody tr td:nth-child(6)::before { content: "الحالة"; }
               .financial-mobile-modal tbody tr td:nth-child(7)::before { content: "بيان 7"; }
               .financial-mobile-modal tbody tr td:nth-child(8)::before { content: "بيان 8"; }
               .financial-mobile-modal tbody tr td:nth-child(9)::before { content: "بيان 9"; }
               .financial-mobile-modal tbody tr td:nth-child(10)::before { content: "بيان 10"; }

               .financial-mobile-modal tbody tr td > * {
                   max-width: 58% !important;
                   text-align: left !important;
               }

               .financial-mobile-modal input,
               .financial-mobile-modal select,
               .financial-mobile-modal textarea {
                   width: 100% !important;
                   min-width: 0 !important;
                   min-height: 44px !important;
                   font-size: 16px !important;
                   border-radius: 14px !important;
               }
           }

           @media print {
               body * {
                   visibility: hidden;
               }
               #printRef, #printRef * {
                   visibility: visible;
               }
               #printRef {
                   position: absolute;
                   left: 0;
                   top: 0;
                   width: 100%;
               }
           }
           `
       }} />


{/* نافذة عرض تفاصيل الملاحظات */}
{selectedNotes && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 financial-mobile-modal">
        <div className="bg-white rounded-xl p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
                <h3 className="text-lg font-bold text-gray-900">ملاحظات العميل</h3>
                <button 
                    onClick={() => setSelectedNotes(null)}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X size={24} />
                </button>
            </div>
            
            <div className="max-h-80 overflow-y-auto">
                {selectedNotes.notes.length > 0 ? (
                    <div className="space-y-3">
                        {selectedNotes.notes.map((note, index) => (
                            <div key={index} className="p-3 border rounded-lg hover:bg-gray-50">
                                <div className="flex justify-between items-start">
                                    <div className="text-sm text-gray-900">{note.text}</div>
                                    <div className="text-xs text-gray-500 whitespace-nowrap mr-2">
                                        {new Date(note.date).toLocaleDateString('ar-EG')} {new Date(note.date).toLocaleTimeString('ar-EG')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-gray-500 py-8">لا توجد ملاحظات</div>
                )}
            </div>
            
            <div className="mt-4 flex justify-end">
                <button 
                    onClick={() => setSelectedNotes(null)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                    إغلاق
                </button>
            </div>
        </div>
    </div>
)}



{/* نافذة عرض معاملات العميل */}
{selectedTransactions && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto financial-mobile-modal">
        <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                    معاملات العميل: {selectedTransactions.clientName}
                </h3>
                <button 
                    onClick={() => setSelectedTransactions(null)}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X size={24} />
                </button>
            </div>
            
            <div className="overflow-y-auto flex-grow">
                {selectedTransactions.transactions.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">التاريخ</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">نوع المعاملة</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">المبلغ</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">الرصيد</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">الملاحظات</th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">الحالة</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {selectedTransactions.transactions.map((transaction, index) => (
                                    <tr key={index} className={`hover:bg-gray-50 ${transaction.IsActive ? '' : 'bg-gray-100'}`}>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {new Date(transaction.TransactionDate).toLocaleDateString('ar-EG')}
                                            <div className="text-xs text-gray-500">
                                                {new Date(transaction.TransactionDate).toLocaleTimeString('ar-EG')}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                                ${transaction.TransactionType === 'إيداع' ? 
                                                    'bg-green-100 text-green-800' : 
                                                    'bg-red-100 text-red-800'}`}>
                                                {transaction.TransactionType === 'إيداع' ? (
                                                    <TrendingUp size={12} className="mr-1" />
                                                ) : (
                                                    <TrendingDown size={12} className="mr-1" />
                                                )}
                                                {transaction.TransactionType}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium">
                                            <span className={transaction.TransactionType === 'إيداع' ? 
                                                'text-green-600' : 'text-red-600'}>
                                                {transaction.Amount?.toLocaleString()} ج.م
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {transaction.IsActive ? (
                                                `${transaction.Balance?.toLocaleString()} ج.م`
                                            ) : (
                                                <span className="text-gray-400">غير محتسب</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate" title={transaction.Notes || '-'}>
                                            {transaction.Notes || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-center">
                                            {transaction.IsActive ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <CheckCircle size={10} className="mr-1" />
                                                    نشط
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                                    <X size={10} className="mr-1" />
                                                    خامل
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center text-gray-500 py-8">لا توجد معاملات لهذا العميل</div>
                )}
            </div>
            
            <div className="mt-4 flex justify-end">
                <button 
                    onClick={() => setSelectedTransactions(null)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                    إغلاق
                </button>
            </div>
        </div>
    </div>
)}



{showDelegateDetailsModal && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto financial-mobile-modal">
        <div className="bg-white rounded-xl w-full max-w-7xl max-h-[95vh] flex flex-col m-4">
            {/* رأس النافذة المحسن */}
            <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-100 rounded-t-xl">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-600 rounded-full">
                        <User size={24} className="text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            التقرير الشامل للمندوب: {selectedDelegateDetails?.delegateName}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            تفاصيل كاملة للعملاء والعمولات والمعاملات
                            {selectedDelegateDetails?.delegateInfo?.PhoneNumber && (
                                <span className="font-medium"> - هاتف: {selectedDelegateDetails.delegateInfo.PhoneNumber}</span>
                            )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            معرف المندوب: {selectedDelegateDetails?.delegateInfo?.DelegateID}
                        </p>
                    </div>
                </div>
       <div className="flex items-center gap-2">
    {/* أزرار للتبويب النشط */}
    <button
        onClick={() => printDelegateDetails()}
        className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700 transition-colors"
        disabled={delegateDetailsLoading}
        title={`طباعة ${activeDetailsTab === 'commissions' ? 'تفاصيل العمولات' : 'عملاء المندوب'}`}
    >
        <Printer size={16} />
        طباعة
    </button>
    
    <button
        onClick={() => exportDelegateDetailsToPDF()}
        className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 transition-colors"
        disabled={delegateDetailsLoading}
        title={`تصدير PDF لـ ${activeDetailsTab === 'commissions' ? 'تفاصيل العمولات' : 'عملاء المندوب'}`}
    >
        <FileText size={16} />
        PDF
    </button>
    
    <button
        onClick={() => exportDelegateDetailsToExcel()}
        className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors"
        disabled={delegateDetailsLoading}
        title={`تصدير Excel لـ ${activeDetailsTab === 'commissions' ? 'تفاصيل العمولات' : 'عملاء المندوب'}`}
    >
        <Download size={16} />
        Excel
    </button>
    
    <button
        onClick={() => setShowDelegateDetailsModal(false)}
        className="text-gray-400 hover:text-gray-600"
        title="إغلاق"
    >
        <X size={24} />
    </button>
</div>
            </div>

            {/* محتوى النافذة */}
            <div className="flex-1 overflow-y-auto p-6">
                {delegateDetailsLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                        <span className="mr-2">جاري تحميل التفاصيل الشاملة...</span>
                    </div>
                ) : selectedDelegateDetails ? (
                    <div className="space-y-6">
                        {/* الملخص الإحصائي المحسن */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm opacity-90">إجمالي العملاء</p>
                                        <p className="text-2xl font-bold">{selectedDelegateDetails.summary?.totalClients || 0}</p>
                                    </div>
                                    <Users size={24} className="opacity-20" />
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm opacity-90">العملاء المسافرين</p>
                                        <p className="text-2xl font-bold">{selectedDelegateDetails.summary?.traveledClients || 0}</p>
                                    </div>
                                    <TrendingUp size={24} className="opacity-20" />
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm opacity-90">عمولة مدفوعة</p>
                                        <p className="text-2xl font-bold">{selectedDelegateDetails.summary?.clientsWithCommission || 0}</p>
                                    </div>
                                    <CheckCircle size={24} className="opacity-20" />
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm opacity-90">عمولة غير مدفوعة</p>
                                        <p className="text-2xl font-bold">{selectedDelegateDetails.summary?.clientsWithoutCommission || 0}</p>
                                    </div>
                                    <AlertCircle size={24} className="opacity-20" />
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm opacity-90">إجمالي العمولات</p>
                                        <p className="text-xl font-bold">{(selectedDelegateDetails.summary?.totalCommissionsPaid || 0).toLocaleString()} ج.م</p>
                                    </div>
                                    <DollarSign size={24} className="opacity-20" />
                                </div>
                            </div>
                            
                            
                        </div>

                        {/* التبويبات */}
                        <div className="bg-white border rounded-xl overflow-hidden">
                            <div className="flex border-b bg-gray-50">
                                <button
                                    onClick={() => setActiveDetailsTab('commissions')}
                                    className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors ${
                                        activeDetailsTab === 'commissions' 
                                        ? 'text-blue-600 border-b-2 border-blue-600 bg-white' 
                                        : 'text-gray-500 hover:text-blue-600'
                                    }`}
                                >
                                    <DollarSign size={16} />
                                    العمولات المدفوعة ({selectedDelegateDetails.commissionDetails?.length || 0})
                                </button>
                                <button
                                    onClick={() => setActiveDetailsTab('clients')}
                                    className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors ${
                                        activeDetailsTab === 'clients' 
                                        ? 'text-blue-600 border-b-2 border-blue-600 bg-white' 
                                        : 'text-gray-500 hover:text-blue-600'
                                    }`}
                                >
                                    <Users size={16} />
                                    عملاء المندوب ({selectedDelegateDetails.clients?.length || 0})
                                </button>
                            </div>
                            
                            <div className="p-6">
                                {/* تبويب العمولات المدفوعة - محسن */}
                                {activeDetailsTab === 'commissions' && (
                                    selectedDelegateDetails.commissionDetails?.length > 0 ? (
                                        <div className="space-y-6">
                                            {selectedDelegateDetails.commissionDetails.map((commission, index) => (
                                                <div key={commission.TransactionID || index} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 shadow-sm">
                                                    {/* رأس المعاملة المحسن */}
                                                    <div className="flex justify-between items-start mb-4">
                                                        <div className="flex items-center gap-4">
                                                            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                                                                <DollarSign size={28} className="text-white" />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-xl font-bold text-green-700 mb-1">دفع عمولة</h4>
                                                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                                                    <div className="flex items-center gap-1">
                                                                        <Calendar size={14} />
                                                                        <span>{new Date(commission.CommissionDate).toLocaleDateString('ar-EG')}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <Clock size={14} />
                                                                        <span>{new Date(commission.CommissionDate).toLocaleTimeString('ar-EG')}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex flex-col items-end gap-2">
                                                            <div className="flex flex-wrap justify-end gap-2">
                                                                <button
                                                                    onClick={() => exportDelegateCommissionAsImage(commission, 'copy')}
                                                                    className="bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm text-sm"
                                                                    title="نسخ صورة واضحة لهذه العمولة وإرسالها للمندوب مباشرة"
                                                                >
                                                                    <Copy size={14} />
                                                                    نسخ صورة للمندوب
                                                                </button>
                                                                <button
                                                                    onClick={() => exportDelegateCommissionAsImage(commission, 'download')}
                                                                    className="bg-white text-gray-700 border border-gray-300 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50 transition-colors shadow-sm text-sm"
                                                                    title="تحميل صورة العمولة بجودة عالية"
                                                                >
                                                                    <Download size={14} />
                                                                    تحميل صورة
                                                                </button>
                                                            </div>
                                                            <div className="bg-green-600 text-white px-4 py-2 rounded-lg shadow-md">
                                                                <p className="text-2xl font-bold">
                                                                    {(commission.CommissionAmount || 0).toLocaleString()} ج.م
                                                                </p>
                                                                <p className="text-xs opacity-90">مبلغ العمولة</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* طريقة الدفع المحسنة */}
                                                    <div className="mb-4">
                                                        <div className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm">
                                                            <div className="flex items-center gap-3">
                                                                <div className="text-2xl">
                                                                    {commission.PaymentMethodType === 'treasury' ? '💰' : 
                                                                     commission.PaymentMethodType === 'bank' ? '🏦' : '💳'}
                                                                </div>
                                                                <div>
                                                                    <p className="font-bold text-blue-900 text-lg">
                                                                        طريقة الدفع: {commission.CompanyName || 'الخزينة الرئيسية'}
                                                                    </p>
                                                                    <p className="text-sm text-blue-600">
                                                                        {commission.PaymentMethodType === 'treasury' ? 'الخزينة الرئيسية' : 
                                                                         commission.PaymentMethodType === 'bank' ? 'حساب بنكي' : 'طريقة دفع أخرى'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                 {/* العملاء المرتبطين - تحسين كبير */}
                                                        {commission.ClientsCount > 0 && (
                                                            <div className="mb-4">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                                                        <Users size={18} />
                                                                        العملاء المرتبطين بهذه العمولة
                                                                    </h5>
                                                                    <span className="bg-blue-100 text-blue-700 text-sm px-3 py-1 rounded-full font-bold">
                                                                        {commission.ClientsCount} عملاء
                                                                    </span>
                                                                </div>
                                                                
                                                                <ClientsCommissionDisplay 
                                                                    clientsData={commission.ClientsNames}
                                                                    clientsCount={commission.ClientsCount}
                                                                    commissionAmount={commission.CommissionAmount}
                                                                    clientsWithCompanies={commission.ClientsWithCompanies} // إضافة البيانات التفصيلية
                                                                />
                                                            </div>
                                                        )}

                                                    {/* الملاحظات */}
                                                    {commission.CommissionNotes && (
                                                        <div className="mb-4">
                                                            <h5 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                                                <FileText size={16} />
                                                                الملاحظات
                                                            </h5>
                                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-gray-700">
                                                                {commission.CommissionNotes}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* معلومات إضافية */}
                                                    <div className="flex justify-between items-center text-sm text-gray-500 pt-3 border-t border-gray-200">
                                                        <span className="flex items-center gap-1">
                                                            <User size={14} />
                                                            تم بواسطة: {commission.CreatedBy || 'النظام'}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <Package size={14} />
                                                            معاملة #{commission.TransactionID}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-12 text-center text-gray-500">
                                            <DollarSign size={64} className="mx-auto text-gray-300 mb-4" />
                                            <p className="text-xl font-medium">لا توجد عمولات مدفوعة لهذا المندوب بعد</p>
                                            <p className="text-sm text-gray-400 mt-2">سيتم عرض العمولات هنا بمجرد دفعها للمندوب</p>
                                        </div>
                                    )
                                )}

                                {/* تبويب عملاء المندوب - كما هو */}
                                {activeDetailsTab === 'clients' && (
                                    selectedDelegateDetails.clients?.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">م</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم العميل</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرقم القومي</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم الجواز</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الشركة</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">مهنة السفر</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ النت</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ التأشير</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ السفر</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">حالة العمولة</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرصيد</th>
                                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الحالة</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {selectedDelegateDetails.clients.map((client, index) => (
                                                        <tr key={client.PassportID} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-600">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                                {client.NameArabic || '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                                {client.NationalID || '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                                {client.PassportNumber || '-'}
                                                            </td>
                                                           <td className="px-4 py-3 text-sm text-gray-700">
                                                            {client.CompanyDisplayName || client.CompanyName || '-'}
                                                        </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                                {client.ProfessionName || '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                                {client.NetWorkDate ? (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                        {new Date(client.NetWorkDate).toLocaleDateString('ar-EG')}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                                {client.VisaDate ? (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                        {new Date(client.VisaDate).toLocaleDateString('ar-EG')}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                                {client.TravelDate ? (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        {new Date(client.TravelDate).toLocaleDateString('ar-EG')}
                                                                    </span>
                                                                ) : '-'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm">
                                                                {client.CommissionStatus === 'مدفوعة' ? (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        <CheckCircle size={12} className="ml-1" />
                                                                        مدفوعة
                                                                    </span>
                                                                ) : client.CommissionStatus === 'غير مدفوعة' ? (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                        <AlertCircle size={12} className="ml-1" />
                                                                        غير مدفوعة
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-400">-</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm">
                                                                <span className={`font-medium ${
                                                                    client.CurrentBalance > 0 ? 'text-green-600' :
                                                                    client.CurrentBalance < 0 ? 'text-red-600' : 'text-gray-600'
                                                                }`}>
                                                                    {client.CurrentBalance?.toLocaleString()} ج.م
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-sm">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                    client.ClientStatus === 'مسافر' ? 'bg-green-100 text-green-800' :
                                                                    client.ClientStatus === 'حصل على التأشيرة' ? 'bg-orange-100 text-orange-800' :
                                                                    client.ClientStatus === 'تم النت' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                    {client.ClientStatus}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="p-12 text-center text-gray-500">
                                            <Users size={64} className="mx-auto text-gray-300 mb-4" />
                                            <p className="text-xl font-medium">لا يوجد عملاء مسجلين لهذا المندوب</p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center text-gray-500 py-8">
                        لا توجد تفاصيل متاحة
                    </div>
                )}
            </div>
        </div>
    </div>
)}





   </div>
);
};

export default FinancialReports;